/**
 * Resume Editor Modal Component
 * Requirements: 4.1, 4.2, 4.4, 1.1, 1.2, 1.3, 1.4, 2.1, 2.2, 2.3, 2.4, 2.5, 3.1, 3.2, 3.3, 3.4, 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 7.1, 7.2, 7.3, 7.4
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { api } from '../services/api';
import { X, Loader2, Save, Plus, Trash2, ChevronUp, ChevronDown, Edit2, Building2, Briefcase, Upload, Download, FileText, FileType, AlertTriangle, Sparkles, Check, XCircle, ExternalLink, Copy, Brain, Settings, SlidersHorizontal, Gauge, Clock, Eye, ArrowLeft, ArrowRight, RefreshCw } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { UpgradePromptModal } from './UpgradePromptModal';
import { DedicatedMasterModal } from './DedicatedMasterModal';
import { ScorecardModal } from './ScorecardModal';
import { WritingSampleModal } from './WritingSampleModal';
import { SectionNavPanel, type SectionState } from './SectionNavPanel';
import { ResumePreviewPanel } from './ResumePreviewPanel';
import { buildScorecard, setTextAtPath, type FindingPath } from '../utils/scorecard';
// Per-item tag minimums + the save/poll/merge rules that mirror the backend's
// shared/tag_status.py (extracted so they're unit-testable).
import { TAG_MIN_BULLET, TAG_MIN_ITEM, TAG_MIN_SUMMARY_SECTION, SUMMARY_SECTION_KEYS, tagChipNeeded, countItemTags, needsTagGeneration, mergeGeneratedTags } from '../utils/tagStatus';
import { parseDateString, isCurrentMarker, FORMAT_HINT } from '../utils/dateParse';
// Load-normalization + YAML export projection. Both derive their field lists
// from utils/resumeSchema (the browser mirror of shared/resume_template.py) —
// no resume item's fields are ever re-typed in this component.
import { CURRENCY_OPTIONS, EMPTY_DEMOGRAPHICS, buildYamlExportData, formatSalaryFloor, normalizeResumeJson } from '../utils/resumeNormalize';
// Per-resume Skills display controls (defaults + bounds shared with the preview).
import {
  clampSkillsNumber, resolveSkillsSettings,
  SKILLS_MAX_LINES_MAX, SKILLS_MAX_LINES_MIN,
  SKILLS_MAX_SECTIONS_MAX, SKILLS_MAX_SECTIONS_MIN,
} from '../utils/skillsDisplay';
// Accordion state for every editor card (defaults + persistence helpers).
import {
  isSectionOpen as resolveSectionOpen, readOpenSections, writeOpenSections,
  RESUME_URLS_KEY,
} from '../utils/editorSections';
import { useResumeLint } from '../hooks/useResumeLint';
import { liveLintHighCount, masterLintBannerText } from '../utils/masterLint';
import { overallScoreLabel } from '../utils/resumeLint';
import { masterPdfUrl, masterResumeUrl } from '../utils/masterUrls';
import { clearCachedDmContent } from '../utils/dmPreview';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useSaveShortcut } from '../hooks/useSaveShortcut';
import { importResumeFile, ImportValidationError, ImportUploadError, ImportProcessError, ImportAuthError, ImportTimeoutError } from '../services/importService';
import {
  addUserFact, confirmAllFacts, confirmFact, dismissFact, factsOf, proposedFacts,
  updateFactText, visibleFacts,
} from '../utils/impliedFacts';
import {
  findFocusElement, flashFocusElement, navKeyForPath, resolveFocusText,
  stripSnippetDecoration,
} from '../utils/editorFocus';
import type { Resume, ResumeJSON, Recommendation, ContactItem, Education, Award, Presentation, Publication, Portfolio, ExperienceCompany, ExperienceRole, ExperienceBullet, UserPreferences, SubcomponentType, GenerationType, SectionSettings, SkillsSettings, SynonymSet, SummarySectionEntry, SummarySectionKey, SummarySections, SummarySplitProposal, Demographics, WritingFingerprint, DedicatedMaster, Status, EditorFocusTarget } from '../types';
import { GENERATION_TYPE_RESTRICTIONS, DEFAULT_SECTION_ORDER } from '../types';
import { GENERATION_TYPE_LABELS } from '../constants';
import yaml from 'js-yaml';
import { BulletTextWithLineBreaks, RESUME_FONT } from './BulletTextWithLineBreaks';

interface ResumeEditorProps {
  isOpen: boolean;
  onClose: () => void;
  // savedName is the resume name that was just persisted. The parent should
  // refresh lists AND set its editing-resume state to savedName so a brand-new
  // resume transitions from create-mode to edit-mode without closing the modal.
  onSuccess: (savedName?: string) => void;
  resumename?: string;
  onDeleteResume?: (resumename: string) => Promise<void>;
  // Switch the editor to another of the user's resumes (Resume ▾ → "Switch
  // resumes"). The parent owns which resume is being edited, so it re-points
  // `resumename` (and its own selected-resume state) and the editor's
  // [isOpen, resumename] load effect reloads through the normal path.
  onSwitchResume?: (resumename: string) => void;
  // Start the app's existing "new resume" flow (quota checks + create-mode
  // editor). The editor closes first, then hands off.
  onNewResume?: () => void;
  // Fired when a CLOSE has successfully dispatched an implied-facts extraction
  // run. The parent owns the wait (this component is unmounting): it polls the
  // resume record until THIS run_id reports done, then opens the review modal.
  onImpliedFactsRun?: (resumename: string, runId: string) => void;
  // Recruiter Check deep-link: open scrolled to this finding's section, with
  // the item flash-highlighted when it can be located. Handled once per
  // `nonce`, so the same finding re-triggers on a second click.
  focusTarget?: EditorFocusTarget | null;
}

// Resume HTML typography constants — keep in sync with skins/default/style.css
// Page: max-width 8.5in, padding 0.6in each side → content width 7.3in = 700.8px
// Footer columns (education/awards/presentations): 50% of 700.8px ≈ 350px
// RESUME_FONT + RESUME_BULLET_WIDTH live in BulletTextWithLineBreaks (shared
// with the Scorecard); imported above so the bullet metrics stay in one place.
// Chrome for every top-level block in the editor's middle scroll pane
// (mockup 19 "rail TOC" `.sec`): a white card with a 1px gray border and
// rounded corners, so it is obvious where one section ends and the next
// begins. Wrapper only — no block's own content or layout depends on it.
const SECTION_CARD = 'rounded-[10px] border border-gray-200 bg-white p-4';

// A dedicated master is still working through search → aggregation → generation.
// Rows in these states are outlined amber and the record is polled.
function dmIsActive(dm: DedicatedMaster): boolean {
  return dm.state === 'searching' || dm.state === 'pending_generate' || dm.state === 'generating';
}

// The server record is authoritative, but a just-created entry can be missing
// from an eventually-consistent read — keep local-only entries so a fresh row
// never blinks out of the list while its generation is being polled.
function mergeDedicatedMasters(local: DedicatedMaster[], server: DedicatedMaster[]): DedicatedMaster[] {
  const serverIds = new Set(server.map(d => d.id));
  return [...server, ...local.filter(d => !serverIds.has(d.id))];
}

const RESUME_WIDTH = 771;          // 7.3in content area in px (+10% for wider lightbox)

// Default icon options — used as fallback until global settings are fetched
const DEFAULT_ICON_OPTIONS: { value: ContactItem['icon']; label: string; svg: string; placeholder: string }[] = [
  { value: 'email-at', label: 'Email', svg: '/icons/email-at.svg', placeholder: 'mailto:email@example.com' },
  { value: 'phone', label: 'Phone', svg: '/icons/phone.svg', placeholder: 'tel:+18889871234' },
  { value: 'phone-volume', label: 'Phone Alt', svg: '/icons/phone-volume.svg', placeholder: 'tel:+18889871234' },
  { value: 'linkedin', label: 'LinkedIn', svg: '/icons/linkedin.svg', placeholder: 'https://www.linkedin.com/in/username' },
  { value: 'x-twitter', label: 'X/Twitter', svg: '/icons/x-twitter.svg', placeholder: 'https://x.com/username' },
  { value: 'bluesky', label: 'Bluesky', svg: '/icons/bluesky.svg', placeholder: 'https://bsky.app/profile/username.bsky.social' },
  { value: 'github', label: 'GitHub', svg: '/icons/github.svg', placeholder: 'https://github.com/username' },
  { value: 'github-square', label: 'GitHub Square', svg: '/icons/github-square.svg', placeholder: 'https://github.com/username' },
  { value: 'globe', label: 'Website', svg: '/icons/globe.svg', placeholder: 'https://your.website.com' },
  { value: 'house', label: 'Home', svg: '/icons/house.svg', placeholder: 'https://www.google.com/maps/place/San+Francisco+Bay+Area' },
  { value: 'facebook', label: 'Facebook', svg: '/icons/facebook.svg', placeholder: 'https://www.facebook.com/username' },
  { value: 'instagram', label: 'Instagram', svg: '/icons/instagram.svg', placeholder: 'https://www.instagram.com/username' },
  { value: 'tiktok', label: 'TikTok', svg: '/icons/tiktok.svg', placeholder: 'https://www.tiktok.com/@username' },
  { value: 'youtube', label: 'YouTube', svg: '/icons/youtube.svg', placeholder: 'https://www.youtube.com/@username' },
  { value: 'discord', label: 'Discord', svg: '/icons/discord.svg', placeholder: 'https://discord.com/users/username' },
  { value: 'slack', label: 'Slack', svg: '/icons/slack.svg', placeholder: 'https://yourworkspace.slack.com/team/yourslackid' },
  { value: 'telegram', label: 'Telegram', svg: '/icons/telegram.svg', placeholder: 'https://t.me/username' },
  { value: 'whatsapp', label: 'WhatsApp', svg: '/icons/whatsapp.svg', placeholder: 'https://wa.me/phonenumber' },
  { value: 'signal-chat', label: 'Signal', svg: '/icons/signal-chat.svg', placeholder: 'https://signal.me/#u/username' },
  { value: 'skillsnap', label: 'SkillSnap', svg: '/icons/skillsnap.svg', placeholder: 'https://username.skillsnap.me' },
];

const GEN_TYPE_CONFIG: Record<GenerationType, { icon: React.ReactNode; className: string; tooltip: string }> = {
  manual: { icon: <Settings className="w-3.5 h-3.5 mr-1" />, className: 'bg-gray-200 text-gray-700 hover:bg-gray-300', tooltip: 'Manual: content appears on bespoke resumes exactly as written, 1:1' },
  embedding: { icon: <Brain className="w-3.5 h-3.5 mr-1" />, className: 'bg-purple-100 text-purple-700 hover:bg-purple-200', tooltip: 'AI Match: AI selects the most relevant items matched against the Job Description, without altering your content' },
  generate: { icon: <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />, className: 'bg-purple-200 text-purple-800 hover:bg-purple-300', tooltip: 'AI Generate: AI fully generates new content tailored to the Job Description — double-check the results!' },
  omit: { icon: <XCircle className="w-3.5 h-3.5 mr-1" />, className: 'bg-red-100 text-red-700 hover:bg-red-200', tooltip: 'Omit: this section will not appear on bespoke resumes' },
};

const DEFAULT_GEN_TYPES: Record<SubcomponentType, GenerationType> = {
  contact: 'manual',
  summary: 'generate',
  skills: 'embedding',
  highlights: 'generate',
  experience: 'embedding',
  education: 'manual',
  awards: 'manual',
  presentations: 'manual',
  publications: 'manual',
  portfolios: 'manual',
  coverletter: 'generate',
  elevatorpitch: 'omit',
};

// Default heading for every section the editor renders. A user-supplied name
// (resumejson.section_settings.names) overrides it; clearing the name — or
// typing the default back — drops the override.
const SECTION_DEFAULT_LABELS: Record<string, string> = {
  contact: 'Contact Information',
  summary: 'Summary',
  highlights: 'Highlights',
  skills: 'Skills',
  experience: 'Experience',
  education: 'Education',
  awards: 'Awards',
  presentations: 'Speaking',
  publications: 'Publications',
  portfolios: 'Portfolios',
  coverletter: 'Cover Letter',
};

// Custom section names are display labels; longer ones are dropped server-side
// (shared/section_settings.MAX_SECTION_NAME_LEN) — cap the input to match.
const MAX_SECTION_NAME_LEN = 80;

// Clean up a stored/legacy section order: keep only sections the editor renders,
// drop duplicates, append anything missing (new sections), and pin contact first.
function normalizeSectionOrder(raw?: SubcomponentType[] | null): SubcomponentType[] {
  const reorderable = DEFAULT_SECTION_ORDER.filter(s => s !== 'contact');
  const allowed = new Set<SubcomponentType>(reorderable);
  const seen = new Set<SubcomponentType>();
  const ordered: SubcomponentType[] = [];
  for (const s of raw || []) {
    if (allowed.has(s) && !seen.has(s)) { seen.add(s); ordered.push(s); }
  }
  for (const s of reorderable) {
    if (!seen.has(s)) { seen.add(s); ordered.push(s); }
  }
  return ['contact', ...ordered];
}

// Demographics option lists (voluntary EEO disclosure). These never appear on a
// generated resume — they only pre-fill applications via the Chrome Extension.
const YES_NO_OPTIONS = ['Yes', 'No'];
const GENDER_OPTIONS = ['Male', 'Female', 'Decline to Answer', 'Other'];
const RACE_OPTIONS = [
  'Hispanic/Latino', 'White', 'Black', 'Pacific Islander', 'Asian',
  'Native American', 'Multi-race', 'Decline to Answer', 'Other',
];
const VET_DIS_OPTIONS = ['Yes', 'No', 'Decline to Answer', 'Other'];

// Canonical empty demographics block — defined next to the normalizer that
// merges it (utils/resumeNormalize), aliased here for the existing call sites.
const emptyDemographics: Demographics = EMPTY_DEMOGRAPHICS;

const emptyResume: ResumeJSON = {
  name: '',
  contact: [],
  summary: '',
  skills: [],
  highlights: [],
  experience: [],
  education: [],
  awards: [],
  presentations: [],
  publications: [],
  portfolios: [],
  summary_phrases: [],
  demographics: { ...emptyDemographics },
};

/**
 * Normalize a recommendation from old "replacement" format to new "action/data" format.
 * Backwards compatible: if rec has "replacement" but no "action", convert it.
 */
export function normalizeRec(rec: Recommendation): Recommendation {
  // 'advise' recommendations are informational: no replacement data, never
  // applied. Keep data null and never coerce the action to 'replace'.
  if (rec.action === 'advise') {
    return { ...rec, data: null };
  }
  if (rec.replacement && !rec.action) {
    const field = rec.field;
    let data: unknown;
    if (field in rec.replacement) {
      data = rec.replacement[field];
    } else {
      const vals = Object.values(rec.replacement);
      data = vals.length > 0 ? vals[0] : null;
    }
    return { ...rec, action: 'replace', data };
  }
  // Ensure action defaults to replace
  if (!rec.action) {
    return { ...rec, action: 'replace' };
  }
  return rec;
}

/**
 * Parse a JSON path into segments.
 * "experience[0].roles[1].bullets[2]" -> [{key:"experience",idx:0}, {key:"roles",idx:1}, {key:"bullets",idx:2}]
 */
function parsePath(path: string): { key: string; idx?: number }[] {
  const segments: { key: string; idx?: number }[] = [];
  const parts = path.split('.');
  for (const part of parts) {
    const match = part.match(/^(\w+)\[(\d+)\]$/);
    if (match) {
      segments.push({ key: match[1], idx: parseInt(match[2], 10) });
    } else {
      segments.push({ key: part });
    }
  }
  return segments;
}

/**
 * Navigate to a target in resume data by path, returning { current, parent, parentKey }.
 */
function navigatePath(obj: any, segments: { key: string; idx?: number }[]): { current: any; parent: any; parentKey: string | number | undefined } {
  let current: any = obj;
  let parent: any = null;
  let parentKey: string | number | undefined;
  for (const seg of segments) {
    if (current == null) return { current: null, parent, parentKey };
    parent = current;
    parentKey = seg.key;
    current = current[seg.key];
    if (current == null) return { current: null, parent, parentKey };
    if (seg.idx !== undefined) {
      if (!Array.isArray(current) || seg.idx >= current.length) return { current: null, parent, parentKey };
      parent = current;
      parentKey = seg.idx;
      current = current[seg.idx];
    }
  }
  return { current, parent, parentKey };
}

/**
 * Apply a recommendation action (replace/append/remove) at the given JSON path.
 * path format: "contacts[0]", "experience[0].roles[0].bullets[1]", "skills[3]", "summary"
 */
function applyActionAtPath(resumeData: ResumeJSON, path: string, field: string, action: string, data: unknown): ResumeJSON {
  const updated = JSON.parse(JSON.stringify(resumeData)) as ResumeJSON;
  const segments = parsePath(path);
  const { current, parent, parentKey } = navigatePath(updated, segments);

  if (current == null && parent == null) return updated;

  if (action === 'replace') {
    if (current != null && typeof current === 'object' && !Array.isArray(current)) {
      // Object target: set the field directly
      current[field] = data;
    } else if (parent != null && parentKey !== undefined) {
      // Scalar or direct target: replace the value at parent
      parent[parentKey] = data;
    }
  } else if (action === 'append') {
    const dataArr = Array.isArray(data) ? data : [data];
    if (current != null && typeof current === 'object' && !Array.isArray(current)) {
      // Object target: append to the field's array
      const existing = Array.isArray(current[field]) ? current[field] as unknown[] : [];
      const merged = [...existing];
      for (const item of dataArr) {
        if (!merged.includes(item)) merged.push(item);
      }
      current[field] = merged;
    } else if (parent != null && parentKey !== undefined) {
      // Array target: append items, coercing scalars into objects when the array
      // already contains objects (e.g., skills items must be {title, tags}).
      if (Array.isArray(current)) {
        const sample = current.length > 0 ? current[0] : null;
        const targetIsObjArr = sample != null && typeof sample === 'object';
        const labelField = targetIsObjArr
          ? ('title' in (sample as object) ? 'title' : 'text' in (sample as object) ? 'text' : 'name')
          : null;
        const merged = [...current];
        for (const item of dataArr) {
          let toAdd: unknown = item;
          if (targetIsObjArr && labelField && typeof item === 'string') {
            toAdd = { [labelField]: item, tags: [] };
          }
          if (typeof toAdd === 'object') {
            merged.push(toAdd);  // objects: skip reference-equality dedup
          } else if (!merged.includes(toAdd)) {
            merged.push(toAdd);
          }
        }
        parent[parentKey] = merged;
      }
    }
  } else if (action === 'remove') {
    const dataArr = Array.isArray(data) ? data : [data];
    const removeSet = new Set(dataArr.map(String));
    if (current != null && typeof current === 'object' && !Array.isArray(current) && Array.isArray((current as any)[field])) {
      // Object target with array field: remove items from current[field] by stringified value
      (current as any)[field] = ((current as any)[field] as unknown[]).filter(
        (item: unknown) => !removeSet.has(String(item))
      );
    } else if (Array.isArray(current) && parent != null && parentKey !== undefined) {
      // Array target: remove matching items.
      // For arrays of strings, match by stringified value.
      // For arrays of objects, match by item[field] (e.g., skills filtered by title).
      parent[parentKey] = current.filter((item: unknown) => {
        if (item != null && typeof item === 'object' && field) {
          return !removeSet.has(String((item as any)[field]));
        }
        return !removeSet.has(String(item));
      });
    } else if (Array.isArray(parent) && typeof parentKey === 'number') {
      // Item-in-array target (e.g., path "skills[9]"): remove the whole item from its parent array.
      (parent as unknown[]).splice(parentKey, 1);
    }
  }

  return updated;
}

// ---- Top/bottom chrome tokens (mirror the `20-section-rail` mockup) --------
/** Base metrics shared by every chrome button. */
const TB_BTN = 'inline-flex items-center gap-1.5 h-[34px] px-[11px] rounded-lg text-[13px] font-medium whitespace-nowrap transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
/** Outlined button (Import / Export / Preview / Resume / Save). */
const TB_OUT = `${TB_BTN} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400`;
/** Quiet button (Cancel / stepper). */
const TB_GHOST = `${TB_BTN} text-gray-700 hover:bg-gray-100`;
/** Filled sky button (Review resume / Save and Close). */
const TB_PRIMARY = `${TB_BTN} bg-primary-600 text-white hover:bg-primary-700`;
/** Dropdown surface. */
const TB_MENU = 'absolute top-full mt-1.5 right-0 z-30 min-w-[214px] bg-white border border-gray-200 rounded-[10px] p-[5px] shadow-[0_12px_28px_rgba(17,24,39,0.14),0_2px_6px_rgba(17,24,39,0.06)]';
const TB_MENU_ITEM = 'flex items-center gap-2.5 w-full px-2.5 py-[7px] rounded-md text-[13px] text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-left';
/** Thin vertical divider between chrome groups. */
const TB_VR = 'w-px h-6 bg-gray-200 flex-shrink-0 mx-[3px]';
/** Small uppercase group label inside a dropdown ("This resume" / "Switch resumes"). */
const TB_MENU_GROUP = 'px-2.5 pt-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500';

/**
 * "View Resume ▾" for one published master resume URL — the web page and the
 * PDF published beside it. Used by the classic master URL row and by every
 * role-targeted master row.
 */
function MasterUrlViewMenu({ url, uat }: { url: string; uat: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const pdfUrl = masterPdfUrl(url);
  const item = (href: string, label: string, hint: string, Icon: typeof ExternalLink, itemUat: string) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      role="menuitem"
      data-uat={itemUat}
      onClick={() => setOpen(false)}
      className={TB_MENU_ITEM}
    >
      <Icon className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
      <span>
        <span className="block font-medium">{label}</span>
        <span className="block text-[11.5px] text-gray-500">{hint}</span>
      </span>
    </a>
  );

  return (
    <div ref={ref} className="relative ml-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-uat={`${uat}-menu`}
        className="flex items-center gap-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm whitespace-nowrap"
        title="View Resume"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <ExternalLink className="w-4 h-4" />
        <span>View Resume</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="menu" className={TB_MENU}>
          {item(url, 'Web Page', 'The live resume page', ExternalLink, `${uat}-web`)}
          {item(pdfUrl, 'PDF', 'The same resume as a PDF', FileText, `${uat}-pdf`)}
        </div>
      )}
    </div>
  );
}

/**
 * "edited 4 min ago" style metadata for a resume row in the Resume menu.
 * Returns null for a missing/unparseable timestamp so the row simply renders
 * without a subtitle rather than showing a bogus date.
 */
function editedAgoLabel(iso?: string): string | null {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return null;
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 0) return 'edited just now';
  if (mins < 1) return 'edited just now';
  if (mins < 60) return `edited ${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `edited ${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `edited ${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 52) return `edited ${weeks}w ago`;
  return `edited ${Math.floor(days / 365)}y ago`;
}

export function ResumeEditor({ isOpen, onClose, onSuccess, resumename, onDeleteResume, onSwitchResume, onNewResume, onImpliedFactsRun, focusTarget }: ResumeEditorProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // Tag-generation status shown in the import notes box after an AI import.
  // 'generating' while the async tag pipeline runs, 'done' once tags land,
  // 'timeout' if it takes unusually long. Takes visual precedence over `warning`.
  const [tagGenPhase, setTagGenPhase] = useState<'idle' | 'generating' | 'done' | 'timeout'>('idle');
  const tagPollRef = useRef<{ cancelled: boolean } | null>(null);
  const [name, setName] = useState('');
  const [resume, setResume] = useState<ResumeJSON>(emptyResume);
  const [showScorecard, setShowScorecard] = useState(false);
  // Recruiter Check BR-1: the Scorecard opened FROM the master-resume warning
  // leads with the high-severity findings; opened any other way it shows
  // everything, exactly as before.
  const [scorecardFocusHigh, setScorecardFocusHigh] = useState(false);
  // Writing voice fingerprint (per-user; read-only via prefs). Drives the
  // "Provide Writing Sample" section chip above Summary.
  const [writingFingerprint, setWritingFingerprint] = useState<WritingFingerprint | null>(null);
  const [showWritingModal, setShowWritingModal] = useState(false);
  const [deletingFingerprint, setDeletingFingerprint] = useState(false);
  // Per-group "add skill" drafts, keyed by group id (or '' for ungrouped).
  const [newSkillByGroup, setNewSkillByGroup] = useState<Record<string, string>>({});
  // Drag-drop: index of the skill being dragged + the group currently hovered.
  const [draggedSkillIdx, setDraggedSkillIdx] = useState<number | null>(null);
  const [dragOverGroup, setDragOverGroup] = useState<string | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  // Synonym/skill conflict (task 8): which skill indices + synonym set ids clash.
  const [conflictSkillIdxs, setConflictSkillIdxs] = useState<Set<number>>(new Set());
  const [conflictSetIds, setConflictSetIds] = useState<Set<string>>(new Set());
  // Date boxes whose text the shared parser couldn't read. Same visual language
  // as the synonym conflict above: paint the offending input red and block Save
  // until it's fixed. Keys are the `dateFieldKey()` strings below.
  const [badDateKeys, setBadDateKeys] = useState<Set<string>>(new Set());
  const [newSummaryPhrase, setNewSummaryPhrase] = useState('');
  // Summary v2 (features/summary_v2.md): per-subsection "add entry" boxes, the
  // one entry currently open for editing, and whether the legacy phrase bank is
  // expanded (it collapses once subsections exist alongside it).
  const [newSummaryEntry, setNewSummaryEntry] = useState<Record<string, string>>({});
  const [editingSummaryEntry, setEditingSummaryEntry] = useState<{ key: SummarySectionKey; index: number } | null>(null);
  const [legacyPhrasesOpen, setLegacyPhrasesOpen] = useState(false);

  // New contact item state
  const [newContactIcon, setNewContactIcon] = useState<ContactItem['icon']>('email-at');
  const [newContactTitle, setNewContactTitle] = useState('');
  const [newContactUrl, setNewContactUrl] = useState('');
  const [newSynonymTerms, setNewSynonymTerms] = useState('');
  const [synonymDrafts, setSynonymDrafts] = useState<Record<string, string>>({});
  // Implied Facts card: a click-to-edit draft per fact + the "add your own" box.
  const [newImpliedFact, setNewImpliedFact] = useState('');
  const [factDrafts, setFactDrafts] = useState<Record<string, string>>({});
  const [newContactAutoFill, setNewContactAutoFill] = useState(false);
  const [editContactAutoFill, setEditContactAutoFill] = useState(false);
  // Demographics salary floor: raw digits while focused, formatted on blur.
  const [salaryFocused, setSalaryFocused] = useState(false);
  // Accordion state for EVERY editor card, keyed by nav key. Only the blocks
  // the user has explicitly toggled are stored (see utils/editorSections) —
  // everything else follows the live default, which for Awards / Speaking /
  // Publications / Portfolios depends on their generation type. Clicking a
  // left-nav row expands its card (see scrollToNavKey).
  const [openSectionOverrides, setOpenSectionOverrides] = useState<Record<string, boolean>>(() => readOpenSections());

  // New education state
  const [newEduTitle, setNewEduTitle] = useState('');
  const [newEduOrg, setNewEduOrg] = useState('');
  const [newEduYear, setNewEduYear] = useState('');
  
  // New award state
  const [newAwardTitle, setNewAwardTitle] = useState('');
  const [newAwardIssuer, setNewAwardIssuer] = useState('');
  const [newAwardReward, setNewAwardReward] = useState('');
  const [newAwardYear, setNewAwardYear] = useState('');

  // New presentation state
  const [newPresentationTitle, setNewPresentationTitle] = useState('');
  const [newPresentationEvent, setNewPresentationEvent] = useState('');
  const [newPresentationYear, setNewPresentationYear] = useState('');

  // New publication state
  const [newPubTitle, setNewPubTitle] = useState('');
  const [newPubUrl, setNewPubUrl] = useState('');
  const [newPubYear, setNewPubYear] = useState('');

  // New portfolio state
  const [newPortTitle, setNewPortTitle] = useState('');
  const [newPortUrl, setNewPortUrl] = useState('');
  const [newPortYear, setNewPortYear] = useState('');

  // Experience editing state
  const [editingContactIndex, setEditingContactIndex] = useState<number | null>(null);
  const [editingSkillIndex, setEditingSkillIndex] = useState<number | null>(null);
  const [editingSummaryPhraseIndex, setEditingSummaryPhraseIndex] = useState<number | null>(null);
  const [editingEducationIndex, setEditingEducationIndex] = useState<number | null>(null);
  const [editingAwardIndex, setEditingAwardIndex] = useState<number | null>(null);
  const [editingPresentationIndex, setEditingPresentationIndex] = useState<number | null>(null);
  const [editingPublicationIndex, setEditingPublicationIndex] = useState<number | null>(null);
  const [editingPortfolioIndex, setEditingPortfolioIndex] = useState<number | null>(null);
  const [editingCompanyIndex, setEditingCompanyIndex] = useState<number | null>(null);
  const [editingRoleIndex, setEditingRoleIndex] = useState<{ companyIdx: number; roleIdx: number } | null>(null);

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Clone state
  const [cloning, setCloning] = useState(false);

  // Import state
  const [importing, setImporting] = useState(false);
  // Full-resume export state ('pdf' | 'text' while a request is in flight)
  const [exporting, setExporting] = useState<null | 'pdf' | 'text'>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);
  // Cmd/Ctrl+S = the footer "Save" button (save WITHOUT closing), never the
  // browser's save-page dialog. Gated on isOpen because this component stays
  // mounted while closed; the in-flight guard lives here rather than in the
  // hook so the chord is still swallowed while a save is running.
  useSaveShortcut(() => {
    if (!saving && !loading && !importing) void handleSave(false);
  }, isOpen);

  // AI Review state
  const [reviewing, setReviewing] = useState(false);
  const [reviewComplete, setReviewComplete] = useState(false);
  const [editedReplacements, setEditedReplacements] = useState<Record<string, string>>({});
  const [showAiUpgradeModal, setShowAiUpgradeModal] = useState(false);

  // Summary v2 AI CTAs (features/summary_v2.md): "Split into sections" (assisted
  // migration from existing summary content) and "Auto-generate Summary
  // Fragments" (bootstrap from the whole resume). Both propose, neither saves.
  // `summarySplitNeedsNumber` latches the server's verdict that NOTHING it
  // proposed for Credibility carries a figure; the nudge then stays up until a
  // credibility entry actually has one.
  const [splittingSummary, setSplittingSummary] = useState(false);
  const [bootstrappingFragments, setBootstrappingFragments] = useState(false);
  const [summarySplitNeedsNumber, setSummarySplitNeedsNumber] = useState(false);

  // Generic Resume URL state. The generic URL is ALWAYS the naked root
  // (https://{username}.skillsnap.me/), and exactly ONE of the user's resumes
  // is published there — `publishMasterResume` says whether it is this one.
  // Publishing is an explicit act ("Publish now"); most recent publish wins.
  const [publishMasterResume, setPublishMasterResume] = useState(false);
  // The resume that owns the root URL when it is NOT this one ('' = nothing
  // published yet, null = not known / other resumes not loaded).
  const [publishedElsewhere, setPublishedElsewhere] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  // Role-targeted paths on the user's OTHER resumes. Sharing a path is allowed
  // (the most recently generated one wins), so this is only an informational
  // note in the add flow — never a rejection.
  const [otherDmPaths, setOtherDmPaths] = useState<string[]>([]);

  // Dedicated (role-targeted) master resumes — extra master URLs generated
  // against an aggregation of same-title jobs. The list lives on the resume
  // record (top-level `dedicated_masters`), not in resumejson, so it is never
  // part of the Save payload; every mutation goes through its own endpoint.
  const [dedicatedMasters, setDedicatedMasters] = useState<DedicatedMaster[]>([]);
  const dedicatedMastersRef = useRef<DedicatedMaster[]>([]);
  const [showDedicatedMasterModal, setShowDedicatedMasterModal] = useState(false);
  const [dmBusyId, setDmBusyId] = useState<string | null>(null);
  const [dmError, setDmError] = useState<string | null>(null);
  // Click-to-edit "Targeted at:" — the row whose criteria is being edited, and
  // the rows edited this session. Keywords are only replayed at REGENERATE
  // time, so an edited row carries a "rebuild to apply" hint until the user
  // clicks its Regenerate button (which clears the id).
  const [editingDmKeywordsId, setEditingDmKeywordsId] = useState<string | null>(null);
  const [dmKeywordsEdited, setDmKeywordsEdited] = useState<Set<string>>(new Set());
  // Mirrors editingDmKeywordsId synchronously so an Enter commit followed by
  // the input's blur can't fire the save twice (state updates are batched).
  const editingDmKeywordsRef = useRef<string | null>(null);
  // Feature gate: status=='active' only. Read from the preferences the editor
  // already loads; null until that fetch lands (or if it failed) — in which
  // case the control stays enabled and the server's 403 is what gates.
  const [userStatus, setUserStatus] = useState<Status | null>(null);
  const [dmAccessDenied, setDmAccessDenied] = useState(false);
  const dmPollRef = useRef<{ cancelled: boolean } | null>(null);

  // Bottom-left "Resume" actions menu (View HTML / View PDF / Clone / Delete)
  const [resumeMenuOpen, setResumeMenuOpen] = useState(false);
  const resumeMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!resumeMenuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (resumeMenuRef.current && !resumeMenuRef.current.contains(e.target as Node)) {
        setResumeMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [resumeMenuOpen]);

  // "Switch resumes" group inside that menu: the user's other resumes, loaded
  // from the SAME endpoint the header's resume selector uses (api.listResumes,
  // which returns name + lastupdate only — no resumejson). Fetched when the
  // menu opens so it is always current, and never on the editor's open path.
  const [switchResumes, setSwitchResumes] = useState<{ resumename: string; lastupdate?: string }[] | null>(null);
  const [switchLoading, setSwitchLoading] = useState(false);
  // A switch/new-resume action parked behind the unsaved-changes confirm.
  const [pendingNav, setPendingNav] = useState<{ kind: 'switch'; resumename: string } | { kind: 'new' } | null>(null);
  useEffect(() => {
    if (!resumeMenuOpen) return;
    let cancelled = false;
    setSwitchLoading(true);
    api.listResumes()
      .then(data => {
        if (cancelled) return;
        setSwitchResumes((data.resumes || []).map((r: any) => ({ resumename: r.resumename, lastupdate: r.lastupdate })));
      })
      .catch(() => { if (!cancelled) setSwitchResumes([]); })
      .finally(() => { if (!cancelled) setSwitchLoading(false); });
    return () => { cancelled = true; };
  }, [resumeMenuOpen]);
  const otherResumes = useMemo(
    () => (switchResumes || []).filter(r => r.resumename !== resumename),
    [switchResumes, resumename]
  );

  // Top "Export" menu (PDF / Text File / Data File)
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!exportMenuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [exportMenuOpen]);

  // Top "Import" menu (Load from PDF / Load from file) — both open the same
  // file picker (it accepts .pdf / .yaml / .yml / .json).
  const [importMenuOpen, setImportMenuOpen] = useState(false);
  const importMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!importMenuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (importMenuRef.current && !importMenuRef.current.contains(e.target as Node)) {
        setImportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [importMenuOpen]);

  // Top-bar resume name: reads as inline text, becomes an input on click.
  // It is still the required "Resume Name" field — only the chrome changed.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Escape cancels a name edit; the ref stops the ensuing blur from committing.
  const cancelNameEditRef = useRef(false);

  function startEditName() {
    if (importing || loading) return;
    cancelNameEditRef.current = false;
    setNameDraft(name);
    setEditingName(true);
  }

  function commitName() {
    if (cancelNameEditRef.current) {
      cancelNameEditRef.current = false;
      setEditingName(false);
      return;
    }
    setName(nameDraft);
    setEditingName(false);
  }

  useEffect(() => {
    if (editingName) nameInputRef.current?.select();
  }, [editingName]);

  // "All recommendations implemented" banner: show ONCE after the user applies
  // all recommendations, then suppress on subsequent opens (persisted per-resume
  // in localStorage). A fresh AI Review re-arms it.
  const recsAckKey = resumename ? `ss_recs_implemented_ack_${resumename}` : '';
  const [recsImplementedAcked, setRecsImplementedAcked] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    try {
      setRecsImplementedAcked(!!recsAckKey && localStorage.getItem(recsAckKey) === '1');
    } catch {
      setRecsImplementedAcked(false);
    }
  }, [isOpen, recsAckKey]);

  // Contact icon options — loaded from global settings, falls back to hardcoded defaults
  const [iconOptions, setIconOptions] = useState(DEFAULT_ICON_OPTIONS);
  // Alpha-sort the contact icon selector by label so icons are easy to find by name.
  const sortedIconOptions = useMemo(
    () => [...iconOptions].sort((a, b) => a.label.localeCompare(b.label)),
    [iconOptions]
  );

  // ---- Per-resume section settings (part of the resume draft) --------------
  // Generation types, custom display names and section order live on the
  // resume at resumejson.section_settings and are persisted ONLY by Save,
  // like every other field in this lightbox.
  const [sectionGenTypes, setSectionGenTypes] = useState<Record<SubcomponentType, GenerationType>>({ ...DEFAULT_GEN_TYPES });
  const [sectionNames, setSectionNames] = useState<Partial<Record<SubcomponentType, string>>>({});
  const [sectionOrder, setSectionOrder] = useState<SubcomponentType[]>(() => normalizeSectionOrder(null));
  const [editingSectionTitle, setEditingSectionTitle] = useState<SubcomponentType | null>(null);
  const [sectionTitleDraft, setSectionTitleDraft] = useState('');
  // Escape cancels a title edit; the ref stops the ensuing blur from committing.
  const cancelSectionTitleRef = useRef(false);
  // The prefs fetch (legacy per-user defaults) and the resume load land
  // independently, so each stashes its half here and re-derives the draft.
  // Once the user touches a section setting the derive stops — their edits win.
  const prefSectionDefaultsRef = useRef<{ genTypes: Partial<Record<SubcomponentType, GenerationType>>; names: Partial<Record<SubcomponentType, string>>; order: SubcomponentType[] | null } | null>(null);
  const loadedSectionSettingsRef = useRef<SectionSettings | null>(null);
  const sectionSettingsTouchedRef = useRef(false);

  // Sections rendered in the reorderable middle of the editor (contact is pinned).
  const orderedSections = useMemo<SubcomponentType[]>(() => sectionOrder.filter(s => s !== 'contact'), [sectionOrder]);

  // ---- Right-hand section nav + live preview -------------------------------
  // The nav rail drives (and reflects) the SAME section-order / gen-type /
  // custom-name state the editor body and the save payload already use.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeNavKey, setActiveNavKey] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // ---- Section accordions --------------------------------------------------
  // Resolved open state: an explicit user toggle wins, otherwise the default
  // for that block (which reads the CURRENT generation types).
  function isSectionOpen(key: string): boolean {
    return resolveSectionOpen(key, openSectionOverrides, sectionGenTypes);
  }

  function setSectionOpen(key: string, open: boolean) {
    setOpenSectionOverrides(prev => {
      if (prev[key] === open) return prev;
      const next = { ...prev, [key]: open };
      writeOpenSections(next);
      return next;
    });
  }

  function toggleSectionOpen(key: string) {
    setSectionOpen(key, !isSectionOpen(key));
  }

  /**
   * Accordion chevron for a card header. Same visual language as every other
   * toggle in the editor: a chevron that rotates -90° when the card is closed,
   * with `aria-expanded` carrying the state for assistive tech.
   */
  function renderSectionToggle(key: string, label: string) {
    const open = isSectionOpen(key);
    return (
      <button
        type="button"
        onClick={() => toggleSectionOpen(key)}
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
        title={`${open ? 'Collapse' : 'Expand'} ${label}`}
        data-testid={`section-toggle-${key}`}
        className="flex-shrink-0 text-gray-400 hover:text-gray-700"
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
    );
  }

  /**
   * Card header for the FIXED editor blocks — the ones with no editable
   * section title. The whole heading is the accordion toggle, exactly like the
   * Implied Facts card it is modelled on. `suffix` renders inside the button
   * after the title (a count, an "(Optional)" note, …).
   */
  function renderCardHeader(key: string, title: string, suffix?: React.ReactNode) {
    const open = isSectionOpen(key);
    return (
      <button
        type="button"
        onClick={() => toggleSectionOpen(key)}
        aria-expanded={open}
        data-testid={`section-toggle-${key}`}
        className="flex items-center gap-1.5 text-base font-bold text-gray-700 hover:text-gray-900 mb-1"
      >
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? '' : '-rotate-90'}`} />
        {title}
        {suffix}
      </button>
    );
  }

  // Scroll the editor pane to a nav row's block.
  function scrollToNavKey(key: string) {
    const root = scrollRef.current;
    if (!root) return;
    // Navigating to a COLLAPSED card also expands it — landing on a closed card
    // the user explicitly asked for would read as broken. The scroll then waits
    // a frame so it measures the expanded height, not the collapsed one.
    const wasClosed = !isSectionOpen(key);
    if (wasClosed) setSectionOpen(key, true);
    const doScroll = () => {
      const el = root.querySelector<HTMLElement>(`[data-nav-key="${key}"]`);
      if (!el) return;
      const top = root.scrollTop + (el.getBoundingClientRect().top - root.getBoundingClientRect().top) - 8;
      root.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };
    if (wasClosed) requestAnimationFrame(doScroll);
    else doScroll();
    setActiveNavKey(key);
  }

  // --- Recruiter Check deep-link ----------------------------------------
  // Scroll to the finding's SECTION (always possible from the rec path), then
  // best-effort locate the ITEM by its text and flash it. Item lookup is by
  // text because editor rows carry no per-finding ids — a miss leaves the user
  // on the right section, which is the useful half.
  const handledFocusNonceRef = useRef<number | null>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (focusTimerRef.current) clearTimeout(focusTimerRef.current); }, []);

  useEffect(() => {
    if (!isOpen) { handledFocusNonceRef.current = null; return; }
    if (loading || !focusTarget) return;
    // `loading` is false for one render BEFORE loadResume starts, so also wait
    // for the requested resume to actually be in state — otherwise the nonce
    // would be consumed against an empty resume and never fire again.
    if (resumename && name !== resumename) return;
    if (handledFocusNonceRef.current === focusTarget.nonce) return;
    handledFocusNonceRef.current = focusTarget.nonce;

    const key = navKeyForPath(focusTarget.path);
    if (!key) return;
    scrollToNavKey(key);

    const needle = resolveFocusText(resume, focusTarget.path, focusTarget.field)
      || stripSnippetDecoration(focusTarget.snippet);
    if (!needle) return;

    // Wait for the section scroll (and any expand) to settle so the item's
    // own scrollIntoView isn't cancelled by the one already in flight.
    if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    focusTimerRef.current = setTimeout(() => {
      try {
        const card = scrollRef.current?.querySelector(`[data-nav-key="${key}"]`);
        const el = findFocusElement(card || null, needle);
        if (!el) return;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        flashFocusElement(el);
      } catch { /* deep-link is best effort — never break the editor */ }
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, loading, name, resumename, focusTarget?.nonce]);

  // Scrollspy: highlight the nav row whose block is at the top of the pane.
  // IntersectionObserver is only the trigger — the winner is recomputed from
  // live rects so a batch that omits an element can't pick a stale one.
  useEffect(() => {
    if (!isOpen || loading) return;
    const root = scrollRef.current;
    if (!root) return;
    // Nav keys in reading order — drives the footer's "Section N of M" stepper.
    const keys: string[] = [];
    root.querySelectorAll<HTMLElement>('[data-nav-key]').forEach(el => {
      if (el.dataset.navKey) keys.push(el.dataset.navKey);
    });
    setNavKeyOrder(prev => (prev.join('|') === keys.join('|') ? prev : keys));

    const recompute = () => {
      const rootTop = root.getBoundingClientRect().top;
      let best: string | null = null;
      let bestTop = -Infinity;
      root.querySelectorAll<HTMLElement>('[data-nav-key]').forEach(el => {
        const top = el.getBoundingClientRect().top - rootTop;
        // Topmost block whose start is at/above the 96px "reading line".
        if (top <= 96 && top > bestTop) { bestTop = top; best = el.dataset.navKey || null; }
      });
      if (!best) {
        const first = root.querySelector<HTMLElement>('[data-nav-key]');
        best = first?.dataset.navKey || null;
      }
      setActiveNavKey(prev => (prev === best ? prev : best));
    };
    const observer = new IntersectionObserver(recompute, { root, threshold: [0, 1] });
    root.querySelectorAll<HTMLElement>('[data-nav-key]').forEach(el => observer.observe(el));
    root.addEventListener('scroll', recompute, { passive: true });
    recompute();
    return () => {
      observer.disconnect();
      root.removeEventListener('scroll', recompute);
    };
  }, [isOpen, loading, orderedSections]);

  // Nav row status: omitted sections read as hollow, everything else is
  // "ready" once it has content. Highlights and the cover letter have no
  // authored content at all (they are written per job), so they count as
  // ready whenever they are not omitted.
  function sectionState(component: SubcomponentType): SectionState {
    if (sectionGenTypes[component] === 'omit') return 'omitted';
    const filled = (n: number) => (n > 0 ? 'ready' : 'empty') as SectionState;
    switch (component) {
      case 'contact': return filled((resume.contact || []).length);
      case 'summary': return filled((resume.summary || '').trim().length);
      case 'skills': return filled((resume.skills || []).length);
      case 'experience': return filled(getExperience().length);
      case 'education': return filled((resume.education || []).length);
      case 'awards': return filled((resume.awards || []).length);
      case 'presentations': return filled((resume.presentations || []).length);
      case 'publications': return filled((resume.publications || []).length);
      case 'portfolios': return filled((resume.portfolios || []).length);
      default: return 'ready';   // highlights / coverletter — generated per job
    }
  }

  // Status for the nav rows that are NOT resume sections (pinned rows), so the
  // rail can show an honest dot for every row it renders.
  function navRowState(key: string): SectionState {
    switch (key) {
      case 'basics':
        return (resume.name || '').trim() ? 'ready' : 'empty';
      case RESUME_URLS_KEY:
        // A saved resume always publishes at least the generic URL; an unsaved
        // one has no URL yet.
        return resumename ? 'ready' : 'empty';
      case 'synonyms':
        return (resume.synonyms || []).some(s => (s.terms || []).filter(t => t.trim()).length >= 2) ? 'ready' : 'empty';
      case 'impliedfacts':
        // "Ready" means the user has actually vetted something — an unreviewed
        // AI proposal is not yet ground truth, so it reads as empty.
        return (resume.implied_facts || []).some(f => f.status === 'confirmed') ? 'ready' : 'empty';
      case 'writingsample':
        return writingFingerprint ? 'ready' : 'empty';
      case 'demographics': {
        const d = resume.demographics || {};
        return Object.entries(d).some(([k, v]) => k !== 'location_country' && String(v || '').trim()) ? 'ready' : 'empty';
      }
      default:
        return sectionState(key as SubcomponentType);
    }
  }

  // "N of M sections ready" — M counts every printable section that is not
  // omitted, N counts those with content (or AI-generated per job).
  const navProgress = useMemo(() => {
    const printable: SubcomponentType[] = ['contact', ...orderedSections, 'coverletter'];
    let done = 0;
    let total = 0;
    for (const c of printable) {
      const state = sectionState(c);
      if (state === 'omitted') continue;
      total += 1;
      if (state === 'ready') done += 1;
    }
    return { done, total };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedSections, sectionGenTypes, resume]);

  // ---- Save state (bottom bar) --------------------------------------------
  // A snapshot of exactly what a Save would send; comparing it to the last
  // saved/loaded snapshot is what "Unsaved changes" means. Section settings are
  // part of the payload, so a gen-type cycle or a drag counts as a change.
  const saveSnapshot = useMemo(
    () => JSON.stringify({ n: name.trim(), r: withSectionSettings(resume) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, resume, sectionGenTypes, sectionNames, sectionOrder],
  );
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [savedClockTick, setSavedClockTick] = useState(0);
  // Baseline re-arms after every load/save; the effect below then captures the
  // snapshot once the derived section settings have settled. `baselineNonce` is
  // what a keep-open Save bumps: the ref alone can't re-run the effect, and a
  // save that leaves the payload byte-identical (the normal case — we just sent
  // it) produces the SAME `saveSnapshot` string, so without the nonce the effect
  // never re-fires and the footer stays stuck on "Unsaved changes".
  const baselineArmedRef = useRef(false);
  const [baselineNonce, setBaselineNonce] = useState(0);

  useEffect(() => {
    if (!isOpen || loading) return;
    if (baselineArmedRef.current) return;
    // Debounce: the section-settings derive lands a tick after the load, so
    // wait for the snapshot to stop moving before calling it "saved".
    const t = setTimeout(() => {
      baselineArmedRef.current = true;
      setSavedSnapshot(saveSnapshot);
    }, 400);
    return () => clearTimeout(t);
  }, [isOpen, loading, saveSnapshot, baselineNonce]);

  // Re-arm on open/close so a reopened editor starts clean.
  useEffect(() => {
    if (isOpen) return;
    baselineArmedRef.current = false;
    setSavedSnapshot(null);
    setLastSavedAt(null);
  }, [isOpen]);

  // ---- Shared server lint (Accuracy dimension) ----------------------------
  // Accuracy is 45% of the scorecard and only the server can compute it, so the
  // rail/chip must NOT show a lint-less (higher) number while waiting for the
  // user to open the Scorecard modal. One pass runs here as soon as the resume
  // has loaded, and again after every Save; the modal consumes the same state
  // (and hands its own passes back via `apply`).
  const resumeRef = useRef(resume);
  resumeRef.current = resume;
  const lintName = resumename || resume.name?.replace(/[^\w .-]/g, '').trim() || 'resume';
  const scorecardLint = useResumeLint({
    // `savedSnapshot !== null` is the editor's own "loaded and settled" signal —
    // linting an empty resume mid-load would produce a meaningless zero-findings
    // pass that never refreshes until the next save.
    enabled: isOpen && !loading && savedSnapshot !== null,
    lintName,
    // Switching resumes inside the open editor starts a FRESH pass (the old
    // resume's score must not linger, or be shown for the new one).
    resumeKey: resumename,
    resumeRef,
  });

  // Deterministic Resume Scorecard overall — the headline number on the rail's
  // score bar and the top-bar Scorecard chip. Recomputed live on every edit
  // (same builder the Scorecard modal uses). It scores net of the user's
  // resolved findings (`resume.scorecard_resolved`, updated the moment the modal
  // marks one resolved) over the same four dimensions the modal shows — so the
  // chip, the rail bar and the modal always agree.
  const scorecardScore = useMemo(
    () => buildScorecard(resume, { lintFindings: scorecardLint.findings, resolvedIds: resume.scorecard_resolved }).overall,
    [resume, scorecardLint.findings],
  );

  /**
   * Recruiter Check BR-1 — unresolved HIGH-severity lint findings on this
   * master resume. Every one of them is copied onto every resume generated from
   * it, which users do not infer, so it is said plainly at the top of the
   * editor. Counted from the live pass, so marking one resolved or fixing it
   * drops the number immediately. 0 while the first pass is still running.
   */
  const masterLintHigh = useMemo(
    () => liveLintHighCount(resume, scorecardLint.findings),
    [resume, scorecardLint.findings],
  );

  // Keep the "N minutes ago" label honest while the editor stays open.
  useEffect(() => {
    if (!isOpen || lastSavedAt === null) return;
    const id = setInterval(() => setSavedClockTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, [isOpen, lastSavedAt]);

  const isDirty = savedSnapshot !== null && saveSnapshot !== savedSnapshot;
  const lastSavedLabel = useMemo(() => {
    if (lastSavedAt === null) return null;
    const mins = Math.floor((Date.now() - lastSavedAt) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSavedAt, savedClockTick]);

  // ---- Bottom-bar section stepper -----------------------------------------
  // Nav keys in the editor's own DOM order (kept in sync by the scrollspy
  // effect), so "Section N of M" and Previous/Next match what the user reads.
  const [navKeyOrder, setNavKeyOrder] = useState<string[]>([]);
  const navKeyLabel = (key: string): string => {
    switch (key) {
      case 'basics': return 'Name';
      case RESUME_URLS_KEY: return 'Resume URLs';
      case 'synonyms': return 'Define Synonyms';
      case 'impliedfacts': return 'Implied Facts';
      case 'writingsample': return 'Writing Sample';
      case 'demographics': return 'Demographics';
      default: return sectionLabel(key as SubcomponentType);
    }
  };
  const navIndex = activeNavKey ? navKeyOrder.indexOf(activeNavKey) : -1;

  // Fetch contact icon list from global settings on mount
  useEffect(() => {
    api.getPublicSetting('contact_icon_list')
      .then(data => {
        if (Array.isArray(data.setting_value) && data.setting_value.length > 0) {
          setIconOptions(data.setting_value);
        }
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  // Cancel any in-flight poll loop on unmount.
  useEffect(() => {
    return () => {
      if (tagPollRef.current) tagPollRef.current.cancelled = true;
      if (dmPollRef.current) dmPollRef.current.cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setError(null); // Clear any previous errors when modal opens
      // Reset the section-settings draft so a freshly opened resume derives
      // from its own settings (and the legacy prefs) rather than the last one.
      sectionSettingsTouchedRef.current = false;
      prefSectionDefaultsRef.current = null;
      loadedSectionSettingsRef.current = null;
      setEditingSectionTitle(null);
      loadSectionGenTypes();
      if (resumename) {
        loadResume();
      } else {
        loadedSectionSettingsRef.current = {};
        deriveSectionSettings();
        setName('');
        setResume(emptyResume);
        setPublishMasterResume(false);
        setPublishedElsewhere(null);
        setOtherDmPaths([]);
        applyDedicatedMasters([]);
      }
      setDmError(null);
      setDmBusyId(null);
    }
  }, [isOpen, resumename]);

  // Stop polling the dedicated-master list once the editor is closed; reopening
  // re-starts it from loadResume if anything is still in flight.
  useEffect(() => {
    if (!isOpen && dmPollRef.current) dmPollRef.current.cancelled = true;
  }, [isOpen]);

  // NOTE: ESC-to-close is intentionally NOT wired for the Edit Resume lightbox.
  // It's a large form and an accidental ESC losing in-progress edits is more
  // annoying than helpful; users close via Cancel / Save and Close. (The
  // Scorecard sub-modal keeps its own ESC handler.)

  // Click outside skill to close editing
  useEffect(() => {
    if (editingSkillIndex === null) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`[data-skill-index="${editingSkillIndex}"]`)) {
        setEditingSkillIndex(null);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [editingSkillIndex]);

  // Once a synonym/skill conflict has been flagged (on a blocked save), re-evaluate
  // live as the user edits skills/synonyms so the red highlight clears the moment
  // the clash is resolved. Only runs while a conflict is currently flagged.
  useEffect(() => {
    if (conflictSkillIdxs.size === 0 && conflictSetIds.size === 0) return;
    const c = computeSynonymConflicts(resume);
    setConflictSkillIdxs(c.skillIdxs);
    setConflictSetIds(c.setIds);
    if (c.setIds.size === 0) setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume.skills, resume.synonyms]);

  // Click outside summary phrase to close editing
  useEffect(() => {
    if (editingSummaryPhraseIndex === null) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`[data-phrase-index="${editingSummaryPhraseIndex}"]`)) {
        setEditingSummaryPhraseIndex(null);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [editingSummaryPhraseIndex]);

  // Click outside a Summary v2 entry closes its editor (same rule as phrases).
  useEffect(() => {
    if (!editingSummaryEntry) return;
    const sel = `[data-summary-entry="${editingSummaryEntry.key}-${editingSummaryEntry.index}"]`;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(sel)) setEditingSummaryEntry(null);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [editingSummaryEntry]);

  // Legacy per-user defaults — the fallback layer under this resume's own
  // section_settings (matches the backend's per-job > resume > user pref >
  // global default resolution).
  async function loadSectionGenTypes() {
    try {
      const prefs = await api.getPreferences();
      const genTypes: Partial<Record<SubcomponentType, GenerationType>> = {};
      const sections: SubcomponentType[] = ['contact', 'summary', 'skills', 'highlights', 'experience', 'education', 'awards', 'presentations', 'publications', 'portfolios', 'coverletter'];
      for (const s of sections) {
        const key = `default_gen_${s}` as keyof UserPreferences;
        if (prefs[key]) genTypes[s] = prefs[key] as GenerationType;
      }
      let names: Partial<Record<SubcomponentType, string>> = {};
      try {
        const parsed = prefs.section_names ? JSON.parse(prefs.section_names) : {};
        if (parsed && typeof parsed === 'object') names = parsed as Partial<Record<SubcomponentType, string>>;
      } catch { /* malformed legacy pref — fall through to defaults */ }
      let order: SubcomponentType[] | null = null;
      try {
        const parsed = prefs.section_order ? JSON.parse(prefs.section_order) as SubcomponentType[] : [];
        if (Array.isArray(parsed) && parsed.length > 0) order = parsed;
      } catch { /* malformed legacy pref — fall through to defaults */ }
      prefSectionDefaultsRef.current = { genTypes, names, order };
      setWritingFingerprint(prefs.writing_fingerprint ?? null);
      setUserStatus(prefs.status ?? null);
    } catch {
      prefSectionDefaultsRef.current = { genTypes: {}, names: {}, order: null };
    }
    deriveSectionSettings();
  }

  // Recompute the section-settings draft from whichever sources have landed:
  // this resume's section_settings first, then the legacy user prefs, then the
  // built-in defaults. Skipped once the user has edited a section setting.
  function deriveSectionSettings() {
    if (sectionSettingsTouchedRef.current) return;
    const prefDefaults = prefSectionDefaultsRef.current;
    const fromResume = loadedSectionSettingsRef.current || {};
    setSectionGenTypes({
      ...DEFAULT_GEN_TYPES,
      ...(prefDefaults?.genTypes || {}),
      ...(fromResume.gen_types || {}),
    });
    // Legacy per-user custom names prefill the draft the same way gen types and
    // order do, so the editor shows what the backend actually renders today —
    // and the user's next Save migrates them onto this resume.
    setSectionNames({
      ...(prefDefaults?.names || {}),
      ...(fromResume.names || {}),
    });
    const rawOrder = (fromResume.order && fromResume.order.length > 0)
      ? fromResume.order
      : (prefDefaults?.order || null);
    setSectionOrder(normalizeSectionOrder(rawOrder));
  }

  // The section-settings draft is now user-owned — stop re-deriving it from the
  // loaded resume / prefs so an in-flight fetch can't clobber their edit.
  function touchSectionSettings() {
    sectionSettingsTouchedRef.current = true;
  }

  // Complete, self-contained section settings for the save payload: every
  // section's resolved generation type, only genuinely customized names, and
  // the full order.
  function buildSectionSettings(): SectionSettings {
    const names: Partial<Record<SubcomponentType, string>> = {};
    for (const key of Object.keys(sectionNames) as SubcomponentType[]) {
      const value = (sectionNames[key] || '').trim();
      if (value && value !== SECTION_DEFAULT_LABELS[key]) names[key] = value;
    }
    return {
      gen_types: { ...sectionGenTypes },
      names,
      order: [...sectionOrder],
    };
  }

  // Attach the current section-settings draft to a resumejson about to be saved.
  function withSectionSettings(rj: ResumeJSON): ResumeJSON {
    return { ...rj, section_settings: buildSectionSettings() };
  }

  function sectionLabel(component: SubcomponentType): string {
    return (sectionNames[component] || '').trim() || SECTION_DEFAULT_LABELS[component] || component;
  }

  function startEditSectionTitle(component: SubcomponentType) {
    cancelSectionTitleRef.current = false;
    setSectionTitleDraft(sectionLabel(component));
    setEditingSectionTitle(component);
  }

  function commitSectionTitle(component: SubcomponentType) {
    if (cancelSectionTitleRef.current) {
      cancelSectionTitleRef.current = false;
      return;
    }
    const value = sectionTitleDraft.trim();
    setSectionNames(prev => {
      const next = { ...prev };
      if (!value || value === SECTION_DEFAULT_LABELS[component]) delete next[component];
      else next[component] = value;
      return next;
    });
    touchSectionSettings();
    setEditingSectionTitle(null);
  }

  function cancelSectionTitle() {
    cancelSectionTitleRef.current = true;
    setEditingSectionTitle(null);
  }

  function moveSection(component: SubcomponentType, direction: 'up' | 'down') {
    const reorderable = [...orderedSections];
    const idx = reorderable.indexOf(component);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || swapIdx < 0 || swapIdx >= reorderable.length) return;
    [reorderable[idx], reorderable[swapIdx]] = [reorderable[swapIdx], reorderable[idx]];
    setSectionOrder(['contact', ...reorderable]);
    touchSectionSettings();
  }

  // Reload just the writing fingerprint (after analyze / delete).
  async function reloadWritingFingerprint() {
    try {
      const prefs = await api.getPreferences();
      setWritingFingerprint(prefs.writing_fingerprint ?? null);
    } catch { /* keep current */ }
  }

  async function handleDeleteFingerprint() {
    if (!window.confirm('Remove your voice fingerprint? AI-generated sections will revert to the default voice.')) return;
    setDeletingFingerprint(true);
    try {
      await api.deleteWritingFingerprint();
      await reloadWritingFingerprint();
    } catch { /* best-effort */ } finally {
      setDeletingFingerprint(false);
    }
  }

  function cycleSectionGenType(component: SubcomponentType) {
    const current = sectionGenTypes[component];
    const allowed = GENERATION_TYPE_RESTRICTIONS[component] || ['manual', 'embedding', 'generate', 'omit'];
    const idx = allowed.indexOf(current);
    const next = allowed[(idx + 1) % allowed.length];
    // Draft-only — persisted with the resume on Save, not as a user preference.
    setSectionGenTypes({ ...sectionGenTypes, [component]: next });
    touchSectionSettings();
  }

  function renderGenTypeButton(component: SubcomponentType) {
    const genType = sectionGenTypes[component];
    const config = GEN_TYPE_CONFIG[genType];
    return (
      <button
        type="button"
        onClick={() => cycleSectionGenType(component)}
        data-help-target={`mainapp.resumes.resumeN.${component}.gentype`}
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${config.className} transition-colors`}
        title={`${config.tooltip}\n\nClick to change. This applies to ALL jobs generated from THIS resume (saved with the resume).`}
      >
        {config.icon}
        {GENERATION_TYPE_LABELS[genType]}
      </button>
    );
  }

  // Section heading: editable display name + generation-type chip, plus
  // up/down arrows for the reorderable sections (contact is pinned first,
  // cover letter pinned last with a fixed title).
  function renderSectionHeader(
    component: SubcomponentType,
    marginClass: 'mb-1' | 'mb-2',
    options: { editableTitle?: boolean } = {},
  ) {
    const { editableTitle = true } = options;
    const idx = orderedSections.indexOf(component);
    const reorderable = idx >= 0;
    const defaultLabel = SECTION_DEFAULT_LABELS[component] || component;
    return (
      <div className={`flex items-center gap-2 ${marginClass}`}>
        {renderSectionToggle(component, sectionLabel(component))}
        {!editableTitle ? (
          <label className="block text-base font-bold text-gray-700">{sectionLabel(component)}</label>
        ) : editingSectionTitle === component ? (
          <input
            type="text"
            autoFocus
            value={sectionTitleDraft}
            onChange={(e) => setSectionTitleDraft(e.target.value)}
            onBlur={() => commitSectionTitle(component)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitSectionTitle(component); }
              else if (e.key === 'Escape') { e.preventDefault(); cancelSectionTitle(); }
            }}
            aria-label={`${defaultLabel} section title`}
            maxLength={MAX_SECTION_NAME_LEN}
            className="text-base font-bold text-gray-700 px-1.5 py-0.5 border border-gray-300 rounded"
            autoComplete="off"
          />
        ) : (
          <button
            type="button"
            onClick={() => startEditSectionTitle(component)}
            data-help-target={`mainapp.resumes.resumeN.${component}.title`}
            className="group flex items-center gap-1 text-base font-bold text-gray-700 hover:text-primary-600"
            title={`Click to rename this section — the name is used on resumes generated from THIS resume. Default: ${defaultLabel}`}
          >
            {sectionLabel(component)}
            <Edit2 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        )}
        {renderGenTypeButton(component)}
        {reorderable && (
          <div className="flex items-center" data-help-target={`mainapp.resumes.resumeN.${component}.reorder`}>
            <button
              type="button"
              onClick={() => moveSection(component, 'up')}
              disabled={idx === 0}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
              aria-label={`Move ${defaultLabel} up`}
              title="Move this section up (on the editor and on generated resumes)"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => moveSection(component, 'down')}
              disabled={idx === orderedSections.length - 1}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
              aria-label={`Move ${defaultLabel} down`}
              title="Move this section down (on the editor and on generated resumes)"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    );
  }


  // Poll the record until the async tag pipeline finishes (resume_tags ===
  // 'generated', or item-tag count grows past the baseline), then bring the
  // generated tags into the editor. Started after a save that will (re)tag
  // items, and on open when a previous save's generation is still in flight.
  // `baselineJson` is the stringified resume state at poll start: if the user
  // hasn't edited since, the fresh record replaces the state wholesale
  // (captures synonym expansion + skill groups too); if they have, only the
  // generated tags are merged in so their edits survive.
  function startTagGenPolling(resumeName: string, baselineTagCount: number, baselineJson?: string) {
    // Cancel any prior poll loop.
    if (tagPollRef.current) tagPollRef.current.cancelled = true;
    const ctrl = { cancelled: false };
    tagPollRef.current = ctrl;
    const startedAt = Date.now();
    const MAX_WAIT_MS = 120000; // matches the gen_tags Lambda timeout
    const INTERVAL_MS = 4000;

    const tick = async () => {
      if (ctrl.cancelled) return;
      try {
        const data = await api.getResume(resumeName);
        if (ctrl.cancelled) return;
        const done = data.resume_tags === 'generated'
          || countItemTags(data.resumejson) > baselineTagCount;
        if (done) {
          setResume((prev) => {
            if (!baselineJson || JSON.stringify(prev) === baselineJson) {
              return normalizeResumeJson(data.resumejson);
            }
            return mergeGeneratedTags(prev, data.resumejson);
          });
          setName(resumeName);
          setPublishMasterResume(!!data.publish_master_resume);
          applyDedicatedMasters(
            mergeDedicatedMasters(dedicatedMastersRef.current, data.dedicated_masters || []),
          );
          setTagGenPhase('done');
          return;
        }
      } catch {
        // transient — keep polling until the timeout
      }
      if (ctrl.cancelled) return;
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        setTagGenPhase('timeout');
        return;
      }
      setTimeout(tick, INTERVAL_MS);
    };
    setTimeout(tick, INTERVAL_MS);
  }

  // Single writer for the dedicated-master list so the poll loop can read the
  // current value without going through React state.
  function applyDedicatedMasters(next: DedicatedMaster[]) {
    dedicatedMastersRef.current = next;
    setDedicatedMasters(next);
  }

  // Poll the resume record while any dedicated master is searching/generating,
  // exactly like the tag-generation loop above: an interval controlled by a
  // cancellable ref, a hard cap, and a merge that never drops rows.
  function startDedicatedMasterPolling(resumeName: string) {
    if (dmPollRef.current) dmPollRef.current.cancelled = true;
    const ctrl = { cancelled: false };
    dmPollRef.current = ctrl;
    const startedAt = Date.now();
    const MAX_WAIT_MS = 300000; // a full generation run, with headroom
    const INTERVAL_MS = 4000;

    const tick = async () => {
      if (ctrl.cancelled) return;
      try {
        const data = await api.getResume(resumeName);
        if (ctrl.cancelled) return;
        const merged = mergeDedicatedMasters(dedicatedMastersRef.current, data.dedicated_masters || []);
        applyDedicatedMasters(merged);
        if (!merged.some(dmIsActive)) return;
      } catch {
        // transient — keep polling until the cap
      }
      if (ctrl.cancelled) return;
      if (Date.now() - startedAt > MAX_WAIT_MS) return;
      setTimeout(tick, INTERVAL_MS);
    };
    setTimeout(tick, INTERVAL_MS);
  }

  function dedicatedMasterError(err: unknown, fallback: string): string {
    const e = err as { response?: { status?: number; data?: { message?: string } } };
    if (e?.response?.status === 403) {
      setDmAccessDenied(true);
      return 'Role-targeted resumes are available on Active accounts.';
    }
    return e?.response?.data?.message || fallback;
  }

  function handleDedicatedMasterCreated(dm: DedicatedMaster) {
    setShowDedicatedMasterModal(false);
    setDmError(null);
    applyDedicatedMasters([...dedicatedMastersRef.current.filter(d => d.id !== dm.id), dm]);
    if (resumename) startDedicatedMasterPolling(resumename);
  }

  // Commit an edited "Targeted at:" line. An empty value reverts (the search
  // Lambda requires keywords), as does an unchanged one.
  async function handleDedicatedMasterKeywords(dm: DedicatedMaster, raw: string) {
    if (editingDmKeywordsRef.current !== dm.id) return;
    editingDmKeywordsRef.current = null;
    const keywords = raw.split(',').map(k => k.trim()).filter(Boolean).join(', ');
    setEditingDmKeywordsId(null);
    if (!resumename || !keywords || keywords === (dm.keywords || '').trim()) return;
    setDmBusyId(dm.id);
    setDmError(null);
    try {
      const updated = await api.updateDedicatedMasterKeywords(resumename, dm.id, keywords);
      applyDedicatedMasters(
        dedicatedMastersRef.current.map(d => (d.id === dm.id ? updated : d)),
      );
      setDmKeywordsEdited(prev => new Set(prev).add(dm.id));
    } catch (err) {
      setDmError(dedicatedMasterError(err, `Could not update the targeting for /${dm.path}. Please try again.`));
    } finally {
      setDmBusyId(null);
    }
  }

  async function handleRegenerateDedicatedMaster(dm: DedicatedMaster) {
    if (!resumename) return;
    setDmBusyId(dm.id);
    setDmError(null);
    setDmKeywordsEdited(prev => {
      if (!prev.has(dm.id)) return prev;
      const next = new Set(prev);
      next.delete(dm.id);
      return next;
    });
    // The preview panel caches this resume's FROZEN generated content; a
    // regenerate is the one thing that moves it, so drop it here and let the
    // next preview selection refetch.
    clearCachedDmContent(resumename, dm.path);
    try {
      const updated = await api.regenerateDedicatedMaster(resumename, dm.id);
      applyDedicatedMasters(
        dedicatedMastersRef.current.map(d => (d.id === dm.id ? updated : d)),
      );
      startDedicatedMasterPolling(resumename);
    } catch (err) {
      setDmError(dedicatedMasterError(err, `Could not regenerate /${dm.path}. Please try again.`));
    } finally {
      setDmBusyId(null);
    }
  }

  async function handleRemoveDedicatedMaster(dm: DedicatedMaster) {
    if (!resumename) return;
    const ok = window.confirm(
      `Remove the master resume published at /${dm.path}? The page and its PDF are deleted — this can't be undone.`,
    );
    if (!ok) return;
    setDmBusyId(dm.id);
    setDmError(null);
    try {
      const remaining = await api.deleteDedicatedMaster(resumename, dm.id);
      applyDedicatedMasters(remaining);
    } catch (err) {
      setDmError(dedicatedMasterError(err, `Could not remove /${dm.path}. Please try again.`));
    } finally {
      setDmBusyId(null);
    }
  }

  // Pick a resume name not already in use, based on `base` (the imported
  // candidate name). Lets repeated imports create "Name", "Name (2)", ...
  // instead of failing the create with "already exists".
  async function deriveUniqueResumeName(base: string): Promise<string> {
    let existing: string[] = [];
    try {
      const data = await api.listResumes();
      existing = (data.resumes || []).map((r: any) => (r.resumename || '').toLowerCase());
    } catch {
      return base; // if the list fails, fall back to the base name
    }
    if (!existing.includes(base.toLowerCase())) return base;
    for (let i = 2; i < 100; i++) {
      const candidate = `${base} (${i})`;
      if (!existing.includes(candidate.toLowerCase())) return candidate;
    }
    return `${base} (${Date.now()})`;
  }

  async function loadResume() {
    if (!resumename) return;
    // Freshly loaded data becomes the new "saved" baseline for the bottom bar.
    baselineArmedRef.current = false;
    setLoading(true);
    try {
      const data = await api.getResume(resumename);
      setName(resumename);

      // Validate response structure
      if (!data || !data.resumejson) {
        console.error('Invalid resume data:', data);
        setError('Resume data is missing or corrupted. Please try again.');
        return;
      }

      const loadedResume = data.resumejson;

      normalizeResumeJson(loadedResume);
      setResume(loadedResume);

      // Per-resume section settings seed the draft (falling back to the legacy
      // user prefs for anything this resume doesn't define).
      loadedSectionSettingsRef.current = loadedResume.section_settings || {};
      deriveSectionSettings();

      // A previous save's tag generation may still be in flight (the save
      // Lambda sets resume_tags='generating'; gen_tags flips it to
      // 'generated'). Surface it and poll so a user who saved-and-closed sees
      // their tags land instead of silent blank tag boxes on reopen.
      if (data.resume_tags === 'generating' && needsTagGeneration(loadedResume)) {
        setTagGenPhase('generating');
        startTagGenPolling(resumename, countItemTags(loadedResume), JSON.stringify(loadedResume));
      }

      // Is THIS resume the one published at the user's root URL?
      setPublishMasterResume(!!data.publish_master_resume);

      // Role-targeted masters ride along on the same record. A generation
      // started before the editor was closed keeps being polled on reopen.
      const loadedMasters = data.dedicated_masters || [];
      applyDedicatedMasters(loadedMasters);
      if (loadedMasters.some(dmIsActive)) {
        startDedicatedMasterPolling(resumename);
      }

      // The user's OTHER resumes: which one (if any) currently owns the root
      // URL, and which role-targeted paths they already target.
      try {
        const listData = await api.listResumes();
        const others = (listData.resumes || [])
          .filter((r: any) => r.resumename !== resumename);
        const owner = others.find((r: any) => r.publish_master_resume);
        setPublishedElsewhere(owner ? owner.resumename : '');
        setOtherDmPaths(others.flatMap((r: any) => r.dedicated_master_paths || []));
      } catch { /* non-critical */ }
    } catch (err: any) {
      console.error('Failed to load resume:', err);
      if (err.response?.status === 404) {
        setError(`Resume "${resumename}" not found. It may have been deleted.`);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(`Failed to load resume: ${err.message}`);
      } else {
        setError('Failed to load resume. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  /**
   * Apply the record a save just returned, in place. The PUT response IS the
   * canonical record (`ReturnValues='ALL_NEW'` — server-side migration and
   * placeholder backfill already applied), so a keep-open save has nothing left
   * to GET. Deliberately does NOT touch `loading`: flipping it swaps the whole
   * lightbox body for a spinner and re-renders every section on every save.
   */
  async function applySavedRecord(record: Resume | undefined) {
    // Defensive: an unexpected response shape must never leave the editor
    // showing stale data — fall back to the full reload.
    if (!record || !record.resumejson) {
      await loadResume();
      return;
    }

    const savedResume = record.resumejson;
    normalizeResumeJson(savedResume);
    setResume(savedResume);

    loadedSectionSettingsRef.current = savedResume.section_settings || {};
    deriveSectionSettings();

    setPublishMasterResume(!!record.publish_master_resume);

    // Mirror loadResume: a role-targeted master still generating keeps polling.
    const masters = record.dedicated_masters || [];
    applyDedicatedMasters(masters);
    if (masters.some(dmIsActive) && resumename) {
      startDedicatedMasterPolling(resumename);
    }
  }

  async function handleSave(closeOnSuccess: boolean = true, resumeOverride?: ResumeJSON, nameOverride?: string, opts?: { skipDateCheck?: boolean; publishNow?: boolean }) {
    // resumeOverride lets callers (e.g. auto-save right after PDF import) save
    // freshly-loaded data without waiting for the setResume state commit.
    // nameOverride lets the import auto-save name a brand-new resume (whose
    // name field is still empty) without waiting for the setName commit.
    // Section settings (generation types, custom names, order) are part of the
    // resume draft and persist only here, on Save.
    const resumeToSave = withSectionSettings(resumeOverride ?? resume);
    const savedName = (nameOverride ?? name).trim();
    if (!savedName) {
      setError('Resume name is required');
      return;
    }
    if (!resumeToSave.name) {
      setError('Your full professional name is required');
      return;
    }
    if (nameOverride && nameOverride.trim() && nameOverride.trim() !== name) {
      setName(nameOverride.trim());
    }

    // Block on synonym/skill conflicts (task 8): two skills that map to the same
    // synonym set would both swap to the winner on a tailored resume and show as
    // duplicates. Flag both sides red and explain the fix; don't save.
    const conflicts = computeSynonymConflicts(resumeToSave);
    if (conflicts.setIds.size > 0) {
      setConflictSkillIdxs(conflicts.skillIdxs);
      setConflictSetIds(conflicts.setIds);
      const firstSet = (resumeToSave.synonyms || []).find(s => conflicts.setIds.has(s.id));
      const clashSkills = resumeToSave.skills
        .filter((_, i) => conflicts.skillIdxs.has(i))
        .map(s => s.title);
      const a = clashSkills[0] || (firstSet?.terms?.[0] ?? 'one skill');
      const b = clashSkills[1] || (firstSet?.terms?.[1] ?? 'another skill');
      const win = firstSet?.terms?.[0] ?? b;
      setError(
        `You've set "${a}" and "${b}" as interchangeable in your synonyms, but ALSO ` +
        `defined them both in your skills. This could lead to duplication on a tailored ` +
        `resume — showing "${a}" next to "${a}" (replaced as a synonym of "${win}"). ` +
        `Easy solution: you can either remove the synonym, or better yet, remove one of ` +
        `the skills — we'll automatically show the synonym that best matches each job.`
      );
      return;
    }
    setConflictSkillIdxs(new Set());
    setConflictSetIds(new Set());

    // Block on unreadable dates (owner: "lint ASAP, flag before saved"). A date
    // we can't parse sorts as undated and silently sinks the entry in the
    // generated resume — and once it's saved, the box that caused it is no
    // longer on screen. Only a genuine parse failure or a real ambiguity blocks;
    // anything merely unusual (a season, a range, a bare year) saves fine, and a
    // current-marker never blocks.
    // `skipDateCheck` is the IMPORT auto-save: an imported resume must never be
    // blocked (product rule) — the server-side `unreadable_date` lint rule
    // surfaces the same problems as review findings instead.
    const dateProblems = opts?.skipDateCheck ? [] : collectDateProblems(resumeToSave);
    if (dateProblems.length > 0) {
      setBadDateKeys(new Set(dateProblems.map(p => p.key)));
      setError(dateProblemBanner(dateProblems));
      return;
    }
    setBadDateKeys(new Set());

    setSaving(true);
    setError(null);
    let savedRecord: Resume | undefined;
    try {
      if (resumename) {
        // Pass new name if it changed (for rename support)
        savedRecord = await api.updateResume(
          resumename, resumeToSave,
          savedName !== resumename ? savedName : undefined,
          // ONLY "Publish now" claims the root URL. A plain save sends no
          // flag, so the server re-publishes only if this resume already
          // owns it — saving another resume can't hijack the URL.
          opts?.publishNow ? true : undefined,
        );
      } else {
        // A brand-new resume takes the root URL only if nothing else holds it
        // (server-side rule) — a clone never steals the original's URL.
        await api.createResume(savedName, resumeToSave);
      }
      // Tell the parent what was saved. The parent refreshes its lists AND
      // sets its editing-resume state to savedName, so a brand-new resume
      // transitions to edit-mode (prop becomes defined) without the modal
      // closing — a subsequent Save then updates instead of creating a dup.
      onSuccess(savedName);
      if (opts?.publishNow) {
        // This resume now owns the root URL; every sibling just gave it up.
        setPublishMasterResume(true);
        setPublishedElsewhere('');
      }
      // Re-run the (free, ~10ms) server lint so the rail score reflects what was
      // just saved. Cheap enough to do on every save; never on keystrokes.
      scorecardLint.refresh();
      // Bottom bar: this is now the saved state (re-armed so a follow-up
      // reload re-captures the server's version of the record).
      setLastSavedAt(Date.now());
      setSavedClockTick(t => t + 1);
      if (closeOnSuccess) {
        // Save and Close bypasses handleClose(), so the implied-facts run is
        // fired here too — it must happen on EVERY way out of the editor, and
        // this path is the one where the freshly saved content matters most.
        fireImpliedFactsRun();
        onClose();
      } else if (needsTagGeneration(resumeToSave)) {
        // Keep-open save where the backend will (re)generate tags. Show the
        // status and poll until tags land, then load the tagged record — this
        // is what makes freshly-imported / under-tagged resumes show their
        // tags without a close/reopen. Works for create, rename, and edit.
        setWarning(null);
        setTagGenPhase('generating');
        startTagGenPolling(savedName, countItemTags(resumeToSave), JSON.stringify(resumeToSave));
      } else if (resumename && savedName === resumename) {
        // Keep-open save in edit mode with no rename and nothing to generate.
        // The save response IS the canonical record, so apply it in place
        // instead of re-fetching — loadResume() would flip `loading` and blank
        // the lightbox. (Create and rename cases reload via the prop-change
        // load effect.)
        await applySavedRecord(savedRecord);
      }
      if (!closeOnSuccess) {
        // Re-arm the "saved" baseline AFTER any in-place reload, so the footer
        // reports the state that is actually on screen. (When closing, the
        // isOpen effect resets the baseline instead.)
        baselineArmedRef.current = false;
        setBaselineNonce(n => n + 1);
      }
    } catch (err: any) {
      // Show specific error message if available
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to save resume');
      }
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  // Scorecard inline item edit: apply the new text to the in-memory resume and
  // persist immediately. Throws on API failure so the scorecard row can show an
  // inline error (handleSave swallows errors into the editor banner, which is
  // hidden behind the scorecard modal — so saved resumes call the API directly).
  async function handleScorecardItemSave(path: FindingPath, newText: string) {
    const updated = setTextAtPath(resume, path, newText);
    setResume(updated);
    if (resumename) {
      await api.updateResume(resumename, withSectionSettings(updated));
      onSuccess(resumename);
      // Keep the rail score in step with the row the user just fixed. (For a
      // correctness row the modal's own recheck also hands fresh findings back
      // through `apply`; whichever lands last, both describe this same text.)
      scorecardLint.refresh();
    } else {
      // Brand-new unsaved resume: route through handleSave so create-mode
      // validation (resume name required, etc.) applies.
      await handleSave(false, updated);
    }
  }

  /**
   * Scorecard "Fix all safe issues" (BR-2): apply ONE batched mutation to the
   * in-memory resume and persist it with ONE save. Never a save per item — each
   * save re-triggers the resume tag lifecycle, so N saves would stampede tag
   * generation. Throws on failure so the panel can say nothing was changed.
   */
  async function handleScorecardBatchFix(mutate: (r: ResumeJSON) => ResumeJSON) {
    // Read through the ref, so a change made moments earlier (a Scorecard skill
    // delete, say) is built ON, never overwritten.
    const updated = mutate(resumeRef.current);
    setResume(updated);
    if (resumename) {
      await api.updateResume(resumename, withSectionSettings(updated));
      onSuccess(resumename);
      scorecardLint.refresh();
    } else {
      // Brand-new unsaved resume: route through handleSave so create-mode
      // validation (resume name required, etc.) applies.
      await handleSave(false, updated);
    }
  }

  // Scorecard "Mark resolved" — persist dismissed finding ids into resume state
  // (in resumejson.scorecard_resolved). Saved with the resume on the next Save,
  // mirroring how recommendations persist — no per-click API call / tag churn.
  function handleScorecardResolvedChange(resolvedIds: string[]) {
    setResume((prev) => ({ ...prev, scorecard_resolved: resolvedIds }));
  }

  /**
   * "Publish now" — make THIS resume the one shown at the user's root URL.
   * Routed through handleSave so every save-time validation (name, synonym
   * conflicts, unreadable dates) still applies; the flag rides along on the
   * same PUT, and the server clears the marker on every sibling.
   */
  async function handlePublishNow() {
    if (!resumename || publishing || saving) return;
    setPublishing(true);
    try {
      await handleSave(false, undefined, undefined, { publishNow: true });
    } finally {
      setPublishing(false);
    }
  }

  // Paths already spoken for ON THIS RESUME, so the add-flow can reject a
  // real collision before the server does. Another RESUME may target the same
  // path — that's allowed (most recent generation wins) and only earns a note.
  const dedicatedMasterTakenPaths = useMemo(
    () => dedicatedMasters.map(d => d.path).filter(Boolean),
    [dedicatedMasters],
  );
  // status=='active' only (uniform gating rule). A null status means the
  // preferences fetch hasn't landed or failed — leave the control live and let
  // the server's 403 do the gating, same as AI Review.
  const dedicatedMasterAllowed = !dmAccessDenied && (userStatus === null || userStatus === 'active');

  // AI Review functions
  const allRecs = resume.recommendations || [];
  const hasReviewedBefore = allRecs.length > 0;
  const pendingRecs = allRecs.filter(r => r.status === 'pending');
  const hasRecommendations = pendingRecs.length > 0;
  // "All recommendations have been implemented" green banner: only once — after
  // a review where every recommendation is now resolved, and not yet acked.
  const showAllImplemented = hasReviewedBefore && !hasRecommendations && !recsImplementedAcked;

  // Open recommendations per NAV ROW — drives the rail's amber "needs
  // attention" dots and its "N need attention" summary line.
  const navAttentionCounts = useMemo(() => {
    const byKey: Record<string, number> = {};
    for (const rec of pendingRecs) {
      const root = rec.path.split('[')[0].split('.')[0];
      const key = root === 'name' ? 'basics' : root;
      byKey[key] = (byKey[key] || 0) + 1;
    }
    return byKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume.recommendations]);

  // Rail footer chip → the pending-recommendations banner in the editor pane.
  function scrollToRecommendations() {
    const root = scrollRef.current;
    const el = root?.querySelector<HTMLElement>('[data-review-anchor]');
    if (!root || !el) return;
    const top = root.scrollTop + (el.getBoundingClientRect().top - root.getBoundingClientRect().top) - 8;
    root.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  // Per-section recommendation counts for top summary
  function getRecSectionCounts(): { section: string; count: number }[] {
    const counts: Record<string, number> = {};
    for (const rec of pendingRecs) {
      const section = rec.path.split('[')[0].split('.')[0];
      counts[section] = (counts[section] || 0) + 1;
    }
    return Object.entries(counts).map(([section, count]) => ({ section, count }));
  }

  async function handleAIReview() {
    if (!resumename) {
      setError('Save the resume first before running AI Review');
      return;
    }
    // Gate AI features for restricted user statuses
    try {
      const prefs = await api.getPreferences();
      const restrictedStatuses = ['trial', 'hibernating', 'cancelled', 'expired'];
      if (prefs.status && restrictedStatuses.includes(prefs.status)) {
        setShowAiUpgradeModal(true);
        return;
      }
    } catch {
      // If we can't check, allow the request (backend will enforce)
    }
    setReviewing(true);
    setError(null);
    try {
      const result = await api.reviewResume(resumename);
      const newRecs = result.recommendations || [];
      // Merge: keep existing dismissed/accepted, add new pending ones
      const existing = resume.recommendations || [];
      const kept = existing.filter(r => r.status === 'dismissed' || r.status === 'accepted');
      setResume({ ...resume, recommendations: [...kept, ...newRecs] });
      setReviewComplete(true);
      setEditedReplacements({});
      // Fresh review re-arms the "all implemented" banner so it can show once
      // more after this round of recommendations is fully applied.
      setRecsImplementedAcked(false);
      try { if (recsAckKey) localStorage.removeItem(recsAckKey); } catch { /* ignore */ }
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to generate AI review. Please try again.');
      }
      console.error(err);
    } finally {
      setReviewing(false);
    }
  }

  /**
   * Shared pre-check for both Summary v2 AI CTAs: restricted statuses get the
   * upgrade modal instead of a raw 403 (the server enforces the gate either
   * way). True means "handled — don't call".
   */
  async function summaryAiBlocked(): Promise<boolean> {
    try {
      const prefs = await api.getPreferences();
      const restrictedStatuses = ['trial', 'hibernating', 'cancelled', 'expired'];
      if (prefs.status && restrictedStatuses.includes(prefs.status)) {
        setShowAiUpgradeModal(true);
        return true;
      }
    } catch {
      // If we can't check, allow the request (backend will enforce)
    }
    return false;
  }

  /**
   * PRE-FILL a Summary v2 proposal into editor state, with fresh client-side
   * `ss_` ids and empty tags. Nothing is written server-side — the bottom bar
   * flips to "Unsaved changes" like any other edit, the user revises, and Save
   * is what adopts it (the tag pipeline fills the tags on that save).
   */
  function applySummaryProposal(result: SummarySplitProposal) {
    const proposed = result.proposed || ({} as SummarySplitProposal['proposed']);
    const filled: SummarySections = {};
    for (const key of SUMMARY_SECTION_KEYS) {
      filled[key] = (proposed[key] || [])
        .map(entry => (entry?.text || '').trim())
        .filter(Boolean)
        .map(text => ({ id: genSummarySectionId(), text, tags: [] }));
    }
    setResume({ ...resume, summary_sections: { ...(resume.summary_sections || {}), ...filled } });
    setSummarySplitNeedsNumber(!!result.credibility_needs_number);
  }

  /** Both CTAs fail the same way: 403 → upgrade modal, else the editor banner. */
  function reportSummaryProposalError(err: any, fallback: string) {
    if (err.response?.status === 403) {
      setShowAiUpgradeModal(true);
    } else if (err.response?.data?.message) {
      setError(err.response.data.message);
    } else {
      setError(fallback);
    }
    console.error(err);
  }

  /**
   * Summary v2 assisted migration. One AI call decomposes this resume's legacy
   * summary content into proposed identity / credibility / relevance entries.
   */
  async function handleSplitSummary() {
    if (!resumename) {
      setError('Save the resume first before splitting the summary');
      return;
    }
    if (await summaryAiBlocked()) return;
    setSplittingSummary(true);
    setError(null);
    try {
      applySummaryProposal(await api.splitSummarySections(resumename));
    } catch (err: any) {
      reportSummaryProposalError(
        err, 'Failed to split the summary into sections. Please try again.');
    } finally {
      setSplittingSummary(false);
    }
  }

  /**
   * Summary v2 bootstrap. Same pre-fill contract as the split, but the server
   * drafts from the WHOLE SAVED resume — experience, skills, credentials — so a
   * resume with no summary to split still gets a starting bank. It reads the
   * saved record, so unsaved edits in this session are not part of the draft.
   */
  async function handleBootstrapFragments() {
    if (!resumename) {
      setError('Save the resume first before generating summary fragments');
      return;
    }
    if (await summaryAiBlocked()) return;
    setBootstrappingFragments(true);
    setError(null);
    try {
      applySummaryProposal(await api.bootstrapSummaryFragments(resumename));
    } catch (err: any) {
      reportSummaryProposalError(
        err, 'Failed to generate summary fragments. Please try again.');
    } finally {
      setBootstrappingFragments(false);
    }
  }

  function itemLabel(item: unknown): string {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
      const obj = item as Record<string, unknown>;
      const candidate = obj.title ?? obj.text ?? obj.name;
      if (typeof candidate === 'string') return candidate;
      return JSON.stringify(item);
    }
    return String(item);
  }

  function getDataDisplay(rec: Recommendation): string {
    const norm = normalizeRec(rec);
    const val = norm.data;
    if (val == null) return '';
    if (Array.isArray(val)) return val.map(itemLabel).join(', ');
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return itemLabel(val);
    return JSON.stringify(val);
  }

  function buildDataFromEdit(rec: Recommendation, editedText: string): unknown {
    const norm = normalizeRec(rec);
    const originalVal = norm.data;
    if (Array.isArray(originalVal)) {
      const labels = editedText.split(',').map(s => s.trim()).filter(Boolean);
      // If original items were objects, preserve their structure and update the label field.
      const first = originalVal.length > 0 ? originalVal[0] : null;
      if (first != null && typeof first === 'object') {
        const firstObj = first as Record<string, unknown>;
        const labelField = 'title' in firstObj ? 'title' : 'text' in firstObj ? 'text' : 'name';
        return labels.map((label, i) => {
          const orig = originalVal[i];
          if (orig != null && typeof orig === 'object') {
            return { ...(orig as Record<string, unknown>), [labelField]: label };
          }
          return { [labelField]: label, tags: [] };
        });
      }
      return labels;
    }
    return editedText;
  }

  function applyRecommendation(rec: Recommendation) {
    const norm = normalizeRec(rec);
    if (norm.action === 'advise') return;   // informational: nothing to apply
    const editedText = editedReplacements[rec.id];
    const data = editedText !== undefined ? buildDataFromEdit(rec, editedText) : norm.data;
    const updated = applyActionAtPath(resume, norm.path, norm.field, norm.action, data);
    const updatedRecs = (updated.recommendations || []).map(r =>
      r.id === rec.id ? { ...r, status: 'accepted' as const } : r
    );
    setResume({ ...updated, recommendations: updatedRecs });
    setEditedReplacements(prev => { const next = {...prev}; delete next[rec.id]; return next; });
  }

  function dismissRecommendation(recId: string) {
    const updatedRecs = (resume.recommendations || []).map(r =>
      r.id === recId ? { ...r, status: 'dismissed' as const } : r
    );
    setResume({ ...resume, recommendations: updatedRecs });
    setEditedReplacements(prev => { const next = {...prev}; delete next[recId]; return next; });
  }

  function renderInlineRecs(path: string) {
    const matching = pendingRecs.filter(r => r.path === path);
    if (matching.length === 0) return null;
    const actionColors: Record<string, string> = {
      replace: 'bg-blue-100 text-blue-700',
      append: 'bg-emerald-100 text-emerald-700',
      remove: 'bg-red-100 text-red-700',
    };
    return (
      <>
        {matching.map(rec => {
          const norm = normalizeRec(rec);
          // 'advise' = a note with no safe mechanical fix: no editable value,
          // no Apply — the user reads it and dismisses it.
          if (norm.action === 'advise') {
            return (
              <div key={rec.id} className="ml-6 mt-0.5 mb-1 relative">
                <div className="absolute -top-1 left-4 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-amber-200" />
                <div className="border border-amber-200 bg-amber-50 rounded pl-3 pr-2 py-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="inline-block px-1.5 py-0 text-[10px] font-medium rounded bg-amber-100 text-amber-800">
                          note
                        </span>
                        <p className="text-xs text-amber-800">{rec.desc}</p>
                      </div>
                      <div className="flex gap-2 mt-1">
                        <button
                          onClick={() => dismissRecommendation(rec.id)}
                          className="flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        >
                          <XCircle className="w-3 h-3" /> Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          const displayVal = editedReplacements[rec.id] ?? getDataDisplay(rec);
          const isMultiline = rec.field === 'text' || rec.field === 'summary' || displayVal.length > 80;
          const isRemove = norm.action === 'remove';
          return (
            <div key={rec.id} className="ml-6 mt-0.5 mb-1 relative">
              {/* Arrow pointing up to the element above */}
              <div className="absolute -top-1 left-4 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-purple-200" />
              <div className="border border-purple-200 bg-purple-50 rounded pl-3 pr-2 py-2">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-purple-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`inline-block px-1.5 py-0 text-[10px] font-medium rounded ${actionColors[norm.action] || actionColors.replace}`}>
                        {norm.action}
                      </span>
                      <p className="text-xs text-purple-700">{rec.desc}</p>
                    </div>
                    {isRemove ? (
                      <p className="text-xs text-red-600 line-through px-2 py-1">{displayVal}</p>
                    ) : isMultiline ? (
                      <textarea
                        value={displayVal}
                        onChange={e => setEditedReplacements(prev => ({...prev, [rec.id]: e.target.value}))}
                        rows={rec.field === 'summary' || rec.path === 'summary' ? 5 : 2}
                        className="w-full px-2 py-1 border border-purple-200 rounded text-xs bg-white resize-none"
                      />
                    ) : (
                      <input
                        type="text"
                        value={displayVal}
                        onChange={e => setEditedReplacements(prev => ({...prev, [rec.id]: e.target.value}))}
                        className="w-full px-2 py-1 border border-purple-200 rounded text-xs bg-white"
                      />
                    )}
                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={() => applyRecommendation(rec)}
                        className="flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        <Check className="w-3 h-3" /> Apply
                      </button>
                      <button
                        onClick={() => dismissRecommendation(rec.id)}
                        className="flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                      >
                        <XCircle className="w-3 h-3" /> Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </>
    );
  }

  function applyAllRecommendations() {
    let updated = { ...resume };
    const recs = updated.recommendations || [];
    for (const rec of recs) {
      if (rec.status === 'pending') {
        const norm = normalizeRec(rec);
        if (norm.action === 'advise') continue;   // notes are never applied
        updated = applyActionAtPath(updated, norm.path, norm.field, norm.action, norm.data);
      }
    }
    // 'advise' notes stay pending — Apply All must not silently accept them.
    updated.recommendations = recs.map(r =>
      r.status === 'pending' && normalizeRec(r).action !== 'advise'
        ? { ...r, status: 'accepted' as const } : r
    );
    setResume(updated);
  }

  function dismissAllRecommendations() {
    const updatedRecs = (resume.recommendations || []).map(r =>
      r.status === 'pending' ? { ...r, status: 'dismissed' as const } : r
    );
    setResume({ ...resume, recommendations: updatedRecs });
  }

  // Skills functions

  function updateSkill(index: number, value: string) {
    const skills = [...resume.skills];
    skills[index] = { ...skills[index], title: value };
    setResume({ ...resume, skills });
  }

  function updateSkillTags(index: number, tags: string[]) {
    const skills = [...resume.skills];
    skills[index] = { ...skills[index], tags };
    setResume({ ...resume, skills });
  }

  function removeSkill(index: number) {
    setResume({ ...resume, skills: resume.skills.filter((_, i) => i !== index) });
  }

  // ── Skill Groups (task 9) ──────────────────────────────────────────────
  // Groups are user-defined buckets; each skill carries an optional group_id.
  // Skills with no/unknown group_id render in the "Ungrouped" lane. The server
  // auto-buckets ungrouped skills on save; explicit placement here always wins.
  function genGroupId(): string {
    const hex = Array.from({ length: 8 }, () =>
      Math.floor(Math.random() * 16).toString(16)).join('');
    return `sg_${hex}`;
  }

  function addSkillGroup() {
    const groups = resume.skill_groups || [];
    const maxOrder = groups.reduce((m, g) => Math.max(m, g.order ?? 0), -1);
    const ng = { id: genGroupId(), title: '', tags: [], order: maxOrder + 1 };
    setResume({ ...resume, skill_groups: [...groups, ng] });
    setEditingGroupId(ng.id);
  }

  function updateSkillGroupTitle(id: string, title: string) {
    setResume({
      ...resume,
      skill_groups: (resume.skill_groups || []).map(g => g.id === id ? { ...g, title } : g),
    });
  }

  /**
   * Skills Display Controls — per-resume shape/caps for the rendered Skills
   * section (`resumejson.skills_settings`). Writes through the same
   * `setResume` path every other resumejson field uses, so the bottom bar's
   * `saveSnapshot` sees the change ("Unsaved changes") and Save persists it —
   * the backend passes resumejson through whole, so there is no API change.
   */
  function updateSkillsSettings(patch: Partial<SkillsSettings>) {
    setResume({
      ...resume,
      skills_settings: { ...(resume.skills_settings || {}), ...patch },
    });
  }

  function removeSkillGroup(id: string) {
    // Drop the group; its skills fall back to ungrouped (group_id cleared).
    setResume({
      ...resume,
      skill_groups: (resume.skill_groups || []).filter(g => g.id !== id),
      skills: resume.skills.map(s => s.group_id === id ? { ...s, group_id: null } : s),
    });
  }

  function addSkillToGroup(groupId: string) {
    const key = groupId || '';
    const val = (newSkillByGroup[key] || '').trim();
    if (!val) return;
    setResume({
      ...resume,
      skills: [...resume.skills, { title: val, tags: [], group_id: groupId || null }],
    });
    setNewSkillByGroup(prev => ({ ...prev, [key]: '' }));
  }

  function moveSkillToGroup(skillIdx: number, groupId: string | null) {
    if (skillIdx == null || skillIdx < 0 || skillIdx >= resume.skills.length) return;
    const skills = [...resume.skills];
    if ((skills[skillIdx].group_id ?? null) === (groupId ?? null)) return;
    skills[skillIdx] = { ...skills[skillIdx], group_id: groupId };
    setResume({ ...resume, skills });
  }

  // ── Synonym/skill conflict detection (task 8) ──────────────────────────
  // Mirrors shared/synonyms.py: a skill "belongs to" a set when its title
  // contains a member phrase (prefix-subset rule). When 2+ skills map to the
  // SAME set, a job-time synonym swap would rewrite both to the winner and
  // duplicate the skill — so we block the save and flag both sides.
  function synTokenize(text: string): string[] {
    return (text || '').toLowerCase().match(/[a-z0-9+#&.']+/g) || [];
  }
  function synSeqMatch(memberTokens: string[], textTokens: string[]): boolean {
    const m = memberTokens.length;
    if (m === 0) return false;
    for (let i = 0; i <= textTokens.length - m; i++) {
      if (memberTokens.every((mt, k) => textTokens[i + k].startsWith(mt))) return true;
    }
    return false;
  }
  // ── date validation (pre-save) ───────────────────────────────────────────
  // Dates are free text on purpose — we store exactly what the user typed and
  // it stays the display source everywhere. But a string nobody can read sorts
  // as undated and silently sinks a role to the bottom of the generated resume,
  // so the ONE place to catch it is before it's saved, while the box that
  // caused it is still on screen. Mirrors shared/dates.py; see
  // tests/date_parse_vectors.json for the shared grammar.

  /** Stable identity for one date input, used to paint it red. */
  function dateFieldKey(...parts: (string | number)[]): string {
    return parts.join(':');
  }

  /**
   * Class list for a date input — red ring when the last Save attempt couldn't
   * read it. Same visual language as the synonym/skill conflict chips.
   */
  function dateInputClass(key: string): string {
    const base = 'px-2 py-1 border rounded text-sm';
    return badDateKeys.has(key)
      ? `${base} border-red-400 ring-2 ring-red-400 bg-red-50`
      : `${base} border-gray-300`;
  }

  interface DateProblem { key: string; label: string; raw: string; reason: string }

  function collectDateProblems(rj: ResumeJSON): DateProblem[] {
    const out: DateProblem[] = [];
    const check = (raw: string | undefined, key: string, label: string) => {
      const res = parseDateString(raw);
      // A current-marker ("Present", "ongoing") is never a problem — it becomes
      // the Current checkbox, not a date.
      if (!res.uncertain) return;
      out.push({ key, label, raw: String(raw ?? '').trim(), reason: res.reason || '' });
    };

    (rj.experience || []).forEach((co, ci) => {
      const cname = (co.company || '').trim() || 'this company';
      check(co.startDate, dateFieldKey('exp', ci, 'startDate'), `${cname} start date`);
      if (!co.current) {
        check(co.endDate, dateFieldKey('exp', ci, 'endDate'), `${cname} end date`);
      }
      (co.roles || []).forEach((role, ri) => {
        const rname = (role.title || '').trim() || 'this role';
        check(role.startDate, dateFieldKey('exp', ci, 'role', ri, 'startDate'),
          `${rname} (${cname}) start date`);
        if (!role.current) {
          check(role.endDate, dateFieldKey('exp', ci, 'role', ri, 'endDate'),
            `${rname} (${cname}) end date`);
        }
      });
    });

    const yearSections: [string, any[]][] = [
      ['education', rj.education || []],
      ['awards', rj.awards || []],
      ['presentations', rj.presentations || []],
      ['publications', rj.publications || []],
      ['portfolios', rj.portfolios || []],
    ];
    yearSections.forEach(([section, items]) => {
      items.forEach((item: any, i: number) => {
        const title = (item?.title || '').trim() || 'this entry';
        check(item?.year, dateFieldKey(section, i, 'year'), `${title} year`);
      });
    });
    return out;
  }

  function dateProblemBanner(problems: DateProblem[]): string {
    const lines = problems.map(p => `\u2022 ${p.label}: "${p.raw}"`);
    const head = problems.length === 1
      ? "One date can't be read, so it wouldn't sort correctly on your resume:"
      : `${problems.length} dates can't be read, so they wouldn't sort correctly on your resume:`;
    return `${head}\n${lines.join('\n')}\nFix them and save again — ${FORMAT_HINT}. `
      + 'For a job you still hold, tick "Current" instead of typing an end date.';
  }

  /**
   * "Present"/"ongoing"/"still here" typed into an End-date box means the
   * Current flag, not a date — clear the text and tick the box. Runs on blur so
   * it never fights the user mid-keystroke; the server does the same on save
   * for anything that bypasses this editor (imports, API callers).
   */
  function absorbCurrentMarker(
    raw: string | undefined,
    apply: (patch: { current: boolean; endDate: string }) => void,
  ): void {
    if (isCurrentMarker(raw)) apply({ current: true, endDate: '' });
  }

  function computeSynonymConflicts(rj: ResumeJSON): { skillIdxs: Set<number>; setIds: Set<string> } {
    const skillIdxs = new Set<number>();
    const setIds = new Set<string>();
    const sets = (rj.synonyms || []).filter(s => (s.terms || []).filter(t => t.trim()).length >= 2);
    const skillTokenLists = rj.skills.map(s => synTokenize(s.title || ''));
    for (const set of sets) {
      const memberTokenLists = (set.terms || []).map(t => synTokenize(t)).filter(t => t.length);
      const matching: number[] = [];
      for (let i = 0; i < skillTokenLists.length; i++) {
        if (memberTokenLists.some(mt => synSeqMatch(mt, skillTokenLists[i]))) matching.push(i);
      }
      if (matching.length >= 2) {
        matching.forEach(i => skillIdxs.add(i));
        setIds.add(set.id);
      }
    }
    return { skillIdxs, setIds };
  }

  // Per-item tag status chip, shown OUTSIDE the tags box so it's visible even
  // when the tag set already has (too few) entries. Rendered whenever an
  // item's tags are below the per-item minimum the backend targets — exactly
  // the items the next save (or the run currently in flight) will (re)tag.
  // Items at/above the minimum keep their tags on save, so no chip = no
  // regeneration.
  function renderTagStatusChip(tags: string[] | undefined, text: string | undefined, min: number = TAG_MIN_ITEM) {
    if (!tagChipNeeded(tags, text, min)) return null;
    const generating = tagGenPhase === 'generating';
    return (
      <span
        data-testid="tag-status-chip"
        className={`inline-flex items-center gap-1 mt-0.5 px-1.5 py-px rounded-full text-[10px] font-medium whitespace-nowrap border ${
          generating
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : 'bg-gray-50 text-gray-500 border-gray-200'
        }`}
        title={generating
          ? 'Tags for this item are being generated now — they\'ll appear here automatically.'
          : `This item has fewer than ${min} tags, so tags will be (re)generated the next time you save. Items with ${min}+ tags keep their tags — clear the tags box to force a refresh.`}
      >
        {generating ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Clock className="w-2.5 h-2.5" />}
        {generating ? 'generating tags…' : 'tags generate on Save'}
      </span>
    );
  }

  // One draggable skill chip (used by every group lane). `i` is the skill's
  // index in the flat resume.skills array — all mutators key off it.
  function renderSkillCard(skill: any, i: number) {
    const conflict = conflictSkillIdxs.has(i);
    return (
      <div
        key={i}
        data-skill-index={i}
        draggable={editingSkillIndex !== i}
        onDragStart={(e) => {
          setDraggedSkillIdx(i);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', String(i));
        }}
        onDragEnd={() => { setDraggedSkillIdx(null); setDragOverGroup(null); }}
        className={`flex items-start gap-1 px-2 py-1.5 rounded-lg text-sm min-w-[140px] max-w-[220px] ${editingSkillIndex === i ? '' : 'cursor-grab active:cursor-grabbing'} ${conflict ? 'bg-red-50 ring-2 ring-red-400' : 'bg-gray-100'}${skill.include === '0' ? ' opacity-40' : ''}`}
        title={conflict ? 'This skill clashes with a synonym set — see the warning above.' : undefined}
      >
        <input
          type="checkbox"
          checked={skill.include !== '0'}
          onChange={(e) => {
            const skills = [...resume.skills];
            skills[i] = { ...skills[i], include: e.target.checked ? '1' : '0' };
            setResume({ ...resume, skills });
          }}
          data-testid={`include-skill-${i}`}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5 shrink-0 w-3 h-3"
          title={skill.include === '0' ? 'Excluded from generation' : 'Included in generation'}
        />
        <div className="flex-1 min-w-0">
          {editingSkillIndex === i ? (
            <>
              <input type="text" value={skill.title} onChange={(e) => updateSkill(i, e.target.value)} onKeyDown={(e) => e.key === 'Enter' && setEditingSkillIndex(null)} className="w-full px-1 py-0 border border-gray-300 rounded text-sm mb-1" autoFocus autoComplete="off" />
              <input
                key={(skill.tags || []).join(',')}
                type="text"
                defaultValue={(skill.tags || []).join(', ')}
                onBlur={(e) => updateSkillTags(i, e.target.value.split(',').map((t: string) => t.trim()).filter(Boolean))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    updateSkillTags(i, e.currentTarget.value.split(',').map((t: string) => t.trim()).filter(Boolean));
                    setEditingSkillIndex(null);
                  }
                }}
                className="w-full px-1 py-0 border border-gray-100 rounded text-xs text-gray-500"
                placeholder="tags (comma-separated)"
              />
              {renderTagStatusChip(skill.tags, skill.title)}
            </>
          ) : (
            <div onClick={() => setEditingSkillIndex(i)} className="cursor-pointer">
              <div className="truncate leading-tight">{skill.title}</div>
              {(skill.tags || []).length > 0 && (
                <div className="text-[10px] text-gray-400 truncate leading-tight mt-0.5">{skill.tags.join(', ')}</div>
              )}
              {renderTagStatusChip(skill.tags, skill.title)}
            </div>
          )}
        </div>
        <button onClick={() => { if (editingSkillIndex === i) setEditingSkillIndex(null); removeSkill(i); }} className="text-gray-400 hover:text-red-500 shrink-0 mt-0.5">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // A per-group "add skill" input (compact). key '' = ungrouped lane.
  function renderGroupAddInput(groupId: string) {
    const key = groupId || '';
    return (
      <div className="flex gap-1 mt-1.5">
        <input
          type="text"
          value={newSkillByGroup[key] || ''}
          onChange={(e) => setNewSkillByGroup(prev => ({ ...prev, [key]: e.target.value }))}
          onKeyDown={(e) => e.key === 'Enter' && addSkillToGroup(groupId)}
          className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
          placeholder="+ add skill…"
          autoComplete="off"
        />
        <button onClick={() => addSkillToGroup(groupId)} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200" title="Add skill to this group">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // Summary Phrase functions
  function addSummaryPhrase() {
    if (newSummaryPhrase.trim()) {
      setResume({ ...resume, summary_phrases: [...(resume.summary_phrases || []), { text: newSummaryPhrase.trim(), tags: [] }] });
      setNewSummaryPhrase('');
    }
  }

  function updateSummaryPhraseText(index: number, value: string) {
    const phrases = [...(resume.summary_phrases || [])];
    phrases[index] = { ...phrases[index], text: value };
    setResume({ ...resume, summary_phrases: phrases });
  }

  function updateSummaryPhraseTags(index: number, tags: string[]) {
    const phrases = [...(resume.summary_phrases || [])];
    phrases[index] = { ...phrases[index], tags };
    setResume({ ...resume, summary_phrases: phrases });
  }

  function removeSummaryPhrase(index: number) {
    setResume({ ...resume, summary_phrases: (resume.summary_phrases || []).filter((_, i) => i !== index) });
  }

  // ── Summary v2 subsections (features/summary_v2.md) ────────────────────
  // identity / credibility / relevance each hold 0–N tagged entries. The
  // `summary_sections` key is NEVER fabricated on load — it appears the moment
  // the user adds their first entry, and (once present) stays with empty lists
  // if they later delete everything.
  const SUMMARY_SECTION_META: { key: SummarySectionKey; label: string; blurb: string; placeholder: string }[] = [
    {
      key: 'identity',
      label: 'Summary: Identity',
      blurb: 'Who you are professionally, in one clause — role archetype, seniority, domain. The recruiter should be able to categorize you in ~2 seconds: which bucket, which level.',
      placeholder: 'Add an identity statement…',
    },
    {
      key: 'credibility',
      label: 'Summary: Credibility',
      blurb: 'One or two concrete signals the positioning is real, not aspirational — a quantified outcome, a scale marker, a recognizable credential. This is what separates a summary from a generic objective statement.',
      placeholder: 'Add a credibility signal (include a number)…',
    },
    {
      key: 'relevance',
      label: 'Summary: Relevance',
      blurb: 'What you bring to THIS role — the piece that flexes per job. The intersection of your strengths and the employer’s stated needs, the part generic summaries always omit.',
      placeholder: 'Add a relevance statement…',
    },
  ];

  function genSummarySectionId(): string {
    const hex = Array.from({ length: 8 }, () =>
      Math.floor(Math.random() * 16).toString(16)).join('');
    return `ss_${hex}`;
  }

  function summaryEntries(key: SummarySectionKey): SummarySectionEntry[] {
    return resume.summary_sections?.[key] || [];
  }

  /** True once ANY subsection carries an entry — the legacy-phrase cutover. */
  function hasSummarySectionEntries(rj: ResumeJSON): boolean {
    return SUMMARY_SECTION_KEYS.some(k => (rj.summary_sections?.[k] || []).length > 0);
  }

  /** Legacy summary content a split could decompose (flat summary OR phrases). */
  function hasLegacySummaryContent(rj: ResumeJSON): boolean {
    return (rj.summary_phrases || []).length > 0 || !!(rj.summary || '').trim();
  }

  function setSummaryEntries(key: SummarySectionKey, entries: SummarySectionEntry[]) {
    setResume({ ...resume, summary_sections: { ...(resume.summary_sections || {}), [key]: entries } });
  }

  function addSummaryEntry(key: SummarySectionKey) {
    const text = (newSummaryEntry[key] || '').trim();
    if (!text) return;
    setSummaryEntries(key, [...summaryEntries(key), { id: genSummarySectionId(), text, tags: [] }]);
    setNewSummaryEntry(prev => ({ ...prev, [key]: '' }));
  }

  function updateSummaryEntry(key: SummarySectionKey, index: number, patch: Partial<SummarySectionEntry>) {
    const entries = [...summaryEntries(key)];
    if (!entries[index]) return;
    entries[index] = { ...entries[index], ...patch };
    setSummaryEntries(key, entries);
  }

  function removeSummaryEntry(key: SummarySectionKey, index: number) {
    setSummaryEntries(key, summaryEntries(key).filter((_, i) => i !== index));
  }

  // Contact functions
  function addContactItem() {
    if (newContactTitle.trim()) {
      const newItem: ContactItem = {
        icon: newContactIcon,
        title: newContactTitle.trim(),
        url: newContactUrl.trim(),
      };
      setResume({
        ...resume,
        contact: [...resume.contact, newItem]
      });
      setNewContactTitle('');
      setNewContactUrl('');
      setNewContactAutoFill(false);
    }
  }

  function updateContactItem(index: number, updates: Partial<ContactItem>) {
    const items = [...resume.contact];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, contact: items });
  }

  function removeContactItem(index: number) {
    setResume({
      ...resume,
      contact: resume.contact.filter((_, i) => i !== index)
    });
  }

  function moveContactItem(index: number, direction: 'up' | 'down') {
    const items = [...resume.contact];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, contact: items });
  }

  // Synonym set functions — terms are entered as one line separated by
  // "//" (or commas); each set gets a stable PK so backend processes can
  // reference it deterministically.
  function parseSynonymTerms(raw: string): string[] {
    const seen = new Set<string>();
    return raw
      .split(/\/\/|,/)
      .map(t => t.trim())
      .filter(t => {
        if (!t) return false;
        const key = t.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  function addSynonymSet() {
    const terms = parseSynonymTerms(newSynonymTerms);
    if (terms.length < 2) return;
    const newSet: SynonymSet = {
      id: `syn_${Math.random().toString(36).slice(2, 10)}`,
      terms,
    };
    setResume({ ...resume, synonyms: [...(resume.synonyms || []), newSet] });
    setNewSynonymTerms('');
  }

  function commitSynonymDraft(id: string) {
    const draft = synonymDrafts[id];
    if (draft === undefined) return;
    const terms = parseSynonymTerms(draft);
    setResume({
      ...resume,
      synonyms: (resume.synonyms || []).map(s => s.id === id ? { ...s, terms } : s),
    });
    setSynonymDrafts(prev => { const next = { ...prev }; delete next[id]; return next; });
  }

  function removeSynonymSet(id: string) {
    setResume({
      ...resume,
      synonyms: (resume.synonyms || []).filter(s => s.id !== id),
    });
    setSynonymDrafts(prev => { const next = { ...prev }; delete next[id]; return next; });
  }

  // ---- Implied Facts ------------------------------------------------------
  // Facts live in resumejson, so every mutation here persists with the normal
  // Save — there is no separate endpoint to call. Confirming is the only thing
  // that makes a fact ground truth for generation.
  function addImpliedFact() {
    const next = addUserFact(resume.implied_facts, newImpliedFact);
    if (next === factsOf(resume.implied_facts)) return;   // blank or duplicate
    setResume({ ...resume, implied_facts: next });
    setNewImpliedFact('');
  }

  function commitFactDraft(id: string) {
    const draft = factDrafts[id];
    setFactDrafts(prev => { const next = { ...prev }; delete next[id]; return next; });
    if (draft === undefined) return;
    setResume(prev => ({ ...prev, implied_facts: updateFactText(prev.implied_facts, id, draft) }));
  }

  function confirmImpliedFact(id: string) {
    setResume(prev => ({ ...prev, implied_facts: confirmFact(prev.implied_facts, id) }));
  }

  function confirmAllImpliedFacts() {
    setResume(prev => ({ ...prev, implied_facts: confirmAllFacts(prev.implied_facts) }));
  }

  // Trashing an AI fact DISMISSES it (hidden tombstone) so the next extraction
  // run can't re-derive and re-ask the same claim; a fact the user typed is
  // genuinely deleted. See `dismissFact`.
  function removeImpliedFact(id: string) {
    setResume(prev => ({ ...prev, implied_facts: dismissFact(prev.implied_facts, id) }));
    setFactDrafts(prev => { const next = { ...prev }; delete next[id]; return next; });
  }

  // Education functions
  function addEducation() {
    if (newEduTitle.trim() && newEduOrg.trim()) {
      const newEdu: Education = {
        title: newEduTitle.trim(),
        org: newEduOrg.trim(),
        year: newEduYear.trim(),
        tags: [],
      };
      setResume({ ...resume, education: [...resume.education, newEdu] });
      setNewEduTitle('');
      setNewEduOrg('');
      setNewEduYear('');
    }
  }

  function updateEducation(index: number, updates: Partial<Education>) {
    const items = [...resume.education];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, education: items });
  }

  function removeEducation(index: number) {
    setResume({ ...resume, education: resume.education.filter((_, i) => i !== index) });
  }

  function moveEducation(index: number, direction: 'up' | 'down') {
    const items = [...resume.education];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, education: items });
  }

  // Award functions
  function addAward() {
    if (newAwardTitle.trim()) {
      const newAwardItem: Award = {
        title: newAwardTitle.trim(),
        issuer: newAwardIssuer.trim(),
        reward: newAwardReward.trim(),
        year: newAwardYear.trim(),
        tags: [],
      };
      setResume({ ...resume, awards: [...resume.awards, newAwardItem] });
      setNewAwardTitle('');
      setNewAwardIssuer('');
      setNewAwardReward('');
      setNewAwardYear('');
    }
  }

  function updateAward(index: number, updates: Partial<Award>) {
    const items = [...resume.awards];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, awards: items });
  }

  function removeAward(index: number) {
    setResume({ ...resume, awards: resume.awards.filter((_, i) => i !== index) });
  }

  function moveAward(index: number, direction: 'up' | 'down') {
    const items = [...resume.awards];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, awards: items });
  }

  // Presentation functions
  function addPresentation() {
    if (newPresentationTitle.trim()) {
      const newPresentationItem: Presentation = {
        title: newPresentationTitle.trim(),
        event: newPresentationEvent.trim(),
        year: newPresentationYear.trim(),
        tags: [],
      };
      setResume({ ...resume, presentations: [...(resume.presentations || []), newPresentationItem] });
      setNewPresentationTitle('');
      setNewPresentationEvent('');
      setNewPresentationYear('');
    }
  }

  function updatePresentation(index: number, updates: Partial<Presentation>) {
    const items = [...(resume.presentations || [])];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, presentations: items });
  }

  function removePresentation(index: number) {
    setResume({ ...resume, presentations: (resume.presentations || []).filter((_, i) => i !== index) });
  }

  function movePresentation(index: number, direction: 'up' | 'down') {
    const items = [...(resume.presentations || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, presentations: items });
  }

  // Publication functions
  function addPublication() {
    if (newPubTitle.trim()) {
      const newPubItem: Publication = {
        title: newPubTitle.trim(),
        url: newPubUrl.trim() || undefined,
        year: newPubYear.trim(),
        tags: [],
      };
      setResume({ ...resume, publications: [...(resume.publications || []), newPubItem] });
      setNewPubTitle('');
      setNewPubUrl('');
      setNewPubYear('');
    }
  }

  function updatePublication(index: number, updates: Partial<Publication>) {
    const items = [...(resume.publications || [])];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, publications: items });
  }

  function removePublication(index: number) {
    setResume({ ...resume, publications: (resume.publications || []).filter((_, i) => i !== index) });
  }

  function movePublication(index: number, direction: 'up' | 'down') {
    const items = [...(resume.publications || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, publications: items });
  }

  // Portfolio functions
  function addPortfolio() {
    if (newPortTitle.trim()) {
      const newPortItem: Portfolio = {
        title: newPortTitle.trim(),
        url: newPortUrl.trim() || undefined,
        year: newPortYear.trim(),
        tags: [],
      };
      setResume({ ...resume, portfolios: [...(resume.portfolios || []), newPortItem] });
      setNewPortTitle('');
      setNewPortUrl('');
      setNewPortYear('');
    }
  }

  function updatePortfolio(index: number, updates: Partial<Portfolio>) {
    const items = [...(resume.portfolios || [])];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, portfolios: items });
  }

  function removePortfolio(index: number) {
    setResume({ ...resume, portfolios: (resume.portfolios || []).filter((_, i) => i !== index) });
  }

  function movePortfolio(index: number, direction: 'up' | 'down') {
    const items = [...(resume.portfolios || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, portfolios: items });
  }

  // Demographics functions (optional, extension-only — never on the resume)
  function updateDemographics(updates: Partial<Demographics>) {
    setResume({
      ...resume,
      demographics: { ...emptyDemographics, ...(resume.demographics || {}), ...updates },
    });
  }

  // Experience functions
  function getExperience(): ExperienceCompany[] {
    return (resume.experience || []) as ExperienceCompany[];
  }

  function setExperience(exp: ExperienceCompany[]) {
    setResume({ ...resume, experience: exp });
  }

  function addCompany() {
    const newCompany: ExperienceCompany = {
      company: '',
      url: [],
      startDate: '',
      current: false,
      roles: []
    };
    setExperience([...getExperience(), newCompany]);
    setEditingCompanyIndex(getExperience().length);
  }

  function updateCompany(index: number, updates: Partial<ExperienceCompany>) {
    const exp = [...getExperience()];
    exp[index] = { ...exp[index], ...updates };
    setExperience(exp);
  }

  function removeCompany(index: number) {
    setExperience(getExperience().filter((_, i) => i !== index));
    setEditingCompanyIndex(null);
  }

  function moveCompany(index: number, direction: 'up' | 'down') {
    const exp = [...getExperience()];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= exp.length) return;
    [exp[index], exp[newIndex]] = [exp[newIndex], exp[index]];
    setExperience(exp);
  }

  function addRole(companyIndex: number) {
    const exp = [...getExperience()];
    const newRole: ExperienceRole = {
      title: '',
      startDate: '',
      current: false,
      bullets: []
    };
    exp[companyIndex].roles = [...exp[companyIndex].roles, newRole];
    setExperience(exp);
    setEditingRoleIndex({ companyIdx: companyIndex, roleIdx: exp[companyIndex].roles.length - 1 });
  }

  function updateRole(companyIndex: number, roleIndex: number, updates: Partial<ExperienceRole>) {
    const exp = [...getExperience()];
    exp[companyIndex].roles[roleIndex] = { ...exp[companyIndex].roles[roleIndex], ...updates };
    setExperience(exp);
  }

  function removeRole(companyIndex: number, roleIndex: number) {
    const exp = [...getExperience()];
    exp[companyIndex].roles = exp[companyIndex].roles.filter((_, i) => i !== roleIndex);
    setExperience(exp);
    setEditingRoleIndex(null);
  }

  function moveRole(companyIndex: number, roleIndex: number, direction: 'up' | 'down') {
    const exp = [...getExperience()];
    const roles = [...exp[companyIndex].roles];
    const newIndex = direction === 'up' ? roleIndex - 1 : roleIndex + 1;
    if (newIndex < 0 || newIndex >= roles.length) return;
    [roles[roleIndex], roles[newIndex]] = [roles[newIndex], roles[roleIndex]];
    exp[companyIndex].roles = roles;
    setExperience(exp);
  }

  function addBullet(companyIndex: number, roleIndex: number) {
    const exp = [...getExperience()];
    const newBullet: ExperienceBullet = { text: '', tags: [] };
    exp[companyIndex].roles[roleIndex].bullets = [...exp[companyIndex].roles[roleIndex].bullets, newBullet];
    setExperience(exp);
  }

  function updateBullet(companyIndex: number, roleIndex: number, bulletIndex: number, updates: Partial<ExperienceBullet>) {
    const exp = [...getExperience()];
    exp[companyIndex].roles[roleIndex].bullets[bulletIndex] = {
      ...exp[companyIndex].roles[roleIndex].bullets[bulletIndex],
      ...updates
    };
    setExperience(exp);
  }

  function removeBullet(companyIndex: number, roleIndex: number, bulletIndex: number) {
    const exp = [...getExperience()];
    exp[companyIndex].roles[roleIndex].bullets = exp[companyIndex].roles[roleIndex].bullets.filter((_, i) => i !== bulletIndex);
    setExperience(exp);
  }

  function moveBullet(companyIndex: number, roleIndex: number, bulletIndex: number, direction: 'up' | 'down') {
    const exp = [...getExperience()];
    const bullets = [...exp[companyIndex].roles[roleIndex].bullets];
    const newIndex = direction === 'up' ? bulletIndex - 1 : bulletIndex + 1;
    if (newIndex < 0 || newIndex >= bullets.length) return;
    [bullets[bulletIndex], bullets[newIndex]] = [bullets[newIndex], bullets[bulletIndex]];
    exp[companyIndex].roles[roleIndex].bullets = bullets;
    setExperience(exp);
  }

  function getIconSvg(iconName: ContactItem['icon']) {
    const iconConfig = iconOptions.find(i => i.value === iconName);
    return iconConfig?.svg || '/icons/globe.svg';
  }

  function getUrlPlaceholder(iconName: ContactItem['icon']) {
    const iconConfig = iconOptions.find(i => i.value === iconName);
    return iconConfig?.placeholder || 'https://example.com';
  }

  function autoFillContactUrl(icon: ContactItem['icon'], title: string): string | null {
    const trimmed = title.trim();
    if (!trimmed) return null;
    const contactPrefix: Record<string, string> = {
      'email-at': 'mailto:',
      'phone': 'tel:+',
      'phone-volume': 'tel:+',
      'linkedin': 'https://www.linkedin.com/in/',
      'x-twitter': 'https://x.com/',
      'github': 'https://github.com/',
      'github-square': 'https://github.com/',
      'discord': 'https://discord.com/users/',
      'telegram': 'https://t.me/',
      'signal-chat': 'https://signal.me/#u/',
    };
    const prefix = contactPrefix[icon];
    if (!prefix) return null;
    if (icon === 'phone' || icon === 'phone-volume') {
      const digits = trimmed.replace(/\D/g, '');
      if (!digits) return null;
      const normalized = (digits.length === 10 && /^[2-9]/.test(digits)) ? `1${digits}` : digits;
      return `tel:+${normalized}`;
    }
    if (icon === 'email-at') {
      return `mailto:${trimmed}`;
    }
    return `${prefix}${trimmed}`;
  }

  // Import functions
  function handleLoadFromFileClick() {
    fileInputRef.current?.click();
  }

  async function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input so the same file can be selected again
    event.target.value = '';

    setImporting(true);
    setError(null);
    setWarning(null);
    setTagGenPhase('idle');
    if (tagPollRef.current) tagPollRef.current.cancelled = true;

    try {
      const result = await importResumeFile(file);

      // Populate form with imported data
      setResume(result.resumejson);

      // Build the friendly import-notes message (shown only when we are NOT
      // about to kick off tag generation; otherwise the tag-gen status box
      // replaces it).
      let importNotes: string | null = null;
      if (result.warnings && result.warnings.length > 0) {
        const friendly = result.warnings.map(w => {
          // Rephrase Schema warnings into plain language
          const schemaMatch = w.match(/^Schema: (\w+)\[0\]: missing required field '(\w+)'$/);
          if (schemaMatch) return `Did not find a ${schemaMatch[1]} section`;
          if (w.includes('processed by AI')) return 'PDF content was processed by AI — please verify accuracy';
          return w;
        });
        importNotes = `Import completed successfully, with a few notes:\n${friendly.map(f => `• ${f}`).join('\n')}`;
      }

      // R7: kick off tag generation immediately by auto-saving (without closing).
      // Pass the freshly imported data explicitly so we don't depend on the
      // setResume state commit above. The async tag pipeline picks up from the
      // save. A brand-new resume normally has an empty name field, which used to
      // silently skip the auto-save — so tags never generated and no status
      // showed. When this is a NEW resume with no name, default it to a unique
      // name based on the imported candidate name so generation always runs.
      const candidateName = (result.resumejson?.name || '').trim();
      let effectiveName = name.trim();
      if (!effectiveName && !resumename && candidateName) {
        effectiveName = await deriveUniqueResumeName(candidateName);
      }
      // Auto-save kicks off tag generation. handleSave shows the tag-gen status
      // and polls until tags land (see its keep-open branch), so the imported
      // resume's tags appear without a manual close/reopen.
      const willSave = !!(effectiveName && result.resumejson?.name);
      if (willSave) {
        if (!name.trim()) setName(effectiveName);
        handleSave(false, result.resumejson, effectiveName, { skipDateCheck: true }).catch((e) => {
          console.warn('Auto-save after import failed:', e);
          if (importNotes) setWarning(importNotes);
        });
      } else if (importNotes) {
        // Could not auto-save (e.g. the importer didn't extract a name) — show
        // the import notes so the user knows to review and save manually.
        setWarning(importNotes);
      }
    } catch (err) {
      // Preserve existing form data on error (Requirement 6.6)
      // Handle specific error types with user-friendly messages (Requirements 8.1-8.5)
      if (err instanceof ImportValidationError) {
        setError(err.message);
      } else if (err instanceof ImportUploadError) {
        setError(err.message);
      } else if (err instanceof ImportProcessError) {
        setError(err.message);
      } else if (err instanceof ImportAuthError) {
        setError(err.message);
      } else if (err instanceof ImportTimeoutError) {
        setError(err.message);
      } else {
        // Generic fallback error (Requirement 8.4)
        setError('Could not read file. Please ensure it\'s a valid YAML, JSON, or PDF file.');
        // Log detailed error for debugging (Requirement 8.5)
        console.error('Import error:', err);
      }
    } finally {
      setImporting(false);
    }
  }

  function handleDownloadTemplate() {
    // Check if resume has real data (not just empty/default values)
    const hasRealData = resume.name ||
                        resume.skills.length > 0 ||
                        resume.summary ||
                        getExperience().length > 0 ||
                        resume.education.length > 0 ||
                        resume.awards.length > 0 ||
                        (resume.presentations || []).length > 0 ||
                        (resume.publications || []).length > 0 ||
                        (resume.portfolios || []).length > 0 ||
                        resume.contact.length > 0;

    if (!hasRealData) {
      // No real data - download the template
      const link = document.createElement('a');
      link.href = '/skillsnap-resume-template.yaml';
      link.download = 'skillsnap-resume-template.yaml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // Convert resume data to YAML format. Every item is projected through the
    // schema mirror (utils/resumeNormalize -> utils/resumeSchema), so the file
    // carries exactly the schema-known fields the record has — `include` and the
    // derived `_iso` dates included — and re-imports without losing them.
    const yamlData = buildYamlExportData(resume);

    // Convert to YAML string with header comment
    const yamlHeader = `# SkillSnap Resume Export
# Generated from: ${name || 'Untitled Resume'}
# Date: ${new Date().toISOString().split('T')[0]}
# Import this file using "Load from File" in SkillSnap.

`;
    const yamlContent = yamlHeader + yaml.dump(yamlData, {
      indent: 2,
      lineWidth: -1, // Don't wrap lines
      noRefs: true,
      quotingType: '"',
      forceQuotes: false
    });

    // Create and download the file
    const blob = new Blob([yamlContent], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename = name ? `${name.replace(/[^a-zA-Z0-9]/g, '_')}.yaml` : 'resume.yaml';
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Export the full, unfiltered resume (all sections / all items, no page-fit
  // pruning) as a PDF or plain-text file, then open it in a new tab.
  async function handleExportFull(format: 'pdf' | 'text') {
    setExporting(format);
    setError(null);
    setWarning(null);
    try {
      const { publicUrl } = await api.exportFullResume(format, resumename || name.trim(), resume);
      window.open(publicUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || `Failed to export ${format === 'pdf' ? 'PDF' : 'text'}.`;
      setError(msg);
    } finally {
      setExporting(null);
    }
  }

  /**
   * Kick off the implied-facts extraction for the resume being closed.
   *
   * Fire-and-forget by design: the close must feel instant, and the endpoint
   * only DISPATCHES (the worker runs in gen_tags). The parent owns the wait —
   * this component is about to unmount — so the run_id is handed up and the
   * parent polls for it. Every close path routes through here.
   */
  function fireImpliedFactsRun() {
    const target = (resumename || name || '').trim();
    if (!target) return;   // create-mode close with nothing saved yet
    api.triggerImpliedFacts(target)
      .then(res => {
        if (res?.status === 'started' && res.run_id) {
          onImpliedFactsRun?.(target, res.run_id);
        }
      })
      .catch(() => { /* best-effort: never surface a close-time error */ });
  }

  // Prevent modal close during import
  function handleClose() {
    if (importing) return;
    if (tagPollRef.current) tagPollRef.current.cancelled = true;
    setTagGenPhase('idle');
    fireImpliedFactsRun();
    // If the "All recommendations have been implemented" banner is currently
    // applicable, remember that the user has seen it so it won't reappear on
    // the next open (re-armed only by running a fresh AI Review).
    try {
      if (recsAckKey && hasReviewedBefore && !hasRecommendations) {
        localStorage.setItem(recsAckKey, '1');
      }
    } catch { /* ignore */ }
    onClose();
  }

  // ---- Resume ▾ → Switch resumes / New resume -----------------------------
  // Both navigations abandon the current draft, so an unsaved editor asks
  // first (the confirm below); a clean editor goes straight through.
  function requestNav(nav: { kind: 'switch'; resumename: string } | { kind: 'new' }) {
    if (importing || saving) return;
    setResumeMenuOpen(false);
    if (isDirty) {
      setPendingNav(nav);
      return;
    }
    runNav(nav);
  }

  function runNav(nav: { kind: 'switch'; resumename: string } | { kind: 'new' }) {
    setPendingNav(null);
    // Stop any in-flight tag poll for the resume we're leaving.
    if (tagPollRef.current) tagPollRef.current.cancelled = true;
    setTagGenPhase('idle');
    setError(null);
    setWarning(null);
    if (nav.kind === 'switch') {
      // Parent re-points `resumename`; the [isOpen, resumename] effect reloads
      // through the same path used when the editor is opened for a resume.
      onSwitchResume?.(nav.resumename);
      return;
    }
    // New resume: hand off to the app's existing create flow (quota checks
    // live there), which reopens the editor in create-mode.
    handleClose();
    onNewResume?.();
  }

  async function handleClone() {
    if (!resumename) return;
    setCloning(true);
    setError(null);
    try {
      // Generate clone name: "Resume Name (1)", or increment if that exists
      const baseName = name || resumename;
      const match = baseName.match(/^(.*)\s*\((\d+)\)$/);
      let cloneName: string;
      if (match) {
        cloneName = `${match[1]} (${parseInt(match[2], 10) + 1})`;
      } else {
        cloneName = `${baseName} (1)`;
      }
      await api.createResume(cloneName, withSectionSettings(resume));
      onClose();
      onSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to clone resume';
      setError(msg);
    } finally {
      setCloning(false);
    }
  }

  async function handleDeleteResume() {
    if (!resumename || !onDeleteResume) return;
    setDeleting(true);
    try {
      await onDeleteResume(resumename);
      setShowDeleteConfirm(false);
      onClose();
      onSuccess();
    } catch (err) {
      console.error('Failed to delete resume:', err);
      setError('Failed to delete resume');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  }

  // ---- Per-section editor bodies -------------------------------------
  // The lightbox renders these in the per-resume section order (draft
  // state below), so the arrows beside each heading reorder both the
  // editor and the generated resume. Bodies are unchanged from when they
  // were inline — only their wrapper moved.
  // One Summary v2 subsection: label + blurb + its tagged entries + add box.
  // Entry interaction is the skills/phrase pattern verbatim (click text to
  // edit, comma-separated tag box committed on blur/Enter, include checkbox,
  // remove ×) with the lower min-3 tag chip.
  function renderSummarySubsection(meta: { key: SummarySectionKey; label: string; blurb: string; placeholder: string }) {
    const key = meta.key;
    const entries = summaryEntries(key);
    return (
      <div key={key} className="mt-3" data-uat={`summary-section-${key}`}>
        <label className="block text-sm font-medium text-gray-700">{meta.label}</label>
        <p className="text-xs text-gray-500 mb-2">{meta.blurb}</p>
        {/* Credibility nudge (features/summary_v2.md): the split found nothing
            quantified. It clears itself the moment a credibility entry carries
            a figure, so it can never nag past being answered. */}
        {key === 'credibility' && summarySplitNeedsNumber
          && !entries.some(e => /\d/.test(e.text || '')) && (
          <div
            data-uat="summary-credibility-nudge"
            className="mb-2 flex items-start gap-1.5 p-2 border border-amber-200 bg-amber-50 rounded-lg text-xs text-amber-900"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
            <span>
              Add one concrete figure — a dollar amount, a team size, a scale marker.
              This is what separates a summary from an objective statement.
            </span>
          </div>
        )}
        {entries.length > 0 && (
          <div className="space-y-2 mb-2">
            {entries.map((entry, i) => {
              const editing = editingSummaryEntry?.key === key && editingSummaryEntry.index === i;
              return (
                <div
                  key={entry.id || i}
                  data-summary-entry={`${key}-${i}`}
                  data-uat="summary-entry"
                  className={`flex items-start gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm border border-gray-100${entry.include === '0' ? ' opacity-40' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={entry.include !== '0'}
                    onChange={(e) => updateSummaryEntry(key, i, { include: e.target.checked ? '1' : '0' })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1 shrink-0 w-3 h-3"
                    title={entry.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                  />
                  <div className="flex-1 min-w-0">
                    {editing ? (
                      <>
                        <textarea
                          value={entry.text}
                          onChange={(e) => updateSummaryEntry(key, i, { text: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); setEditingSummaryEntry(null); } }}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-1"
                          rows={2}
                          autoFocus
                        />
                        <input
                          key={(entry.tags || []).join(',')}
                          type="text"
                          defaultValue={(entry.tags || []).join(', ')}
                          onBlur={(e) => updateSummaryEntry(key, i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updateSummaryEntry(key, i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                              setEditingSummaryEntry(null);
                            }
                          }}
                          className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                          placeholder="tags (comma-separated)"
                        />
                        {entry.include !== '0' && renderTagStatusChip(entry.tags, entry.text, TAG_MIN_SUMMARY_SECTION)}
                      </>
                    ) : (
                      <div onClick={() => setEditingSummaryEntry({ key, index: i })} className="cursor-pointer">
                        <div className="text-sm text-gray-700 leading-snug">{entry.text}</div>
                        {(entry.tags || []).length > 0 && (
                          <div className="text-[10px] text-gray-400 truncate leading-tight mt-0.5">{entry.tags.join(', ')}</div>
                        )}
                        {entry.include !== '0' && renderTagStatusChip(entry.tags, entry.text, TAG_MIN_SUMMARY_SECTION)}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { if (editing) setEditingSummaryEntry(null); removeSummaryEntry(key, i); }}
                    className="text-gray-400 hover:text-red-500 shrink-0 mt-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={newSummaryEntry[key] || ''}
            onChange={(e) => setNewSummaryEntry(prev => ({ ...prev, [key]: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && addSummaryEntry(key)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder={meta.placeholder}
            data-uat={`summary-add-${key}`}
            autoComplete="off"
          />
          <button onClick={() => addSummaryEntry(key)} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200" title={`Add to ${meta.label}`}>
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  function renderSummarySection() {
    return (
      <>
          {/* Summary */}
          <div data-help-target="mainapp.resumes.resumeN.summary" className={sectionGenTypes.summary === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('summary', 'mb-1')}
            {isSectionOpen('summary') && (<>
            {/* The recruiter shorthand is the card's sub-header (owner,
                2026-08-19): it frames the whole card — the Manual block and the
                three subsections both — so it sits directly under the title and
                renders exactly ONCE. */}
            <p className="text-xs text-gray-600 mb-3">
              <span className="font-medium text-gray-700">who I am → why you should believe me → why I fit this job</span>
              {' '}— it maps to how recruiters read (categorize → verify → decide relevance) in the first 10 seconds.
            </p>
            <label className="block text-sm font-medium text-gray-700">Manual Summary</label>
            <p className="text-xs text-gray-500 mb-2">
              When "Manual" is selected, this section is used verbatim, excepting spelling
              and grammar check, and synonym word processing.
            </p>
            {/* maxWidth caps this fixed resume-width control at the panel width so it
                cannot scroll the editor sideways on a narrow viewport (the same guard
                the company-description textarea already carries). */}
            <textarea
              rows={5}
              value={resume.summary}
              onChange={(e) => setResume({ ...resume, summary: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg"
              style={{ fontFamily: RESUME_FONT, fontSize: '10.5pt', lineHeight: 1.45, width: RESUME_WIDTH + 26, maxWidth: '100%' }}
              placeholder="Brief professional summary..."
            />
            {renderInlineRecs('summary')}
            </>)}
          </div>

          {/* Summary v2 building blocks (features/summary_v2.md): the three
              subsections a tailored summary is assembled from. */}
          {isSectionOpen('summary') && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">Summary Sections</label>
            <p className="text-xs text-gray-500">
              When "AI (Generate)" is selected, the summary is built using sentence fragments
              that are the best fit for the job you are applying to — one for each of the three
              structural sections of a good summary: Identity, Credibility, Relevance. Generate
              as many as you like; SkillSnap picks the top matches for each job description.
              Your Summary stays in your voice — AI only does the stitching. As always, spelling
              and grammar checks are included, as is synonym word processing.
            </p>
            {/* Assisted migration. Offered only to a resume that HAS legacy
                summary content and has not adopted subsections; the moment the
                proposal lands (or the user types their own first entry) the CTA
                and the legacy block both disappear. */}
            {hasLegacySummaryContent(resume) && !hasSummarySectionEntries(resume) && (
              <div className="mt-3 flex items-start gap-3 p-2.5 border border-primary-200 bg-primary-50 rounded-lg">
                <p className="flex-1 text-xs text-gray-600">
                  Split your existing summary into these three subsections. AI proposes the
                  entries from your own wording — review and edit them, then Save to keep them.
                </p>
                <button
                  type="button"
                  onClick={handleSplitSummary}
                  disabled={splittingSummary || bootstrappingFragments || loading || saving || importing}
                  data-uat="summary-split-cta"
                  className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {splittingSummary
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Sparkles className="w-3.5 h-3.5" />}
                  <span>{splittingSummary ? 'Splitting…' : 'Split into sections'}</span>
                </button>
              </div>
            )}
            {/* Bootstrap from the WHOLE resume (owner, 2026-08-19). Same
                "no entries yet" gate as the split, but no legacy-content
                requirement — it reads experience, skills and credentials — so
                both CTAs can show at once on a resume that has a summary too. */}
            {!hasSummarySectionEntries(resume) && (
              <div className="mt-3 flex items-start gap-3 p-2.5 border border-gray-200 bg-gray-50 rounded-lg">
                <p className="flex-1 text-xs text-gray-600">Need help getting started:</p>
                <button
                  type="button"
                  onClick={handleBootstrapFragments}
                  disabled={splittingSummary || bootstrappingFragments || loading || saving || importing}
                  data-uat="summary-bootstrap-cta"
                  className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {bootstrappingFragments
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Sparkles className="w-3.5 h-3.5" />}
                  <span>{bootstrappingFragments ? 'Generating…' : 'Auto-generate Summary Fragments'}</span>
                </button>
              </div>
            )}
            {SUMMARY_SECTION_META.map(renderSummarySubsection)}
          </div>
          )}

          {/* Legacy phrase bank — shown only for resumes that have phrases and
              have NOT adopted subsections. One entry anywhere above hides it. */}
          {isSectionOpen('summary') && (resume.summary_phrases || []).length > 0 && !hasSummarySectionEntries(resume) && (
          <div className="mt-4 border-t border-gray-100 pt-3" data-uat="legacy-summary-phrases">
            <button
              type="button"
              onClick={() => setLegacyPhrasesOpen(o => !o)}
              className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              {legacyPhrasesOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Legacy phrases ({(resume.summary_phrases || []).length})
            </button>
            {legacyPhrasesOpen && (
            <div className="mt-2">
            <label className="block text-sm font-semibold text-gray-600 mb-1">Summary Phrases</label>
            <p className="text-xs text-gray-500 mb-2">
              Optional. Define consistent phrasing that's selected as a unit when building the summary section.
              Phrases are used verbatim, so craft them carefully — they're ranked by tag relevance, and low-match phrases are excluded automatically.
            </p>
            {(resume.summary_phrases || []).length > 0 && (
              <div className="space-y-2 mb-2">
                {(resume.summary_phrases || []).map((phrase, i) => (
                  <div key={i} data-phrase-index={i} className={`flex items-start gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm border border-gray-100${phrase.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={phrase.include !== '0'}
                      onChange={(e) => {
                        const phrases = [...(resume.summary_phrases || [])];
                        phrases[i] = { ...phrases[i], include: e.target.checked ? '1' : '0' };
                        setResume({ ...resume, summary_phrases: phrases });
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1 shrink-0 w-3 h-3"
                      title={phrase.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    <div className="flex-1 min-w-0">
                      {editingSummaryPhraseIndex === i ? (
                        <>
                          <textarea
                            value={phrase.text}
                            onChange={(e) => updateSummaryPhraseText(i, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); setEditingSummaryPhraseIndex(null); } }}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-1"
                            rows={2}
                            autoFocus
                          />
                          <input
                            key={(phrase.tags || []).join(',')}
                            type="text"
                            defaultValue={(phrase.tags || []).join(', ')}
                            onBlur={(e) => updateSummaryPhraseTags(i, e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateSummaryPhraseTags(i, e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean));
                                setEditingSummaryPhraseIndex(null);
                              }
                            }}
                            className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                            placeholder="tags (comma-separated)"
                          />
                          {phrase.include !== '0' && renderTagStatusChip(phrase.tags, phrase.text)}
                        </>
                      ) : (
                        <div onClick={() => setEditingSummaryPhraseIndex(i)} className="cursor-pointer">
                          <div className="text-sm text-gray-700 leading-snug">{phrase.text}</div>
                          {(phrase.tags || []).length > 0 && (
                            <div className="text-[10px] text-gray-400 truncate leading-tight mt-0.5">{phrase.tags.join(', ')}</div>
                          )}
                          {phrase.include !== '0' && renderTagStatusChip(phrase.tags, phrase.text)}
                        </div>
                      )}
                    </div>
                    <button onClick={() => { if (editingSummaryPhraseIndex === i) setEditingSummaryPhraseIndex(null); removeSummaryPhrase(i); }} className="text-gray-400 hover:text-red-500 shrink-0 mt-1">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input type="text" value={newSummaryPhrase} onChange={(e) => setNewSummaryPhrase(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSummaryPhrase()} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Add summary phrase..." autoComplete="off" />
              <button onClick={addSummaryPhrase} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            </div>
            )}
          </div>
          )}
      </>
    );
  }

  function renderHighlightsSection() {
    return (
      <>
          {/* Highlights — no editable content; the bullets are selected/written
              per job at generation time. Only the heading (name/order) and the
              generation-type pill are set here, like every other section. */}
          <div data-help-target="mainapp.resumes.resumeN.highlights" className={sectionGenTypes.highlights === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('highlights', 'mb-1')}
            {isSectionOpen('highlights') && (
            <p className="text-xs text-gray-500 mb-2">
              {sectionGenTypes.highlights === 'embedding'
                ? 'AI Match selects the 3 most relevant bullets from your Experience section — regardless of company or role — and uses each one verbatim as a highlight, with the company cited at the end. Those bullets also remain in their Company/Role section. AI does NOT modify the text, so be sure every experience bullet is well-formed and polished.'
                : sectionGenTypes.highlights === 'generate'
                ? 'AI Generate writes 3 brand-new highlight bullets tailored to each job description, drawing on your highest-matching experience and combining achievements as needed. The wording is freshly composed by AI rather than copied, so review the results for accuracy before applying.'
                : 'Highlights will be omitted — no highlights section will appear on generated resumes.'}
            </p>
            )}
          </div>
      </>
    );
  }

  function renderSkillsSection() {
    return (
      <>
          {/* Skills */}
          <div data-help-target="mainapp.resumes.resumeN.skills" className={sectionGenTypes.skills === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('skills', 'mb-2')}
            {isSectionOpen('skills') && (<>
            {/* Gen-type-aware description of how skills render on the bespoke resume. */}
            <p className="text-xs text-gray-500 mb-2">
              {sectionGenTypes.skills === 'embedding' && (
                <>
                  AI Match will match the skill groups and skills against each job description, then display
                  only the most relevant skills for each job (highest match first). The skill group name is also
                  tweaked to best fit the job description language, to make the categories sound familiar to the
                  hiring manager. If a skill group has low relevance, but one particular skill has high relevance,
                  that skill can be added to an &quot;Other&quot; category, to ensure it&apos;s represented.
                </>
              )}
              {sectionGenTypes.skills === 'manual' && (
                <>
                  Will display ALL active (checked) skills. To remove a skill from the resume, simply uncheck the
                  skill below. If you uncheck ALL skills from a particular skill group, that group will not appear
                  on the resume.
                </>
              )}
              {sectionGenTypes.skills === 'omit' && (
                <>The Skills section will not appear on generated resumes.</>
              )}
            </p>
            {/* Conflict pointer (task 8): full copy is in the top error banner; this
                is the local cue tying the red highlight to the synonym clash. */}
            {conflictSetIds.size > 0 && (
              <div className="mb-2 text-xs text-red-600 font-medium">
                ⚠ Skills highlighted in red clash with a synonym set — see the note at the top.
              </div>
            )}
            {/* Skills Display Controls — how much of the Skills section renders
                and in what shape, per resume. Omit has nothing to shape. */}
            {sectionGenTypes.skills !== 'omit' && (() => {
              const sk = resolveSkillsSettings(resume.skills_settings);
              return (
                <div className="flex flex-wrap items-end gap-3 mb-3">
                  <div className="w-44">
                    <label className="block text-xs text-gray-500 mb-1">Skills display</label>
                    <select
                      data-testid="skills-display-select"
                      value={sk.display}
                      onChange={(e) => updateSkillsSettings({ display: e.target.value as SkillsSettings['display'] })}
                      title="In sections: skills grouped under section names. Individual best fit: one flat best-fit list."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    >
                      <option value="sections">In sections</option>
                      <option value="individual">Individual best fit</option>
                    </select>
                  </div>
                  <div className="w-36">
                    <label className="block text-xs text-gray-500 mb-1">Max skill sections</label>
                    <input
                      data-testid="skills-max-sections"
                      type="number"
                      min={SKILLS_MAX_SECTIONS_MIN}
                      max={SKILLS_MAX_SECTIONS_MAX}
                      value={sk.max_sections}
                      onChange={(e) => updateSkillsSettings({
                        max_sections: clampSkillsNumber(e.target.value, SKILLS_MAX_SECTIONS_MIN, SKILLS_MAX_SECTIONS_MAX, sk.max_sections),
                      })}
                      title="Most skill sections shown per resume"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="w-36">
                    <label className="block text-xs text-gray-500 mb-1">Max skill lines</label>
                    <input
                      data-testid="skills-max-lines"
                      type="number"
                      min={SKILLS_MAX_LINES_MIN}
                      max={SKILLS_MAX_LINES_MAX}
                      value={sk.max_lines}
                      onChange={(e) => updateSkillsSettings({
                        max_lines: clampSkillsNumber(e.target.value, SKILLS_MAX_LINES_MIN, SKILLS_MAX_LINES_MAX, sk.max_lines),
                      })}
                      title="Lines per section. In Individual mode the total lines = sections × lines."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  {/* Trailing "Other" line. Only the sections display has group
                      lines for a skill to miss, so the control is DISABLED (not
                      hidden, and the stored value is untouched) in Individual
                      mode — switching back must restore the user's choice. */}
                  {(() => {
                    const otherApplies = sk.display === 'sections';
                    return (
                      <div className="pb-2">
                        <label
                          className={`flex items-center gap-2 text-xs ${otherApplies ? 'text-gray-500' : 'text-gray-400 cursor-not-allowed'}`}
                          title={otherApplies
                            ? "When checked, high-relevance skills that don't make a selected section can appear in a trailing 'Other' line. Uncheck to suppress the 'Other' line."
                            : 'Only applies to the In sections display'}
                        >
                          <input
                            data-testid="skills-include-other"
                            type="checkbox"
                            checked={sk.include_other}
                            disabled={!otherApplies}
                            onChange={(e) => updateSkillsSettings({ include_other: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                          />
                          Include &lsquo;Other&rsquo; section
                        </label>
                      </div>
                    );
                  })()}
                </div>
              );
            })()}
            {(() => {
              const groups = (resume.skill_groups || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
              const groupIdSet = new Set(groups.map(g => g.id));
              const indexed = resume.skills.map((skill, i) => ({ skill, i }));
              const ungrouped = indexed.filter(x => !x.skill.group_id || !groupIdSet.has(x.skill.group_id as string));
              const dropHandlers = (gid: string | null) => ({
                onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverGroup(gid ?? '__ungrouped__'); },
                onDragLeave: () => setDragOverGroup(null),
                onDrop: (e: React.DragEvent) => {
                  e.preventDefault();
                  const raw = e.dataTransfer.getData('text/plain');
                  const idx = draggedSkillIdx ?? (raw ? parseInt(raw, 10) : NaN);
                  if (!isNaN(idx)) moveSkillToGroup(idx, gid);
                  setDraggedSkillIdx(null); setDragOverGroup(null);
                },
              });

              if (groups.length === 0) {
                // No groups yet — flat list (legacy look); "Add group" below starts grouping.
                return (
                  <div className="mb-2">
                    <div className="flex flex-wrap gap-2" {...dropHandlers(null)}>
                      {ungrouped.map(({ skill, i }) => renderSkillCard(skill, i))}
                    </div>
                    {renderGroupAddInput('')}
                  </div>
                );
              }

              return (
                <div className="space-y-2 mb-2">
                  {groups.map(g => {
                    const items = indexed.filter(x => x.skill.group_id === g.id);
                    const hot = dragOverGroup === g.id;
                    return (
                      <div key={g.id} {...dropHandlers(g.id)} className={`border rounded-lg p-2 transition-colors ${hot ? 'border-primary-400 bg-primary-50/40' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          {editingGroupId === g.id ? (
                            <input autoFocus value={g.title} onChange={(e) => updateSkillGroupTitle(g.id, e.target.value)} onBlur={() => setEditingGroupId(null)} onKeyDown={(e) => e.key === 'Enter' && setEditingGroupId(null)} placeholder="Group name…" className="text-sm font-semibold px-1.5 py-0.5 border border-gray-300 rounded flex-1 min-w-0" autoComplete="off" />
                          ) : (
                            <button onClick={() => setEditingGroupId(g.id)} className="text-sm font-semibold text-gray-700 hover:text-primary-600 truncate" title="Click to rename">
                              {g.title || <span className="text-gray-400 italic font-normal">Untitled group</span>}
                            </button>
                          )}
                          <span className="text-xs text-gray-400 shrink-0">{items.length}</span>
                          <div className="flex-1" />
                          <button onClick={() => removeSkillGroup(g.id)} title="Delete group (its skills become ungrouped)" className="text-gray-400 hover:text-red-500 shrink-0">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {items.length > 0 ? items.map(({ skill, i }) => renderSkillCard(skill, i))
                            : <span className="text-xs text-gray-400 italic py-1">Drag skills here</span>}
                        </div>
                        {renderGroupAddInput(g.id)}
                      </div>
                    );
                  })}
                  {/* Ungrouped lane — always a drop target so skills can be pulled back out. */}
                  <div {...dropHandlers(null)} className={`border border-dashed rounded-lg p-2 transition-colors ${dragOverGroup === '__ungrouped__' ? 'border-primary-400 bg-primary-50/40' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm font-semibold text-gray-500">Ungrouped</span>
                      <span className="text-xs text-gray-400">{ungrouped.length}</span>
                      <span className="text-[11px] text-gray-400 italic">— auto-sorted into your groups on save</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {ungrouped.length > 0 ? ungrouped.map(({ skill, i }) => renderSkillCard(skill, i))
                        : <span className="text-xs text-gray-400 italic py-1">Drag skills here to ungroup</span>}
                    </div>
                    {renderGroupAddInput('')}
                  </div>
                </div>
              );
            })()}
            {resume.skills.map((_, i) => (
              <div key={`skill-rec-${i}`}>{renderInlineRecs(`skills[${i}]`)}</div>
            ))}
            {/* Section-level and out-of-range skills recommendations */}
            {renderInlineRecs('skills')}
            {(() => {
              const indexedPaths = new Set(resume.skills.map((_, i) => `skills[${i}]`));
              indexedPaths.add('skills');
              const unmatchedPaths = [...new Set(
                pendingRecs
                  .filter(r => (r.path === 'skills' || r.path.startsWith('skills[')) && !indexedPaths.has(r.path))
                  .map(r => r.path)
              )];
              return unmatchedPaths.map(p => <div key={`extra-skill-${p}`}>{renderInlineRecs(p)}</div>);
            })()}
            <button onClick={addSkillGroup} className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200" title="Create a new skill group">
              <Plus className="w-4 h-4" /> Add group
            </button>
            </>)}
          </div>
      </>
    );
  }

  function renderExperienceSection() {
    return (
      <>
          {/* Experience */}
          <div data-help-target="mainapp.resumes.resumeN.experience" className={sectionGenTypes.experience === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('experience', 'mb-2')}
            {isSectionOpen('experience') && (<>
            <div className="space-y-4 mb-3">
              {getExperience().map((company, companyIdx) => (
                <div key={companyIdx} className={`border border-gray-200 rounded-lg p-3${company.include === '0' ? ' opacity-40' : ''}`}>
                  {/* Company Header */}
                  <div className="flex items-start gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={company.include !== '0'}
                      onChange={(e) => updateCompany(companyIdx, { include: e.target.checked ? '1' : '0' })}
                      data-testid={`include-company-${companyIdx}`}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1.5 shrink-0"
                      title={company.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    <Building2 className="w-5 h-5 text-gray-500 mt-1" />
                    <div className="flex-1 min-w-0">
                      {editingCompanyIndex === companyIdx ? (
                        <div className="space-y-2">
                          <input type="text" value={company.company} onChange={(e) => updateCompany(companyIdx, { company: e.target.value })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Company Name" />
                          <div className="grid grid-cols-3 gap-2">
                            <input type="text" value={(company.url || []).join(', ')} onChange={(e) => updateCompany(companyIdx, { url: e.target.value.split(',').map(u => u.trim()).filter(Boolean) })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Company URL(s), comma-separated" />
                            <input type="text" value={company.employees || ''} onChange={(e) => updateCompany(companyIdx, { employees: e.target.value })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="# Employees" />
                            <input type="text" value={company.location || ''} onChange={(e) => updateCompany(companyIdx, { location: e.target.value })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Location" />
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <input
                              type="text"
                              value={company.startDate}
                              onChange={(e) => updateCompany(companyIdx, { startDate: e.target.value })}
                              className={dateInputClass(dateFieldKey('exp', companyIdx, 'startDate'))}
                              data-testid={`date-exp-${companyIdx}-startDate`}
                              placeholder="e.g. June 2022"
                            />
                            <input
                              type="text"
                              value={company.endDate || ''}
                              onChange={(e) => updateCompany(companyIdx, { endDate: e.target.value })}
                              onBlur={(e) => absorbCurrentMarker(e.target.value, (patch) => updateCompany(companyIdx, patch))}
                              disabled={company.current}
                              className={`${dateInputClass(dateFieldKey('exp', companyIdx, 'endDate'))} disabled:bg-gray-100`}
                              data-testid={`date-exp-${companyIdx}-endDate`}
                              placeholder="e.g. June 2022"
                            />
                            <label className="flex items-center text-sm">
                              <input type="checkbox" checked={company.current} onChange={(e) => updateCompany(companyIdx, { current: e.target.checked, endDate: e.target.checked ? undefined : company.endDate })} className="mr-2" />
                              Current
                            </label>
                          </div>
                          <textarea value={company.description || ''} onChange={(e) => updateCompany(companyIdx, { description: e.target.value })} className="px-2 py-1 border border-gray-300 rounded" rows={2} placeholder="Short company description" style={{ fontFamily: RESUME_FONT, fontSize: '10.5pt', lineHeight: 1.45, width: RESUME_WIDTH, maxWidth: '100%' }} />
                          <button onClick={() => setEditingCompanyIndex(null)} className="text-xs text-primary-600 hover:underline">Done editing</button>
                        </div>
                      ) : (
                        <div>
                          <span className="font-medium">{company.company || 'New Company'}</span>
                          {company.location && <span className="text-gray-500 text-sm ml-2">({company.location})</span>}
                          <div className="text-xs text-gray-500">{company.startDate} - {company.current ? 'Present' : company.endDate}</div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button data-testid={`edit-company-${companyIdx}`} aria-label="Edit company" onClick={() => setEditingCompanyIndex(editingCompanyIndex === companyIdx ? null : companyIdx)} className="text-gray-500 hover:text-gray-600">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveCompany(companyIdx, 'up')} disabled={companyIdx === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveCompany(companyIdx, 'down')} disabled={companyIdx === getExperience().length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeCompany(companyIdx)} className="text-gray-500 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Company-level recommendations (company name / description) */}
                  {renderInlineRecs(`experience[${companyIdx}]`)}

                  {/* Roles */}
                  <div className="ml-6 space-y-3">
                    {company.roles.map((role, roleIdx) => (
                      <div key={roleIdx} className={`border-l-2 border-gray-200 pl-3${role.include === '0' ? ' opacity-50' : ''}`}>
                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={role.include !== '0'}
                            onChange={(e) => updateRole(companyIdx, roleIdx, { include: e.target.checked ? '1' : '0' })}
                            data-testid={`include-role-${companyIdx}-${roleIdx}`}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1 shrink-0 w-3.5 h-3.5"
                            title={role.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                          />
                          <Briefcase className="w-4 h-4 text-gray-500 mt-1" />
                          <div className="flex-1 min-w-0">
                            {editingRoleIndex?.companyIdx === companyIdx && editingRoleIndex?.roleIdx === roleIdx ? (
                              <div className="space-y-2">
                                <input type="text" value={role.title} onChange={(e) => updateRole(companyIdx, roleIdx, { title: e.target.value })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Role Title" />
                                <div className="grid grid-cols-3 gap-2">
                                  <input
                                    type="text"
                                    value={role.startDate}
                                    onChange={(e) => updateRole(companyIdx, roleIdx, { startDate: e.target.value })}
                                    className={dateInputClass(dateFieldKey('exp', companyIdx, 'role', roleIdx, 'startDate'))}
                                    data-testid={`date-exp-${companyIdx}-role-${roleIdx}-startDate`}
                                    placeholder="e.g. June 2022"
                                  />
                                  <input
                                    type="text"
                                    value={role.endDate || ''}
                                    onChange={(e) => updateRole(companyIdx, roleIdx, { endDate: e.target.value })}
                                    onBlur={(e) => absorbCurrentMarker(e.target.value, (patch) => updateRole(companyIdx, roleIdx, patch))}
                                    disabled={role.current}
                                    className={`${dateInputClass(dateFieldKey('exp', companyIdx, 'role', roleIdx, 'endDate'))} disabled:bg-gray-100`}
                                    data-testid={`date-exp-${companyIdx}-role-${roleIdx}-endDate`}
                                    placeholder="e.g. June 2022"
                                  />
                                  <label className="flex items-center text-sm">
                                    <input type="checkbox" checked={role.current} onChange={(e) => updateRole(companyIdx, roleIdx, { current: e.target.checked, endDate: e.target.checked ? undefined : role.endDate })} className="mr-2" />
                                    Current
                                  </label>
                                </div>
                                <input type="text" value={role.location || ''} onChange={(e) => updateRole(companyIdx, roleIdx, { location: e.target.value })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Location (if different from company)" />
                                <button onClick={() => setEditingRoleIndex(null)} className="text-xs text-primary-600 hover:underline">Done editing</button>
                              </div>
                            ) : (
                              <div>
                                <span className="text-sm font-medium">{role.title || 'New Role'}</span>
                                <div className="text-xs text-gray-500">{role.startDate} - {role.current ? 'Present' : role.endDate}</div>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <button onClick={() => setEditingRoleIndex(editingRoleIndex?.companyIdx === companyIdx && editingRoleIndex?.roleIdx === roleIdx ? null : { companyIdx, roleIdx })} className="text-gray-500 hover:text-gray-600">
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button onClick={() => moveRole(companyIdx, roleIdx, 'up')} disabled={roleIdx === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                              <ChevronUp className="w-3 h-3" />
                            </button>
                            <button onClick={() => moveRole(companyIdx, roleIdx, 'down')} disabled={roleIdx === company.roles.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                              <ChevronDown className="w-3 h-3" />
                            </button>
                            <button onClick={() => removeRole(companyIdx, roleIdx)} className="text-gray-500 hover:text-red-500">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>

                        {/* Bullets */}
                        <div className="ml-5 mt-2 space-y-1">
                          {role.bullets.map((bullet, bulletIdx) => (
                            <div key={bulletIdx} className={bullet.include === '0' ? 'opacity-50' : ''}>
                              <div className="flex items-start gap-2">
                                <input
                                  type="checkbox"
                                  checked={bullet.include !== '0'}
                                  onChange={(e) => updateBullet(companyIdx, roleIdx, bulletIdx, { include: e.target.checked ? '1' : '0' })}
                                  data-testid={`include-bullet-${companyIdx}-${roleIdx}-${bulletIdx}`}
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1.5 shrink-0 w-3 h-3"
                                  title={bullet.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                                />
                                <span className="text-gray-500 mt-1">•</span>
                                {/* min-w-0 + fitContainer: without them the fixed
                                    RESUME_BULLET_WIDTH control sets this flex item's
                                    automatic minimum size, so the row could not shrink
                                    and the icon cluster below was pushed OUTSIDE the
                                    company card (WQ 92e68bf0). */}
                                <div className="flex-1 min-w-0">
                                  <BulletTextWithLineBreaks
                                    text={bullet.text}
                                    onChange={(text) => updateBullet(companyIdx, roleIdx, bulletIdx, { text })}
                                    fitContainer
                                  />
                                  <input
                                    key={(bullet.tags || []).join(',')}
                                    type="text"
                                    defaultValue={(bullet.tags || []).join(', ')}
                                    onBlur={(e) => updateBullet(companyIdx, roleIdx, bulletIdx, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        updateBullet(companyIdx, roleIdx, bulletIdx, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                                        e.currentTarget.blur();
                                      }
                                    }}
                                    className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500 mt-1"
                                    placeholder="Tags (comma-separated)"
                                  />
                                  {renderTagStatusChip(bullet.tags, bullet.text, TAG_MIN_BULLET)}
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                  <button onClick={() => moveBullet(companyIdx, roleIdx, bulletIdx, 'up')} disabled={bulletIdx === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                                    <ChevronUp className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => moveBullet(companyIdx, roleIdx, bulletIdx, 'down')} disabled={bulletIdx === role.bullets.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                                    <ChevronDown className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => removeBullet(companyIdx, roleIdx, bulletIdx)} className="text-gray-500 hover:text-red-500">
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                              {renderInlineRecs(`experience[${companyIdx}].roles[${roleIdx}].bullets[${bulletIdx}]`)}
                            </div>
                          ))}
                          <button onClick={() => addBullet(companyIdx, roleIdx)} className="text-xs text-primary-600 hover:underline flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Add bullet
                          </button>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addRole(companyIdx)} className="text-xs text-primary-600 hover:underline flex items-center gap-1 ml-1">
                      <Plus className="w-3 h-3" /> Add role
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addCompany} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">
              <Plus className="w-4 h-4" /> Add Company
            </button>
            </>)}
          </div>
      </>
    );
  }

  function renderEducationSection() {
    return (
      <>
          {/* Education */}
          <div data-help-target="mainapp.resumes.resumeN.education" className={sectionGenTypes.education === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('education', 'mb-2')}
            {isSectionOpen('education') && (<>
            <div className="space-y-2 mb-3">
              {resume.education.map((edu, i) => (
                <div key={i}>
                  <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${edu.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={edu.include !== '0'}
                      onChange={(e) => updateEducation(i, { include: e.target.checked ? '1' : '0' })}
                      data-testid={`include-education-${i}`}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                      title={edu.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    {editingEducationIndex === i ? (
                      <div className="flex-1 space-y-1">
                        <div className="flex gap-2 items-center">
                          <input type="text" value={edu.title} onChange={(e) => updateEducation(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title/Degree" />
                          <input type="text" value={edu.org} onChange={(e) => updateEducation(i, { org: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Organization" />
                          <input type="text" value={edu.url || ''} onChange={(e) => updateEducation(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                          <input type="text" value={edu.year || ''} onChange={(e) => updateEducation(i, { year: e.target.value })} className={`w-24 ${dateInputClass(dateFieldKey('education', i, 'year'))}`} data-testid={`date-education-${i}-year`} placeholder="Year" />
                        </div>
                        <input
                          key={(edu.tags || []).join(',')}
                          type="text"
                          defaultValue={(edu.tags || []).join(', ')}
                          onBlur={(e) => updateEducation(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updateEducation(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                          placeholder="Tags (comma-separated)"
                        />
                        {renderTagStatusChip(edu.tags, edu.title)}
                      </div>
                    ) : (
                      <div className="flex-1">
                        <span className="text-sm font-medium">{edu.title}</span>
                        <span className="text-sm text-gray-500"> — {edu.org}</span>
                        {edu.year && <span className="text-xs text-gray-500 ml-2">({edu.year})</span>}
                        <span className="ml-2">{renderTagStatusChip(edu.tags, edu.title)}</span>
                      </div>
                    )}
                    <button onClick={() => setEditingEducationIndex(editingEducationIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveEducation(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveEducation(i, 'down')} disabled={i === resume.education.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => removeEducation(i)} className="text-gray-500 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {renderInlineRecs(`education[${i}]`)}
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Title/Degree</label>
                <input type="text" value={newEduTitle} onChange={(e) => setNewEduTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="B.S. Computer Science" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Organization</label>
                <input type="text" value={newEduOrg} onChange={(e) => setNewEduOrg(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="University Name" />
              </div>
              <div className="w-24">
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <input type="text" value={newEduYear} onChange={(e) => setNewEduYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addEducation()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2020" />
              </div>
              <button onClick={addEducation} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            </>)}
          </div>
      </>
    );
  }

  function renderAwardsSection() {
    return (
      <>
          {/* Awards */}
          <div data-help-target="mainapp.resumes.resumeN.awards" className={sectionGenTypes.awards === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('awards', 'mb-2')}
            {isSectionOpen('awards') && (<>
            <div className="space-y-2 mb-3">
              {resume.awards.map((award, i) => (
                <div key={i}>
                  <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${award.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={award.include !== '0'}
                      onChange={(e) => updateAward(i, { include: e.target.checked ? '1' : '0' })}
                      data-testid={`include-awards-${i}`}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                      title={award.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    {editingAwardIndex === i ? (
                      <div className="flex-1 space-y-1">
                        <div className="flex gap-2 items-center">
                          <input type="text" value={award.title} onChange={(e) => updateAward(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" />
                          <input type="text" value={award.issuer || ''} onChange={(e) => updateAward(i, { issuer: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Issuer" />
                          <input type="text" value={award.year || ''} onChange={(e) => updateAward(i, { year: e.target.value })} className={`w-24 ${dateInputClass(dateFieldKey('awards', i, 'year'))}`} data-testid={`date-awards-${i}-year`} placeholder="Year" />
                        </div>
                        <div className="flex gap-2 items-center">
                          <input type="text" value={award.reward || ''} onChange={(e) => updateAward(i, { reward: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Reward" />
                          <input type="text" value={award.url || ''} onChange={(e) => updateAward(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                        </div>
                        <input
                          key={(award.tags || []).join(',')}
                          type="text"
                          defaultValue={(award.tags || []).join(', ')}
                          onBlur={(e) => updateAward(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updateAward(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                          placeholder="Tags (comma-separated)"
                        />
                        {renderTagStatusChip(award.tags, award.title)}
                      </div>
                    ) : (
                      <div className="flex-1">
                        <span className="text-sm font-medium">{award.title}</span>
                        {award.issuer && <span className="text-sm text-gray-500"> — {award.issuer}</span>}
                        {award.reward && <span className="text-sm text-gray-500"> — {award.reward}</span>}
                        {award.year && <span className="text-xs text-gray-500 ml-2">({award.year})</span>}
                        <span className="ml-2">{renderTagStatusChip(award.tags, award.title)}</span>
                      </div>
                    )}
                    <button onClick={() => setEditingAwardIndex(editingAwardIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveAward(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveAward(i, 'down')} disabled={i === resume.awards.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => removeAward(i)} className="text-gray-500 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {renderInlineRecs(`awards[${i}]`)}
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Title</label>
                <input type="text" value={newAwardTitle} onChange={(e) => setNewAwardTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Award Name" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Issuer</label>
                <input type="text" value={newAwardIssuer} onChange={(e) => setNewAwardIssuer(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Awarding organization" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Reward</label>
                <input type="text" value={newAwardReward} onChange={(e) => setNewAwardReward(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Reward provided" />
              </div>
              <div className="w-24">
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <input type="text" value={newAwardYear} onChange={(e) => setNewAwardYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addAward()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2023" />
              </div>
              <button onClick={addAward} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            </>)}
          </div>
      </>
    );
  }

  function renderPresentationsSection() {
    return (
      <>
          {/* Presentations */}
          <div data-help-target="mainapp.resumes.resumeN.presentations" className={sectionGenTypes.presentations === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('presentations', 'mb-2')}
            {isSectionOpen('presentations') && (<>
            <div className="space-y-2 mb-3">
              {(resume.presentations || []).map((presentation, i) => (
                <div key={i}>
                  <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${presentation.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={presentation.include !== '0'}
                      onChange={(e) => updatePresentation(i, { include: e.target.checked ? '1' : '0' })}
                      data-testid={`include-presentations-${i}`}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                      title={presentation.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    {editingPresentationIndex === i ? (
                      <div className="flex-1 space-y-1">
                        <div className="flex gap-2 items-center">
                          <input type="text" value={presentation.title} onChange={(e) => updatePresentation(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" />
                          <input type="text" value={presentation.event || ''} onChange={(e) => updatePresentation(i, { event: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Event/Location" />
                          <input type="text" value={presentation.url || ''} onChange={(e) => updatePresentation(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                          <input type="text" value={presentation.year || ''} onChange={(e) => updatePresentation(i, { year: e.target.value })} className={`w-24 ${dateInputClass(dateFieldKey('presentations', i, 'year'))}`} data-testid={`date-presentations-${i}-year`} placeholder="Year" />
                        </div>
                        <input
                          key={(presentation.tags || []).join(',')}
                          type="text"
                          defaultValue={(presentation.tags || []).join(', ')}
                          onBlur={(e) => updatePresentation(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updatePresentation(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                          placeholder="Tags (comma-separated)"
                        />
                        {renderTagStatusChip(presentation.tags, presentation.title)}
                      </div>
                    ) : (
                      <div className="flex-1">
                        <span className="text-sm font-medium">{presentation.title}</span>
                        {presentation.event && <span className="text-sm text-gray-500"> — {presentation.event}</span>}
                        {presentation.year && <span className="text-xs text-gray-500 ml-2">({presentation.year})</span>}
                        <span className="ml-2">{renderTagStatusChip(presentation.tags, presentation.title)}</span>
                      </div>
                    )}
                    <button onClick={() => setEditingPresentationIndex(editingPresentationIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => movePresentation(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => movePresentation(i, 'down')} disabled={i === (resume.presentations || []).length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => removePresentation(i)} className="text-gray-500 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {renderInlineRecs(`presentations[${i}]`)}
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Title</label>
                <input type="text" value={newPresentationTitle} onChange={(e) => setNewPresentationTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Presentation or Keynote Title" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Event/Location</label>
                <input type="text" value={newPresentationEvent} onChange={(e) => setNewPresentationEvent(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Event name or location" />
              </div>
              <div className="w-24">
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <input type="text" value={newPresentationYear} onChange={(e) => setNewPresentationYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPresentation()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2023" />
              </div>
              <button onClick={addPresentation} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            </>)}
          </div>
      </>
    );
  }

  function renderPublicationsSection() {
    return (
      <>
          {/* Publications */}
          <div data-help-target="mainapp.resumes.resumeN.publications" className={sectionGenTypes.publications === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('publications', 'mb-2')}
            {isSectionOpen('publications') && (<>
            <div className="space-y-2 mb-3">
              {(resume.publications || []).map((pub, i) => (
                <div key={i}>
                  <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${pub.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={pub.include !== '0'}
                      onChange={(e) => updatePublication(i, { include: e.target.checked ? '1' : '0' })}
                      data-testid={`include-publications-${i}`}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                      title={pub.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    {editingPublicationIndex === i ? (
                      <div className="flex-1 space-y-1">
                        <div className="flex gap-2 items-center">
                          <input type="text" value={pub.title} onChange={(e) => updatePublication(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" />
                          <input type="text" value={pub.url || ''} onChange={(e) => updatePublication(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                          <input type="text" value={pub.year || ''} onChange={(e) => updatePublication(i, { year: e.target.value })} className={`w-24 ${dateInputClass(dateFieldKey('publications', i, 'year'))}`} data-testid={`date-publications-${i}-year`} placeholder="Year" />
                        </div>
                        <input
                          key={(pub.tags || []).join(',')}
                          type="text"
                          defaultValue={(pub.tags || []).join(', ')}
                          onBlur={(e) => updatePublication(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updatePublication(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                          placeholder="Tags (comma-separated)"
                        />
                        {renderTagStatusChip(pub.tags, pub.title)}
                      </div>
                    ) : (
                      <div className="flex-1">
                        {pub.url ? (
                          <a href={pub.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary-600 hover:underline">{pub.title}</a>
                        ) : (
                          <span className="text-sm font-medium">{pub.title}</span>
                        )}
                        {pub.year && <span className="text-xs text-gray-500 ml-2">({pub.year})</span>}
                        <span className="ml-2">{renderTagStatusChip(pub.tags, pub.title)}</span>
                      </div>
                    )}
                    <button onClick={() => setEditingPublicationIndex(editingPublicationIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => movePublication(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => movePublication(i, 'down')} disabled={i === (resume.publications || []).length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => removePublication(i)} className="text-gray-500 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {renderInlineRecs(`publications[${i}]`)}
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Title</label>
                <input type="text" value={newPubTitle} onChange={(e) => setNewPubTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Publication Title" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">URL (optional)</label>
                <input type="text" value={newPubUrl} onChange={(e) => setNewPubUrl(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://example.com/article" />
              </div>
              <div className="w-24">
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <input type="text" value={newPubYear} onChange={(e) => setNewPubYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPublication()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2023" />
              </div>
              <button onClick={addPublication} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            </>)}
          </div>
      </>
    );
  }

  function renderPortfoliosSection() {
    return (
      <>
          {/* Portfolios */}
          <div data-help-target="mainapp.resumes.resumeN.portfolios" className={sectionGenTypes.portfolios === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('portfolios', 'mb-2')}
            {isSectionOpen('portfolios') && (<>
            <div className="space-y-2 mb-3">
              {(resume.portfolios || []).map((port, i) => (
                <div key={i}>
                  <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${port.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={port.include !== '0'}
                      onChange={(e) => updatePortfolio(i, { include: e.target.checked ? '1' : '0' })}
                      data-testid={`include-portfolios-${i}`}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                      title={port.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    {editingPortfolioIndex === i ? (
                      <div className="flex-1 space-y-1">
                        <div className="flex gap-2 items-center">
                          <input type="text" value={port.title} onChange={(e) => updatePortfolio(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" />
                          <input type="text" value={port.url || ''} onChange={(e) => updatePortfolio(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                          <input type="text" value={port.year || ''} onChange={(e) => updatePortfolio(i, { year: e.target.value })} className={`w-24 ${dateInputClass(dateFieldKey('portfolios', i, 'year'))}`} data-testid={`date-portfolios-${i}-year`} placeholder="Year" />
                        </div>
                        <input
                          key={(port.tags || []).join(',')}
                          type="text"
                          defaultValue={(port.tags || []).join(', ')}
                          onBlur={(e) => updatePortfolio(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updatePortfolio(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                          placeholder="Tags (comma-separated)"
                        />
                        {renderTagStatusChip(port.tags, port.title)}
                      </div>
                    ) : (
                      <div className="flex-1">
                        {port.url ? (
                          <a href={port.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary-600 hover:underline">{port.title}</a>
                        ) : (
                          <span className="text-sm font-medium">{port.title}</span>
                        )}
                        {port.year && <span className="text-xs text-gray-500 ml-2">({port.year})</span>}
                        <span className="ml-2">{renderTagStatusChip(port.tags, port.title)}</span>
                      </div>
                    )}
                    <button onClick={() => setEditingPortfolioIndex(editingPortfolioIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => movePortfolio(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => movePortfolio(i, 'down')} disabled={i === (resume.portfolios || []).length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => removePortfolio(i)} className="text-gray-500 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {renderInlineRecs(`portfolios[${i}]`)}
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Title</label>
                <input type="text" value={newPortTitle} onChange={(e) => setNewPortTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Portfolio Title" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">URL (optional)</label>
                <input type="text" value={newPortUrl} onChange={(e) => setNewPortUrl(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://example.com/portfolio" />
              </div>
              <div className="w-24">
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <input type="text" value={newPortYear} onChange={(e) => setNewPortYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPortfolio()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2023" />
              </div>
              <button onClick={addPortfolio} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            </>)}
          </div>
      </>
    );
  }

  function renderSection(component: SubcomponentType) {
    switch (component) {
      case 'summary': return renderSummarySection();
      case 'highlights': return renderHighlightsSection();
      case 'skills': return renderSkillsSection();
      case 'experience': return renderExperienceSection();
      case 'education': return renderEducationSection();
      case 'awards': return renderAwardsSection();
      case 'presentations': return renderPresentationsSection();
      case 'publications': return renderPublicationsSection();
      case 'portfolios': return renderPortfoliosSection();
      default: return null;
    }
  }

  if (!isOpen) return null;


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-label={resumename ? 'Edit Resume' : 'Create Resume'} data-help-target="mainapp.resumes.resumeN">
      {/* Lightbox width: widened 1232 -> 1360 (WQ 92e68bf0) so the experience
          bullet rows (fixed-width RESUME_BULLET_WIDTH control + tags input +
          the move/delete icon cluster) have comfortable room on desktop.
          `w-full` still collapses it to the viewport on narrower screens, and
          the bullet rows are ALSO structurally shrink-safe (min-w-0 +
          fitContainer + flex-shrink-0), so containment no longer depends on
          this max-width. */}
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-[1360px] max-h-[90vh] overflow-hidden flex flex-col">
        {/* ── Top bar: title + every resume-level action (mockup 20). ── */}
        {/* flex-wrap: the action cluster is ~465px of buttons, so on a phone it
            overflowed the shell and gave the whole editor a horizontal scroll.
            Wrapping is inert at desktop widths (it all fits on one line) and
            costs no functionality — every action stays present and reachable. */}
        <header className="flex-shrink-0 min-h-[60px] flex flex-wrap items-center gap-2 pl-5 pr-3 py-2 bg-white border-b border-gray-200">
          {/* Eyebrow + inline-editable resume name (the required Resume Name). */}
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="hidden lg:inline text-[11px] font-semibold uppercase tracking-[0.07em] text-gray-500 whitespace-nowrap">
              {resumename ? 'Edit resume' : 'New resume'}
            </span>
            {editingName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={commitName}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitName(); }
                  if (e.key === 'Escape') { cancelNameEditRef.current = true; setEditingName(false); }
                }}
                data-help-target="mainapp.resumes.resumeN.resumename"
                data-testid="resume-name-input"
                className="w-[212px] max-w-[36vw] px-2.5 py-1.5 rounded-lg border border-primary-500 bg-white text-[15px] font-semibold tracking-[-0.01em] text-gray-900 outline-none ring-[3px] ring-primary-100"
                placeholder="My Professional Resume"
                aria-label="Resume name (required)"
                autoComplete="off"
                autoFocus
              />
            ) : (
              <button
                type="button"
                onClick={startEditName}
                disabled={importing || loading}
                data-help-target="mainapp.resumes.resumeN.resumename"
                data-testid="resume-name-inline"
                className="group flex items-center gap-1.5 min-w-0 px-2 py-1 -ml-2 rounded-lg border border-transparent hover:bg-gray-50 hover:border-gray-200 disabled:opacity-60 transition-colors"
                title="Resume name (required) — an internal label to help you tell your resumes apart; it is never printed on the resume."
              >
                <Edit2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" aria-hidden="true" />
                {loading && !name.trim() ? (
                  /* Existing resume still loading — a quiet skeleton instead of a
                     misleading "Name this resume" flash (create mode never sets
                     `loading`, so the placeholder still shows instantly there). */
                  <span className="inline-block w-40 h-[15px] rounded bg-gray-100 animate-pulse" aria-hidden="true" />
                ) : (
                  <span className={`truncate text-[15px] font-semibold tracking-[-0.01em] ${name.trim() ? 'text-gray-900' : 'text-amber-700'}`}>
                    {name.trim() || 'Name this resume'}
                  </span>
                )}
              </button>
            )}
          </div>

          <span className="flex-1 min-w-[8px]" />

          <div className="flex flex-wrap items-center justify-end gap-[5px] min-w-0">
            {(() => {
              const hasResumeData = !!(resume.name || resume.skills.length > 0 || resume.summary || getExperience().length > 0);
              const busy = importing || loading || exporting !== null;
              return (
                <>
                  {/* Import ▾ — Load from PDF / Load from file */}
                  <div ref={importMenuRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setImportMenuOpen(o => !o)}
                      disabled={busy}
                      data-help-target="mainapp.resumes.resumeN.import"
                      className={TB_OUT}
                      title="Import resume data (PDF, YAML or JSON)"
                      aria-haspopup="menu"
                      aria-expanded={importMenuOpen}
                    >
                      <Upload className="w-[15px] h-[15px]" />
                      <span className="hidden xl:inline">Import</span>
                      <ChevronDown className={`w-[13px] h-[13px] text-gray-400 transition-transform ${importMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {importMenuOpen && (
                      <div role="menu" className={TB_MENU}>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setImportMenuOpen(false); handleLoadFromFileClick(); }}
                          data-help-target="mainapp.resumes.resumeN.loadfrompdf"
                          className={TB_MENU_ITEM}
                        >
                          <FileText className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
                          <span>
                            <span className="block font-medium">Load from PDF</span>
                            <span className="block text-[11.5px] text-gray-500">Parse an existing resume PDF</span>
                          </span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setImportMenuOpen(false); handleLoadFromFileClick(); }}
                          data-help-target="mainapp.resumes.resumeN.import.fromfile"
                          className={TB_MENU_ITEM}
                        >
                          <Save className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
                          <span>
                            <span className="block font-medium">Load from file</span>
                            <span className="block text-[11.5px] text-gray-500">YAML or JSON resume data</span>
                          </span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Export ▾ — PDF / Text File / Data File */}
                  <div ref={exportMenuRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setExportMenuOpen(o => !o)}
                      disabled={busy}
                      data-help-target="mainapp.resumes.resumeN.export"
                      className={TB_OUT}
                      title="Export this resume (PDF, Text File, or Data File)"
                      aria-haspopup="menu"
                      aria-expanded={exportMenuOpen}
                    >
                      {exporting !== null ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <Download className="w-[15px] h-[15px]" />}
                      <span className="hidden xl:inline">Export</span>
                      <ChevronDown className={`w-[13px] h-[13px] text-gray-400 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {exportMenuOpen && (
                      <div role="menu" className={TB_MENU}>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setExportMenuOpen(false); handleExportFull('pdf'); }}
                          disabled={!hasResumeData || exporting !== null}
                          data-help-target="mainapp.resumes.resumeN.export.pdf"
                          className={TB_MENU_ITEM}
                          title="Export the full resume (all sections, all items) as a PDF"
                        >
                          {exporting === 'pdf' ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <FileText className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />}
                          <span>
                            <span className="block font-medium">PDF</span>
                            <span className="block text-[11.5px] text-gray-500">Every section, nothing trimmed</span>
                          </span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setExportMenuOpen(false); handleExportFull('text'); }}
                          disabled={!hasResumeData || exporting !== null}
                          data-help-target="mainapp.resumes.resumeN.export.text"
                          className={TB_MENU_ITEM}
                          title="Export the full resume (all sections, all items) as a plain-text file"
                        >
                          {exporting === 'text' ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <FileType className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />}
                          <span>
                            <span className="block font-medium">Text File</span>
                            <span className="block text-[11.5px] text-gray-500">Plain text for ATS paste-in</span>
                          </span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setExportMenuOpen(false); handleDownloadTemplate(); }}
                          data-help-target="mainapp.resumes.resumeN.export.datafile"
                          className={TB_MENU_ITEM}
                          title={hasResumeData ? 'Export resume data as a YAML datafile' : 'Download a blank YAML template'}
                        >
                          <Download className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
                          <span>
                            <span className="block font-medium">{hasResumeData ? 'Data File' : 'Data File Template'}</span>
                            <span className="block text-[11.5px] text-gray-500">YAML backup of this resume</span>
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}

            {resumename && (
              <>
                <span className={TB_VR} aria-hidden="true" />

                {/* Review resume — runs the AI review (was a banner-row button). */}
                <button
                  type="button"
                  onClick={handleAIReview}
                  disabled={reviewing || importing || loading || saving}
                  data-help-target="mainapp.resumes.resumeN.aireview"
                  data-testid="review-resume"
                  className={TB_PRIMARY}
                  title={
                    reviewing
                      ? 'Reviewing…'
                      : (reviewComplete || hasReviewedBefore)
                        ? 'Review complete — click to run the AI review again'
                        : 'Review Resume and Generate Recommendations'
                  }
                >
                  {reviewing
                    ? <Loader2 className="w-[15px] h-[15px] animate-spin" />
                    : (reviewComplete || hasReviewedBefore)
                      ? <Check className="w-[15px] h-[15px]" />
                      : <Sparkles className="w-[15px] h-[15px]" />}
                  <span>{reviewing ? 'Reviewing…' : 'Review resume'}</span>
                </button>

                {/* Scorecard NN — opens the (read-only) scorecard modal. */}
                <button
                  type="button"
                  onClick={() => setShowScorecard(true)}
                  disabled={importing || loading}
                  data-help-target="mainapp.resumes.resumeN.scorecard"
                  data-testid="scorecard-chip"
                  className={TB_GHOST}
                  title={scorecardLint.computing
                    ? 'Calculating your resume score (checking spelling and grammar)…'
                    : 'View Resume Scorecard — a deterministic read of how your resume comes across, including an Authenticity (human-voice) score'}
                >
                  <Gauge className="w-[15px] h-[15px]" />
                  <span className="hidden xl:inline">Scorecard</span>
                  <span
                    data-testid="scorecard-chip-score"
                    data-computing={scorecardLint.computing ? '1' : '0'}
                    // While the FIRST (Accuracy) lint pass is in flight the
                    // number isn't knowable, so no number is shown at all — a
                    // neutral, pulsing placeholder holds the chip's place until
                    // the real score lands (a soon-to-drop 84 reads as a bug).
                    className={`inline-flex items-center justify-center min-w-[26px] h-[19px] px-1.5 rounded-full border text-[11px] font-bold tabular-nums ${
                      scorecardLint.computing
                        ? 'animate-pulse opacity-60 bg-gray-50 border-gray-200 text-gray-400'
                        : scorecardScore >= 80
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : scorecardScore >= 60
                            ? 'bg-amber-50 border-amber-200 text-amber-700'
                            : 'bg-red-50 border-red-200 text-red-700'
                    }`}
                  >
                    {overallScoreLabel(scorecardScore, scorecardLint.computing)}
                  </span>
                </button>
              </>
            )}

            <span className={TB_VR} aria-hidden="true" />

            {/* Live-preview toggle. */}
            <button
              type="button"
              onClick={() => setPreviewOpen(o => !o)}
              aria-pressed={previewOpen}
              data-help-target="mainapp.resumes.resumeN.preview"
              data-testid="preview-toggle"
              className={`hidden md:inline-flex ${TB_OUT} ${previewOpen ? 'bg-primary-50 border-primary-200 text-primary-700 hover:bg-primary-50' : ''}`}
              title={previewOpen ? 'Hide live preview' : 'Show live preview'}
            >
              <Eye className="w-[15px] h-[15px]" />
              <span>Preview</span>
            </button>

            {/* Resume ▾ — View HTML / View PDF / Clone / Delete. */}
            {resumename && (
              <div ref={resumeMenuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setResumeMenuOpen(o => !o)}
                  disabled={saving || importing || deleting || cloning || exporting !== null}
                  data-help-target="mainapp.resumes.resumeN.resumemenu"
                  className={TB_OUT}
                  title="Resume actions — View, Clone, or Delete"
                  aria-haspopup="menu"
                  aria-expanded={resumeMenuOpen}
                >
                  {(cloning || exporting === 'pdf') ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <SlidersHorizontal className="w-[15px] h-[15px]" />}
                  <span className="hidden lg:inline">Resume</span>
                  <ChevronDown className={`w-[13px] h-[13px] text-gray-400 transition-transform ${resumeMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {resumeMenuOpen && (
                  <div role="menu" className={`${TB_MENU} min-w-[256px]`}>
                    <div className={TB_MENU_GROUP}>This resume</div>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setResumeMenuOpen(false);
                        if (user?.username) {
                          const url = masterResumeUrl(user.username);
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      disabled={!user?.username}
                      data-help-target="mainapp.resumes.resumeN.resumemenu.viewhtml"
                      className={TB_MENU_ITEM}
                    >
                      <ExternalLink className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
                      <span>View HTML</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setResumeMenuOpen(false); handleExportFull('pdf'); }}
                      disabled={exporting !== null}
                      data-help-target="mainapp.resumes.resumeN.resumemenu.viewpdf"
                      className={TB_MENU_ITEM}
                    >
                      {exporting === 'pdf' ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <FileText className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />}
                      <span>View PDF</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setResumeMenuOpen(false); handleClone(); }}
                      disabled={cloning || saving || importing || loading}
                      data-help-target="mainapp.resumes.resumeN.resumemenu.clone"
                      className={TB_MENU_ITEM}
                    >
                      {cloning ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <Copy className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />}
                      <span>Clone Resume</span>
                    </button>
                    {onDeleteResume && (
                      <>
                        <div className="h-px bg-gray-200 my-[5px]" />
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setResumeMenuOpen(false); setShowDeleteConfirm(true); }}
                          disabled={saving || importing || deleting}
                          data-help-target="mainapp.resumes.resumeN.resumemenu.delete"
                          className={`${TB_MENU_ITEM} text-red-700 hover:bg-red-50`}
                        >
                          <Trash2 className="w-[15px] h-[15px] text-red-600 flex-shrink-0" />
                          <span>Delete Resume</span>
                        </button>
                      </>
                    )}

                    {/* Switch resumes — the user's other resumes, from the same
                        list the header selector uses. With only one resume the
                        whole group is omitted (no empty state); "New Resume"
                        below always shows. */}
                    {onSwitchResume && switchResumes === null && switchLoading && (
                      <div className="flex items-center gap-2.5 px-2.5 py-[7px] text-[12.5px] text-gray-500">
                        <Loader2 className="w-[14px] h-[14px] animate-spin text-gray-400" />
                        <span>Loading resumes…</span>
                      </div>
                    )}

                    {onSwitchResume && otherResumes.length > 0 && (
                      <>
                        <div className="h-px bg-gray-200 my-[5px]" />
                        <div className={TB_MENU_GROUP}>Switch resumes</div>
                        <div className="max-h-[240px] overflow-y-auto" data-help-target="mainapp.resumes.resumeN.resumemenu.switchresumes" data-testid="switch-resumes">
                          {resumename && (
                            <div
                              className={`${TB_MENU_ITEM} bg-primary-50 text-primary-700 hover:bg-primary-50 cursor-default`}
                              data-help-target="mainapp.resumes.resumeN.resumemenu.switchresumes.current"
                              aria-current="true"
                            >
                              <FileText className="w-[15px] h-[15px] text-primary-600 flex-shrink-0" />
                              <span className="flex-1 min-w-0">
                                <span className="block truncate font-medium">{resumename}</span>
                                <span className="block text-[11.5px] text-primary-600/80">Currently editing</span>
                              </span>
                              <Check className="w-[14px] h-[14px] text-primary-600 flex-shrink-0" />
                            </div>
                          )}
                          {otherResumes.map((r, idx) => {
                            const meta = editedAgoLabel(r.lastupdate);
                            return (
                              <button
                                key={r.resumename}
                                type="button"
                                role="menuitem"
                                onClick={() => requestNav({ kind: 'switch', resumename: r.resumename })}
                                disabled={saving || importing || deleting || loading}
                                data-help-target={idx === 0 ? 'mainapp.resumes.resumeN.resumemenu.switchresumes.resume0' : undefined}
                                className={TB_MENU_ITEM}
                                title={`Switch the editor to "${r.resumename}"`}
                              >
                                <FileText className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
                                <span className="flex-1 min-w-0">
                                  <span className="block truncate">{r.resumename}</span>
                                  {meta && <span className="block text-[11.5px] text-gray-500">{meta}</span>}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {onNewResume && (
                      <>
                        <div className="h-px bg-gray-200 my-[5px]" />
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => requestNav({ kind: 'new' })}
                          disabled={saving || importing || deleting}
                          className={TB_MENU_ITEM}
                          data-help-target="mainapp.resumes.resumeN.resumemenu.newresume"
                          data-testid="new-resume-item"
                          title="Create a new resume"
                        >
                          <Plus className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
                          <span>New Resume</span>
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={handleClose}
              disabled={importing}
              data-help-target="mainapp.resumes.resumeN.close"
              className="w-[34px] h-[34px] grid place-items-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Import Progress Overlay */}
        {importing && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center z-10" role="status" aria-label="Analyzing resume file">
            <Loader2 className="w-12 h-12 animate-spin text-primary-600 mb-4" />
            <p className="text-gray-700 font-medium">Analyzing resume file...</p>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".yaml,.yml,.json,.pdf"
          onChange={handleFileSelect}
          className="hidden"
        />

        {loading ? (
          <div className="flex items-center justify-center py-12" role="status" aria-label="Loading resume">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="relative flex-1 flex min-h-0">
          {/* Left rail: section nav (bands + drag reorder). */}
          <SectionNavPanel
            orderedSections={orderedSections}
            getLabel={sectionLabel}
            genTypes={sectionGenTypes}
            getState={navRowState}
            attentionCounts={navAttentionCounts}
            recommendationCount={pendingRecs.length}
            recommendationSections={getRecSectionCounts()}
            onShowRecommendations={scrollToRecommendations}
            onReorder={(next) => { setSectionOrder(['contact', ...next]); touchSectionSettings(); }}
            onMove={moveSection}
            onCycleGenType={cycleSectionGenType}
            activeKey={activeNavKey}
            onNavigate={scrollToNavKey}
            progress={navProgress}
            score={scorecardScore}
            scoreComputing={scorecardLint.computing}
            onShowScorecard={resumename ? () => setShowScorecard(true) : undefined}
            storageKey={resumename || '__new__'}
          />

          <div ref={scrollRef} className="flex-1 min-w-0 overflow-y-auto p-4 space-y-4">
            {tagGenPhase === 'generating' ? (
              <div className="p-3 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg text-sm flex items-start gap-2">
                <Loader2 className="w-4 h-4 mt-0.5 flex-shrink-0 animate-spin" />
                <span>
                  Tag generation is running — this usually takes about 60 seconds.
                  Items being tagged are marked below; their tags will appear automatically when ready, and you can keep editing in the meantime.
                  <br />
                  <span className="text-yellow-700">Generated tags are AI-produced — please verify accuracy.</span>
                </span>
              </div>
            ) : tagGenPhase === 'done' ? (
              <div className="p-3 bg-green-50 text-green-800 border border-green-200 rounded-lg text-sm flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Tag generation complete — tags have been added to your resume.
                  <br />
                  <span className="text-green-700">Generated tags are AI-produced — please verify accuracy.</span>
                </span>
              </div>
            ) : tagGenPhase === 'timeout' ? (
              <div className="p-3 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg text-sm">
                Tag generation is taking longer than usual. Close and reopen this resume in a moment to see the tags.
              </div>
            ) : warning ? (
              <div className="p-3 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg text-sm whitespace-pre-line">{warning}</div>
            ) : null}
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm whitespace-pre-line" data-testid="editor-error">{error}</div>
            )}

            {/* Master-resume warning (Recruiter Check BR-1). Plain language,
                and it teaches the model users don't infer: this resume is the
                source every generated resume is cut from, so a problem here is
                a problem on every application. Click → the Scorecard, already
                filtered to those findings. */}
            {resumename && masterLintHigh > 0 && (
              <button
                type="button"
                onClick={() => { setScorecardFocusHigh(true); setShowScorecard(true); }}
                data-testid="master-lint-banner"
                data-count={String(masterLintHigh)}
                className="w-full flex items-start gap-2 p-3 text-left border border-amber-200 bg-amber-50 rounded-lg hover:bg-amber-100"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                <span className="text-sm text-amber-900">
                  {masterLintBannerText(masterLintHigh)}
                  <span className="block text-xs text-amber-700 mt-0.5 underline">
                    Show me what they are
                  </span>
                </span>
              </button>
            )}

            {/* AI Review status banners. The Review trigger and the Scorecard
                chip live in the TOP BAR; only the banners stay in the pane. */}
            {resumename && (
              <div className="space-y-2">
                {/* Pending recommendations banner */}
                {hasRecommendations && (
                  <div data-review-anchor className="border border-purple-200 bg-purple-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium text-purple-800">
                          {pendingRecs.length} pending recommendation{pendingRecs.length !== 1 ? 's' : ''}. Scroll down to review inline recommendations.
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={applyAllRecommendations}
                          className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          Apply All
                        </button>
                        <button
                          onClick={dismissAllRecommendations}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        >
                          Dismiss All
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {getRecSectionCounts().map(({ section, count }) => (
                        <span key={section} className="text-xs bg-white border border-purple-200 text-purple-700 px-2 py-0.5 rounded">
                          {section}: {count}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs mt-2 text-purple-600">
                      Remember: this resume will be customized per job, based on the job description. So while it&apos;s important to get a solid foundation, further AI customization is applied on top of this &quot;core&quot; resume.
                    </p>
                  </div>
                )}

                {/* "All implemented" banner — shown once, then suppressed on reopen */}
                {showAllImplemented && (
                  <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        All recommendations have been implemented!
                      </span>
                    </div>
                    <p className="text-xs mt-2 text-green-600">
                      Remember: this resume will be customized per job, based on the job description. So while it&apos;s important to get a solid foundation, further AI customization is applied on top of this &quot;core&quot; resume.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Name — the professional name printed at the top of every resume. */}
            <div data-nav-key="basics" data-help-target="mainapp.resumes.resumeN.basics" className={SECTION_CARD}>
              {renderCardHeader('basics', 'Name')}
              {isSectionOpen('basics') && (
                <div>
                  {/* Field label sits a tier below the card heading above it,
                      matching the URL field's label weight. */}
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Full Professional Name *
                  </label>
                  <input
                    type="text"
                    value={resume.name}
                    onChange={(e) => setResume({
                      ...resume,
                      name: e.target.value
                    })}
                    data-help-target="mainapp.resumes.resumeN.profname"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="John Doe"
                    autoComplete="off"
                  />
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div data-nav-key="contact" data-help-target="mainapp.resumes.resumeN.contact" className={SECTION_CARD}>
              <div className={sectionGenTypes.contact === 'omit' ? 'opacity-50' : ''}>
                {renderSectionHeader('contact', 'mb-2')}
                {isSectionOpen('contact') && (<>
                <div className="space-y-2 mb-3">
                  {resume.contact.map((item, i) => (
                    <div key={i}>
                      <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${item.include === '0' ? ' opacity-40' : ''}`}>
                        <input
                          type="checkbox"
                          checked={item.include !== '0'}
                          onChange={(e) => updateContactItem(i, { include: e.target.checked ? '1' : '0' })}
                          data-testid={`include-contact-${i}`}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                          title={item.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                        />
                        {editingContactIndex === i ? (
                          <div className="flex-1 flex gap-2 items-center">
                            <div className="relative w-20">
                              <select value={item.icon} onChange={(e) => updateContactItem(i, { icon: e.target.value as ContactItem['icon'] })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm appearance-none pl-7">
                                {sortedIconOptions.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                              </select>
                              <img src={getIconSvg(item.icon)} alt="" className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                            <input type="text" value={item.title} onFocus={() => setEditContactAutoFill(!item.url)} onChange={(e) => { const updates: Partial<ContactItem> = { title: e.target.value }; if (editContactAutoFill) { const auto = autoFillContactUrl(item.icon, e.target.value); if (auto) updates.url = auto; } updateContactItem(i, updates); }} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" autoComplete="off" />
                            <input type="text" value={item.url || ''} onChange={(e) => { setEditContactAutoFill(false); updateContactItem(i, { url: e.target.value }); }} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL" autoComplete="off" />
                          </div>
                        ) : (
                          <>
                            <img src={getIconSvg(item.icon)} alt="" className="w-4 h-4" />
                            {/* min-w-0: `truncate` cannot shrink a flex item whose
                                default min-width is auto, so a long unbreakable
                                contact URL pushed the whole row (and the editor's
                                scroll container) sideways instead of ellipsising. */}
                            <span className="flex-1 min-w-0 text-sm truncate">
                              {item.title}
                              {item.url && <span className="text-gray-500 ml-2 text-xs">({item.url})</span>}
                            </span>
                          </>
                        )}
                        <button onClick={() => { setEditingContactIndex(editingContactIndex === i ? null : i); setEditContactAutoFill(false); }} className="text-gray-500 hover:text-gray-600">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => moveContactItem(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button onClick={() => moveContactItem(i, 'down')} disabled={i === resume.contact.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button onClick={() => removeContactItem(i)} className="text-gray-500 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {renderInlineRecs(`contacts[${i}]`)}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 items-end">
                  <div className="w-20">
                    <label className="block text-xs text-gray-500 mb-1">Icon</label>
                    <div className="relative">
                      <select
                        value={newContactIcon}
                        onChange={(e) => setNewContactIcon(e.target.value as ContactItem['icon'])}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm appearance-none pl-8"
                      >
                        {sortedIconOptions.map(i => (
                          <option key={i.value} value={i.value}>{i.label}</option>
                        ))}
                      </select>
                      <img src={getIconSvg(newContactIcon)} alt="" className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Title</label>
                    <input type="text" value={newContactTitle} onFocus={() => setNewContactAutoFill(!newContactUrl)} onChange={(e) => { setNewContactTitle(e.target.value); if (newContactAutoFill) { const auto = autoFillContactUrl(newContactIcon, e.target.value); if (auto) setNewContactUrl(auto); } }} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="name displayed" autoComplete="off" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">URL / Link</label>
                    <input type="text" value={newContactUrl} onChange={(e) => { setNewContactAutoFill(false); setNewContactUrl(e.target.value); }} onKeyDown={(e) => e.key === 'Enter' && addContactItem()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder={getUrlPlaceholder(newContactIcon)} autoComplete="off" />
                  </div>
                  <button onClick={addContactItem} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                </>)}
              </div>
            </div>

            {/* Reorderable sections — order comes from the per-resume
                section settings draft (saved with the resume). */}
            {orderedSections.map((component) => (
              <div key={component} data-nav-key={component} className={SECTION_CARD}>
                {renderSection(component)}
              </div>
            ))}

            {/* Cover Letter — pinned last (it isn't a rendered resume section,
                so it has no content editor, no reorder arrows and no custom
                name; only the generation type is set here). */}
            <div data-nav-key="coverletter" data-help-target="mainapp.resumes.resumeN.coverletter" className={SECTION_CARD}>
              <div className={sectionGenTypes.coverletter === 'omit' ? 'opacity-50' : ''}>
                {renderSectionHeader('coverletter', 'mb-1', { editableTitle: false })}
                {isSectionOpen('coverletter') && (
                <p className="text-xs text-gray-500 mb-2">
                  {sectionGenTypes.coverletter === 'omit'
                    ? 'Cover letters will not be generated for jobs using this resume.'
                    : 'Cover letters are generated per job from this resume.'}
                </p>
                )}
              </div>
            </div>

            {/* Everything above prints on the resume; everything below is setup and
                reference material, so a quiet rule separates the two groups. */}
            <hr data-testid="editor-section-divider" className="border-t border-gray-200 my-1" />

            {/* Resume URLs. Subsection one is the account's ONE generic URL (the
                naked root) — published on demand, and only ever showing one of
                the user's resumes; below it, the role-targeted resumes. */}
            <div data-nav-key="resumeurls" data-help-target="mainapp.resumes.resumeN.resumeurls" className={SECTION_CARD}>
              {renderCardHeader('resumeurls', 'Resume URLs')}
              {isSectionOpen('resumeurls') && (
                <div className="space-y-2">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Generic Resume URL
                    </label>
                    {/* One URL per account — no path to choose. Whichever of
                        your resumes was published last is the one it shows. */}
                    <div className="flex items-center gap-0">
                      <span
                        className="flex-1 min-w-0 inline-flex items-center px-3 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-600 text-sm select-all break-all"
                        data-help-target="mainapp.resumes.resumeN.masterurl"
                        data-uat="generic-resume-url"
                      >
                        https://{user?.username?.toLowerCase() || 'your_name'}.skillsnap.me/
                      </span>
                      {resumename && user?.username && (
                        <MasterUrlViewMenu
                          url={masterResumeUrl(user.username)}
                          uat="master-view"
                        />
                      )}
                    </div>
                    {!resumename ? (
                      <p className="text-xs text-gray-500 italic">
                        Save this resume once before you can publish it to that URL.
                      </p>
                    ) : publishMasterResume ? (
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium border border-green-200"
                          data-uat="generic-published-badge"
                        >
                          <Check className="w-3 h-3" />
                          Published
                        </span>
                        <span className="text-xs text-gray-500">
                          This is the resume shown at that URL.
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={handlePublishNow}
                          disabled={publishing || saving}
                          data-uat="generic-publish-now"
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Make this the resume shown at your public URL — most recent publish wins"
                        >
                          {publishing
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Upload className="w-4 h-4" />}
                          <span>Publish now</span>
                        </button>
                        <span className="text-xs text-gray-500">
                          {publishedElsewhere
                            ? <>Currently showing &ldquo;{publishedElsewhere}&rdquo;.</>
                            : publishedElsewhere === ''
                              ? 'Nothing is published there yet.'
                              : null}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Role-targeted resumes — second subsection of this card. The
                      label shows even with no rows yet: the Add button sits under it. */}
                  <label className="block text-sm font-medium text-gray-700 pt-1">
                    Role-Targeted URLs
                  </label>
                  {dedicatedMasters.map((dm) => {
                    const working = dmIsActive(dm);
                    const failed = dm.state === 'error';
                    const busy = dmBusyId === dm.id;
                    const keywordsEditable = dedicatedMasterAllowed && !working && !busy;
                    return (
                      <div
                        key={dm.id}
                        data-uat="dedicated-master-row"
                        className={`space-y-1 rounded-lg p-2 ${
                          working
                            ? 'border border-amber-400 bg-amber-50'
                            : failed
                              ? 'border border-red-300 bg-red-50'
                              : 'border border-transparent'
                        }`}
                      >
                        <div className="flex items-center gap-0">
                          <span className="inline-flex items-center px-3 py-2 rounded-l-lg border border-r-0 border-gray-300 bg-gray-100 text-gray-500 text-sm select-all">
                            https://{user?.username?.toLowerCase() || 'your_name'}.skillsnap.me/
                          </span>
                          <input
                            type="text"
                            value={dm.path}
                            readOnly
                            aria-label={`Role-targeted resume path ${dm.path}`}
                            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-r-lg text-sm bg-gray-50 text-gray-600"
                          />
                          {dm.state === 'ready' && user?.username && (
                            <MasterUrlViewMenu
                              url={masterResumeUrl(user.username, dm.path)}
                              uat="dm-view"
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => handleRegenerateDedicatedMaster(dm)}
                            disabled={working || busy || !dedicatedMasterAllowed}
                            className="ml-2 p-2 text-gray-500 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={
                              !dedicatedMasterAllowed
                                ? 'Role-targeted resumes are available on Active accounts.'
                                : working
                                  ? 'Already generating'
                                  : `Rebuild /${dm.path} from a fresh job search`
                            }
                            aria-label={`Regenerate ${dm.path}`}
                          >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveDedicatedMaster(dm)}
                            disabled={busy || !dedicatedMasterAllowed}
                            className="p-2 text-gray-500 hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={
                              !dedicatedMasterAllowed
                                ? 'Role-targeted resumes are available on Active accounts.'
                                : `Remove /${dm.path}`
                            }
                            aria-label={`Remove ${dm.path}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {working && (
                          <p className="text-xs text-amber-700">
                            {dm.state === 'searching'
                              ? 'Searching for matching jobs…'
                              : 'Generating this resume — it may take a minute or two.'}
                            {typeof dm.matched_jobs === 'number' && dm.matched_jobs > 0
                              && ` (${dm.matched_jobs} matching jobs)`}
                          </p>
                        )}
                        {failed && (
                          <p className="text-xs text-red-600">
                            {dm.error || 'This resume could not be generated. Try regenerating it.'}
                          </p>
                        )}
                        {editingDmKeywordsId === dm.id ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500 shrink-0">Targeted at:</span>
                            <input
                              // key resets the uncontrolled input whenever the
                              // stored keywords change (same trick as the skill
                              // tags editor).
                              key={dm.keywords}
                              type="text"
                              defaultValue={dm.keywords || ''}
                              autoFocus
                              autoComplete="off"
                              aria-label={`Targeting criteria for ${dm.path}`}
                              data-uat="dm-keywords-input"
                              onBlur={(e) => handleDedicatedMasterKeywords(dm, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleDedicatedMasterKeywords(dm, e.currentTarget.value);
                                } else if (e.key === 'Escape') {
                                  e.currentTarget.value = dm.keywords || '';
                                  editingDmKeywordsRef.current = null;
                                  setEditingDmKeywordsId(null);
                                }
                              }}
                              className="flex-1 min-w-0 px-1 py-0 border border-gray-100 rounded text-xs text-gray-500"
                              placeholder="job title or keywords (comma-separated)"
                            />
                          </div>
                        ) : (
                          <p
                            className={`text-xs text-gray-500${keywordsEditable ? ' cursor-pointer' : ''}`}
                            onClick={keywordsEditable ? () => {
                              setDmError(null);
                              editingDmKeywordsRef.current = dm.id;
                              setEditingDmKeywordsId(dm.id);
                            } : undefined}
                            title={keywordsEditable ? 'Edit targeting criteria' : undefined}
                            data-uat="dm-keywords"
                          >
                            Targeted at: {dm.keywords}
                          </p>
                        )}
                        {dmKeywordsEdited.has(dm.id) && (
                          <p className="text-xs text-amber-700" data-uat="dm-keywords-rebuild-hint">
                            Criteria updated &mdash; rebuild to apply them.
                          </p>
                        )}
                      </div>
                    );
                  })}

                  {dmError && <p className="text-xs text-red-600">{dmError}</p>}

                  {/* Wrapper carries the hover state: disabled buttons swallow
                      mouse events, so the tooltip hangs off this span. */}
                  <span className="relative group inline-flex">
                    <button
                      type="button"
                      onClick={() => { setDmError(null); setShowDedicatedMasterModal(true); }}
                      disabled={!resumename || !dedicatedMasterAllowed}
                      data-uat="dedicated-master-add"
                      data-lpignore="true"
                      data-form-type="other"
                      data-1p-ignore="true"
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                        !resumename || !dedicatedMasterAllowed
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title={
                        !dedicatedMasterAllowed
                          ? undefined // custom delayed tooltip below
                          : !resumename
                            ? 'Save this resume once before adding another resume URL'
                            : 'Add a resume aimed at a type of role'
                      }
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add a role-targeted resume</span>
                    </button>
                    {!dedicatedMasterAllowed && (
                      <span
                        role="tooltip"
                        data-uat="dedicated-master-add-tooltip"
                        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-lg bg-gray-900 text-white text-xs leading-relaxed px-3 py-2 shadow-lg z-50 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-hover:delay-500"
                      >
                        Role-targeted resumes are available on{' '}
                        <strong className="font-semibold">Active</strong> accounts. Your existing
                        resume URLs keep working.
                        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                      </span>
                    )}
                  </span>
                  <p className="text-xs text-gray-500">
                    A role-targeted resume is generated against all jobs matching a role
                    (e.g. &quot;solution engineer&quot;) rather than one posting &mdash; handy when a
                    headhunter asks for a resume for a type of role.
                  </p>
                  {/* Sets the expectation for the wait: this runs the same full
                      generation pipeline a bespoke resume does, on top of a job
                      search — so it is minutes, not seconds. */}
                  <p className="text-xs text-gray-500" data-testid="dm-processing-note">
                    Creating one takes a few minutes: SkillSnap finds real job postings that match
                    your role keywords and blends them into one representative role profile, then
                    generates a resume against it &mdash; scoring and selecting your best-fitting
                    content for each section before publishing the page and PDF.
                  </p>
                </div>
              )}
            </div>

            {/* Define Synonyms */}
            <div data-nav-key="synonyms" data-help-target="mainapp.resumes.resumeN.synonyms" className={SECTION_CARD}>
              {renderCardHeader('synonyms', 'Define Synonyms')}
              {isSectionOpen('synonyms') && (<>
              <p className="text-xs text-gray-500 mb-2">
                Optional. Each set are words or phrases that mean the same thing on your resume
                (e.g. &quot;Software Developer&quot; and &quot;Software Engineer&quot; and &quot;Programmer&quot;).
                When generating a bespoke resume, SkillSnap aligns your wording to whichever term the job description prefers.
                {' '}This only affects language in the Summary, Experience Bullets, and Skills sections.
              </p>
              <div className="space-y-2 mb-3">
                {(resume.synonyms || []).map((set, i) => {
                  const draftVal = synonymDrafts[set.id] ?? set.terms.join(' // ');
                  const termCount = parseSynonymTerms(draftVal).length;
                  const conflicted = conflictSetIds.has(set.id);
                  return (
                    <div key={set.id}>
                      <div className={`flex items-center gap-2 p-2 rounded ${conflicted ? 'bg-red-50 ring-2 ring-red-400' : 'bg-gray-50'}`}>
                        <input
                          type="text"
                          value={draftVal}
                          onChange={(e) => setSynonymDrafts(prev => ({ ...prev, [set.id]: e.target.value }))}
                          onBlur={() => commitSynonymDraft(set.id)}
                          onKeyDown={(e) => e.key === 'Enter' && commitSynonymDraft(set.id)}
                          className={`flex-1 px-2 py-1 border rounded text-sm ${conflicted ? 'border-red-300' : 'border-gray-300'}`}
                          placeholder="Software Developer // Software Engineer // Programmer"
                          autoComplete="off"
                        />
                        <button onClick={() => removeSynonymSet(set.id)} className="text-gray-500 hover:text-red-500" title="Remove synonym set">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {termCount === 1 && (
                        <div className="flex items-center gap-1 text-xs text-amber-600 mt-0.5 ml-1">
                          <AlertTriangle className="w-3 h-3" />
                          <span>A synonym set needs at least 2 terms (separate with &quot;//&quot;) — this set will be ignored.</span>
                        </div>
                      )}
                      {renderInlineRecs(`synonyms[${i}]`)}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">New synonym set (separate terms with &quot;//&quot;)</label>
                  <input
                    type="text"
                    value={newSynonymTerms}
                    onChange={(e) => setNewSynonymTerms(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addSynonymSet()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Software Developer // Software Engineer // Programmer"
                    autoComplete="off"
                  />
                </div>
                <button
                  onClick={addSynonymSet}
                  disabled={parseSynonymTerms(newSynonymTerms).length < 2}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40"
                  title="Add synonym set"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {renderInlineRecs('synonyms')}
              </>)}
            </div>

            {/* Provide Writing Sample (per-user voice fingerprint) */}
            <div data-nav-key="writingsample" data-help-target="mainapp.resumes.resumeN.writingsample" className={SECTION_CARD}>
              {renderCardHeader(
                'writingsample',
                'Writing Sample',
                <span className="font-normal text-gray-400">(Optional)</span>,
              )}
              {isSectionOpen('writingsample') && (<>
              <p className="text-xs text-gray-500 mb-2">
                Complete a short writing exercise and SkillSnap generates a &quot;fingerprint&quot; of your
                written voice — abstract traits like rhythm and punctuation to keep generated content
                sounding more like you.
              </p>
              {writingFingerprint ? (
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Done state: same button, grey, still opens the modal (redo). */}
                  <button
                    type="button"
                    onClick={() => setShowWritingModal(true)}
                    data-help-target="mainapp.resumes.resumeN.writingsample.open"
                    className="px-3 py-1.5 text-sm bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300"
                    title={writingFingerprint.created_at
                      ? `Fingerprint captured ${new Date(writingFingerprint.created_at).toLocaleDateString()} — click to redo`
                      : 'Fingerprint on file — click to redo'}
                  >
                    Complete Writing Sample - Done!
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteFingerprint}
                    disabled={deletingFingerprint}
                    data-help-target="mainapp.resumes.resumeN.writingsample.delete"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:text-red-600 hover:border-red-300 hover:bg-red-50 disabled:opacity-40"
                    title="Remove voice fingerprint"
                  >
                    {deletingFingerprint ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Delete Writing Sample
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowWritingModal(true)}
                  data-help-target="mainapp.resumes.resumeN.writingsample.open"
                  className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Complete Writing Sample
                </button>
              )}
              </>)}
            </div>

            {/* Implied Facts */}
            <div data-nav-key="impliedfacts" data-help-target="mainapp.resumes.resumeN.impliedfacts" className={SECTION_CARD}>
              <div className="flex items-center justify-between mb-1">
                {/* Accordion header — the list grows long, so it's collapsed
                    by default and expanded on demand (or via the left nav).
                    Kept bespoke (rather than renderCardHeader) for the fact
                    counts and the "Confirm all" action beside it; the OPEN
                    state is the shared per-card one. */}
                <button
                  type="button"
                  onClick={() => toggleSectionOpen('impliedfacts')}
                  aria-expanded={isSectionOpen('impliedfacts')}
                  className="flex items-center gap-1.5 text-base font-bold text-gray-700 hover:text-gray-900"
                  data-testid="section-toggle-impliedfacts"
                >
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isSectionOpen('impliedfacts') ? '' : '-rotate-90'}`} />
                  Implied Facts
                  {(() => {
                    const total = visibleFacts(resume.implied_facts).length;
                    const proposed = proposedFacts(resume.implied_facts).length;
                    if (!total) return null;
                    return (
                      <span className="text-xs font-normal text-gray-400">
                        {total} fact{total === 1 ? '' : 's'}{proposed ? ` · ${proposed} new` : ''}
                      </span>
                    );
                  })()}
                </button>
                {proposedFacts(resume.implied_facts).length > 0 && (
                  <button
                    onClick={confirmAllImpliedFacts}
                    className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200 flex items-center gap-1"
                    data-help-target="mainapp.resumes.resumeN.impliedfacts.confirmall"
                    title="Confirm every proposed fact"
                  >
                    <Check className="w-3 h-3" />
                    Confirm all
                  </button>
                )}
              </div>
              {isSectionOpen('impliedfacts') && (<>
              <p className="text-xs text-gray-500 mb-2">
                Things your resume implies but never says &mdash; how long you&rsquo;ve worked in a field,
                what your numbers add up to. Snappy works these out when you close this editor.
                {' '}Confirm the true ones and every tailored resume will stay consistent with them;
                edit or trash anything that isn&rsquo;t right &mdash; we won&rsquo;t ask about it again.
              </p>
              <div className="space-y-1 mb-2">
                {/* Dismissed facts are hidden but stay in the array, so the
                    recommendation path index below must be the STORED index. */}
                {factsOf(resume.implied_facts).map((f, i) => ({ f, i }))
                  .filter(({ f }) => f.status !== 'dismissed')
                  .map(({ f, i }) => {
                  const draftVal = factDrafts[f.id];
                  const isProposed = f.status === 'proposed';
                  return (
                    <div key={f.id}>
                      <div className={`flex items-center gap-2 px-2 py-1 rounded ${isProposed ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                        <input
                          type="text"
                          value={draftVal ?? f.fact}
                          onChange={(e) => setFactDrafts(prev => ({ ...prev, [f.id]: e.target.value }))}
                          onBlur={() => commitFactDraft(f.id)}
                          onKeyDown={(e) => e.key === 'Enter' && commitFactDraft(f.id)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                          autoComplete="off"
                          aria-label="Implied fact"
                        />
                        {isProposed ? (
                          <button
                            onClick={() => confirmImpliedFact(f.id)}
                            className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide rounded bg-amber-100 text-amber-700 hover:bg-amber-200 flex items-center gap-1 flex-shrink-0"
                            title="Confirm this fact as true"
                            data-testid="implied-fact-confirm"
                          >
                            <Check className="w-3 h-3" />
                            New &mdash; confirm
                          </button>
                        ) : (
                          <span className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide rounded bg-green-100 text-green-700 flex-shrink-0">
                            Confirmed
                          </span>
                        )}
                        <button
                          onClick={() => removeImpliedFact(f.id)}
                          className="text-gray-500 hover:text-red-500 flex-shrink-0"
                          title={f.source === 'user'
                            ? 'Delete this fact'
                            : "Not right — don't ask about this again"}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {renderInlineRecs(`implied_facts[${i}]`)}
                    </div>
                  );
                })}
                {visibleFacts(resume.implied_facts).length === 0 && (
                  <p className="text-xs text-gray-500 italic">
                    No facts yet. Close this editor and Snappy will look for some &mdash; or add your own below.
                  </p>
                )}
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Add a fact of your own</label>
                  <input
                    type="text"
                    value={newImpliedFact}
                    onChange={(e) => setNewImpliedFact(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addImpliedFact()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="15 years working in data engineering"
                    autoComplete="off"
                  />
                </div>
                <button
                  onClick={addImpliedFact}
                  disabled={!newImpliedFact.trim()}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40"
                  title="Add fact"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {renderInlineRecs('implied_facts')}
              </>)}
            </div>

            {/* Demographics — optional, never appears on the resume. Available in
                the Chrome Extension to accelerate applications. */}
            {(() => {
              const demo: Demographics = { ...emptyDemographics, ...(resume.demographics || {}) };
              const country = (demo.location_country || '').trim() || 'your country';
              // Renders a labeled dropdown; when "Other" is chosen, reveals a
              // free-text input bound to `${field}_other`.
              const renderSelect = (
                label: string,
                field: keyof Demographics,
                options: string[],
                withOther: boolean,
              ) => (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{label}</label>
                  <select
                    value={(demo[field] as string) || ''}
                    onChange={(e) => updateDemographics({ [field]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="">Select…</option>
                    {options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                  {withOther && demo[field] === 'Other' && (
                    <input
                      type="text"
                      value={(demo[`${field}_other` as keyof Demographics] as string) || ''}
                      onChange={(e) => updateDemographics({ [`${field}_other`]: e.target.value })}
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Please specify"
                    />
                  )}
                </div>
              );
              return (
                <div data-nav-key="demographics" data-help-target="mainapp.resumes.resumeN.demographics" className={SECTION_CARD}>
                  {renderCardHeader('demographics', 'Demographics')}
                  {isSectionOpen('demographics') && (<>
                  <p className="text-xs text-gray-500 mb-3">
                    Optional. Does not appear on your resume — available in the SkillSnap Chrome
                    Extension to accelerate your application process.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Location Country</label>
                      <input
                        type="text"
                        value={demo.location_country || ''}
                        onChange={(e) => updateDemographics({ location_country: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="USA"
                      />
                    </div>
                    {renderSelect(`Authorized to work in ${country}?`, 'authorized_to_work', YES_NO_OPTIONS, false)}
                    {renderSelect('Require Visa Sponsorship?', 'require_visa_sponsorship', YES_NO_OPTIONS, false)}
                    {renderSelect('Gender', 'gender', GENDER_OPTIONS, true)}
                    {renderSelect('Race', 'race', RACE_OPTIONS, true)}
                    {renderSelect('Veteran', 'veteran', VET_DIS_OPTIONS, true)}
                    {renderSelect('Disabled', 'disabled', VET_DIS_OPTIONS, true)}
                    {/* Last (and taller than the selects above, caption included) —
                        anywhere earlier it threw off the two-column row heights. */}
                    <div className="sm:col-span-2">
                      <label className="block text-xs text-gray-500 mb-1">Salary Expectation Floor (annual)</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={salaryFocused
                            ? (demo.salary_floor || '')
                            : formatSalaryFloor(demo.salary_floor || '', demo.salary_currency || 'USD')}
                          onFocus={() => setSalaryFocused(true)}
                          onBlur={() => setSalaryFocused(false)}
                          onChange={(e) => updateDemographics({ salary_floor: e.target.value.replace(/[^0-9]/g, '') })}
                          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="225000"
                        />
                        {/* w-auto: the native select sizes itself to the widest
                            option label, so nothing truncates. */}
                        <select
                          value={demo.salary_currency || 'USD'}
                          onChange={(e) => updateDemographics({ salary_currency: e.target.value })}
                          aria-label="Currency"
                          className="w-auto shrink-0 px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                        >
                          {CURRENCY_OPTIONS.map((c) => (
                            <option key={c.code} value={c.code}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      <p className="mt-1 text-xs text-gray-400">
                        Numbers only. Never shown on your resume — the extension uses it to suggest a
                        salary ask per job.
                      </p>
                    </div>
                  </div>
                  </>)}
                </div>
              );
            })()}


          </div>

          {/* Live preview slides out from the RIGHT, over the right edge of the
              editor content — the left nav rail stays visible. */}
          {previewOpen && (
            <ResumePreviewPanel
              resume={resume}
              orderedSections={orderedSections}
              getLabel={sectionLabel}
              genTypes={sectionGenTypes}
              activeKey={activeNavKey}
              onClose={() => setPreviewOpen(false)}
              username={user?.username}
              resumeName={resumename}
              publishMaster={publishMasterResume}
              dedicatedMasters={dedicatedMasters}
            />
          )}
          </div>
        )}

        {/* ── Bottom bar: save state · section stepper · save actions ── */}
        <footer className="flex-shrink-0 min-h-[60px] flex items-center gap-3 px-[18px] py-2 bg-white border-t border-gray-200">
          <span className="flex items-center gap-2 text-[12.5px] text-gray-500" data-help-target="mainapp.resumes.resumeN.savestate" data-testid="save-state">
            <span className={`w-[7px] h-[7px] rounded-full flex-shrink-0 ${isDirty ? 'bg-amber-500' : 'bg-green-700'}`} aria-hidden="true" />
            {isDirty ? (
              <span className="font-semibold text-amber-700">Unsaved changes</span>
            ) : (
              <span>
                <span className="font-semibold text-gray-700">All changes saved</span>
                {lastSavedLabel ? ` · ${lastSavedLabel}` : ''}
              </span>
            )}
          </span>

          {navIndex >= 0 && navKeyOrder.length > 0 && (
            <>
              <span className={`${TB_VR} hidden lg:block`} aria-hidden="true" />
              <span className="hidden lg:flex items-center gap-1.5 text-[12.5px] text-gray-500" data-help-target="mainapp.resumes.resumeN.sectionposition" data-testid="section-position">
                <span className="font-semibold text-gray-700">Section {navIndex + 1} of {navKeyOrder.length}</span>
                <span>· {navKeyLabel(navKeyOrder[navIndex])}</span>
              </span>
            </>
          )}

          <span className="flex-1" />

          <span className="hidden lg:flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => navIndex > 0 && scrollToNavKey(navKeyOrder[navIndex - 1])}
              disabled={navIndex <= 0}
              data-help-target="mainapp.resumes.resumeN.prevsection"
              className={TB_GHOST}
              title="Scroll to the previous section"
            >
              <ArrowLeft className="w-[15px] h-[15px]" />
              <span className="hidden xl:inline">Previous section</span>
            </button>
            <button
              type="button"
              onClick={() => navIndex >= 0 && navIndex < navKeyOrder.length - 1 && scrollToNavKey(navKeyOrder[navIndex + 1])}
              disabled={navIndex < 0 || navIndex >= navKeyOrder.length - 1}
              data-help-target="mainapp.resumes.resumeN.nextsection"
              className={TB_GHOST}
              title="Scroll to the next section"
            >
              <span className="hidden xl:inline">Next section</span>
              <ArrowRight className="w-[15px] h-[15px]" />
            </button>
          </span>

          <span className={TB_VR} aria-hidden="true" />

          <button
            onClick={handleClose}
            disabled={importing}
            data-help-target="mainapp.resumes.resumeN.cancel"
            className={TB_GHOST}
            title="Close without saving"
          >
            Cancel
          </button>
          <button
            onClick={() => handleSave(false)}
            disabled={saving || loading || importing}
            title="Save without closing (Cmd/Ctrl+S) — useful when you want tag regeneration to run and then keep editing"
            data-help-target="mainapp.resumes.resumeN.save"
            className={TB_OUT}
          >
            {saving ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <Save className="w-[15px] h-[15px]" />}
            Save
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving || loading || importing}
            data-help-target="mainapp.resumes.resumeN.saveclose"
            className={TB_PRIMARY}
          >
            {saving ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <Check className="w-[15px] h-[15px]" />}
            Save and Close
          </button>
        </footer>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                Delete Resume
              </h3>
              <p className="text-sm text-gray-600 text-center mb-1">
                Are you sure you want to delete <span className="font-medium">"{resumename}"</span>?
              </p>
              <p className="text-sm text-red-600 text-center font-medium">
                This action cannot be undone.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteResume}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved-changes guard for Resume ▾ → Switch resumes / New Resume.
          Both leave the current draft behind, so a dirty editor confirms. */}
      {pendingNav && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" role="dialog" aria-modal="true" data-testid="switch-unsaved-confirm">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-amber-100 rounded-full mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                Discard unsaved changes?
              </h3>
              <p className="text-sm text-gray-600 text-center">
                {pendingNav.kind === 'switch' ? (
                  <>You have unsaved edits to <span className="font-medium">"{resumename}"</span>. Switching to <span className="font-medium">"{pendingNav.resumename}"</span> will discard them.</>
                ) : (
                  <>You have unsaved edits to <span className="font-medium">"{resumename}"</span>. Starting a new resume will discard them.</>
                )}
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setPendingNav(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Keep editing
              </button>
              <button
                onClick={() => runNav(pendingNav)}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
              >
                Discard and {pendingNav.kind === 'switch' ? 'switch' : 'continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      <UpgradePromptModal
        isOpen={showAiUpgradeModal}
        onClose={() => setShowAiUpgradeModal(false)}
        limitType="ai"
        onNavigateToSettings={() => {
          setShowAiUpgradeModal(false);
          onClose();
        }}
      />

      <ScorecardModal
        isOpen={showScorecard}
        onClose={() => { setShowScorecard(false); setScorecardFocusHigh(false); }}
        resume={resume}
        resumename={resumename}
        onSaveItem={handleScorecardItemSave}
        onResolvedChange={handleScorecardResolvedChange}
        onLintFindings={scorecardLint.apply}
        // Seed the modal with the editor's shared pass so opening the Scorecard
        // on an already-scored resume never blanks a known-good number.
        initialLintFindings={scorecardLint.findings}
        // Merge-flow deletions land in editor state ONLY (no API call): they
        // ride along with the user's next resume Save and Cancel discards them,
        // exactly like any other unsaved edit in the editor.
        onEditorMutate={(mutate) => setResume((prev) => mutate(prev))}
        onBatchFix={handleScorecardBatchFix}
        focusHigh={scorecardFocusHigh}
      />

      <WritingSampleModal
        isOpen={showWritingModal}
        onClose={() => setShowWritingModal(false)}
        onSuccess={reloadWritingFingerprint}
      />

      {resumename && (
        <DedicatedMasterModal
          isOpen={showDedicatedMasterModal}
          resumename={resumename}
          username={user?.username?.toLowerCase() || ''}
          existingPaths={dedicatedMasterTakenPaths}
          sharedPaths={otherDmPaths}
          onClose={() => setShowDedicatedMasterModal(false)}
          onCreated={handleDedicatedMasterCreated}
          onAccessDenied={() => setDmAccessDenied(true)}
        />
      )}
    </div>
  );
}
